import { formatDateDDMMYY, formatDateTimeDDMMYY } from '../../utils/dateFormat';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CreateNewFolderOutlinedIcon from '@mui/icons-material/CreateNewFolderOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import EastIcon from '@mui/icons-material/East';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LibraryMusicIcon from '@mui/icons-material/LibraryMusic';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import LoopIcon from '@mui/icons-material/Loop';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PlaylistAddOutlinedIcon from '@mui/icons-material/PlaylistAddOutlined';
import PlaylistPlayIcon from '@mui/icons-material/PlaylistPlay';
import PreviewIcon from '@mui/icons-material/Preview';
import SearchIcon from '@mui/icons-material/Search';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import SyncIcon from '@mui/icons-material/Sync';
import TvIcon from '@mui/icons-material/Tv';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import ViewTimelineIcon from '@mui/icons-material/ViewTimeline';
import ViewListIcon from '@mui/icons-material/ViewList';
import GridViewIcon from '@mui/icons-material/GridView';

import type { StreamChannel } from '../../services/channelsService';
import { DashboardTab } from './DashboardTab';
import {
  getTranscoders,
  type StreamAnalytics,
  type StreamStats,
  type Transcoder,
} from '../../services/streamsService';
import {
  appendPlayKastrPlaylistItem,
  appendPlayKastrLiveItem,
  archivePlayKastrPlaylist,
  buildPlayKastrAssetStreamUrl,
  clearPlayKastrPlaylist,
  cleanupPlayKastrAssetOriginal,
  cancelPlayKastrUploadJob,
  applyPlayKastrPendingChanges,
  continuePlayKastrPlayout,
  createPlayKastrFolder,
  createPlayKastrPlaylist,
  deletePlayKastrAsset,
  deletePlayKastrFolder,
  downloadPlayKastrGracenote,
  downloadPlayKastrLogsCsv,
  downloadPlayKastrTechnicalReport,
  downloadPlayKastrXmltv,
  generatePlayKastrPlaylistSnapshot,
  getPlayKastrAssetStreamToken,
  getPlayKastrMediaInfo,
  getPlayKastrPlayoutStatus,
  getPlayKastrWorkspace,
  listPlayKastrAssets,
  listPlayKastrFolders,
  listPlayKastrLogs,
  listPlayKastrUploadJobs,
  movePlayKastrPlaylistItem,
  populatePlayKastrPlaylistBlock,
  removePlayKastrPlaylistItem,
  removePlayKastrPlaylistItems,
  reloadPlayKastrSession,
  resolvePlayKastrItemRouteId,
  resolvePlayKastrPlaylistHashRouteId,
  resolvePlayKastrPlaylistRouteId,
  resolvePlayKastrSessionRouteId,
  skipPlayKastrSessionItem,
  replacePlayKastrPlaylistItems,
  renamePlayKastrFolder,
  startPlayKastrPlaylistFromItem,
  updatePlayKastrPlaylist,
  updatePlayKastrAsset,
  startPlayKastrPlayout,
  stopPlayKastrSession,
  uploadPlayKastrAsset,
  type PlayKastrAsset,
  type PlayKastrFolder,
  type PlayKastrLogEntry,
  type PlayKastrMediaInfo,
  type PlayKastrPlaylistItem,
  type PlayKastrActivePlayout,
  type PlayKastrUploadJob,
  type PlayKastrWorkspaceResponse,
} from '../../services/playkastrService';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { VideoPlayer, type PreviewType } from '../VideoPlayer';
import { useAuthenticatedObjectUrl } from '../../utils/authenticatedMedia';

const MemoizedVideoPlayer = React.memo(VideoPlayer);
const v = (name: string) => `var(${name})`;
type WorkspaceView = 'library' | 'rundown' | 'playout' | 'epg' | 'logs';
type WorkspaceNavItem = {
  key: WorkspaceView;
  label: string;
  icon: React.ReactNode;
};
type PlaylistManagerMode = 'open' | 'create';
const VIRTUAL_OVERSCAN_ROWS = 6;
const LIBRARY_PAGE_SIZE = 20;
const ASSET_LIST_ROW_HEIGHT = 118;
const ASSET_ICON_ROW_HEIGHT = 238;
const RUNDOWN_ROW_HEIGHT = 82;

const workspaceViewStorageKey = (channelId?: number | null) =>
  channelId ? `playkastr-workspace-view:${channelId}` : null;

type UploadQueueItem = {
  id: string;
  jobId?: number | null;
  name: string;
  size: number;
  progress: number;
  status: 'pending' | 'uploading' | 'processing' | 'ready' | 'error';
  error?: string | null;
  assetId?: number | null;
};

type PlaykastrLogRange = '3h' | '12h' | '24h' | '7d' | '30d' | '90d' | '180d' | '365d';
type AssetViewMode = 'list' | 'icon';

const mapUploadJobToQueueItem = (job: PlayKastrUploadJob): UploadQueueItem => {
  const status = String(job.status || '').toUpperCase();
  return {
    id: `job-${job.id}`,
    jobId: job.id,
    name: job.label || job.file_name || `Upload ${job.id}`,
    size: Number(job.total_bytes || job.bytes_received || 0),
    progress: Math.max(0, Math.min(100, Number(job.progress_pct || 0))),
    status: status === 'READY' ? 'ready' : status === 'ERROR' ? 'error' : status === 'UPLOADING' ? 'uploading' : 'processing',
    error: job.error_message || null,
    assetId: job.asset_id ?? null,
  };
};

const cardSx = {
  borderRadius: 0,
  border: '1px solid',
  borderColor: v('--border'),
  bgcolor: v('--bg-card'),
} as const;

const railTitleSx = {
  fontSize: '0.62rem',
  fontWeight: 900,
  letterSpacing: '0.12em',
  textTransform: 'uppercase' as const,
  color: v('--text-muted'),
} as const;

const microButtonSx = {
  borderRadius: 0,
  textTransform: 'uppercase' as const,
  fontWeight: 900,
  fontSize: '0.64rem',
  letterSpacing: '0.1em',
  minHeight: 28,
} as const;

const actionIconSx = {
  borderRadius: '6px',
  border: '1px solid',
  borderColor: v('--border'),
  bgcolor: 'var(--bg-body)',
  color: v('--text-secondary'),
  width: 30,
  height: 30,
} as const;

const studioPillSx = {
  borderRadius: 0,
  height: 22,
  fontWeight: 900,
  fontSize: '0.6rem',
  letterSpacing: '0.1em',
} as const;

const fieldLabelSx = {
  fontSize: '0.58rem',
  fontWeight: 900,
  letterSpacing: '0.12em',
  textTransform: 'uppercase' as const,
  color: v('--text-muted'),
} as const;

const disabledActionSx = {
  borderColor: 'var(--border-strong)',
  color: 'var(--text-disabled)',
  opacity: 0.72,
  bgcolor: 'var(--bg-body)',
} as const;

const prominentActionIconSx = {
  width: 34,
  height: 34,
  borderRadius: '999px',
  border: '1px solid',
  borderColor: 'var(--border-strong)',
  bgcolor: 'rgba(var(--text-main-rgb), 0.08)',
  color: 'var(--text-main)',
  boxShadow: '0 0 0 1px rgba(var(--bg-body-rgb), 0.22), inset 0 0 0 1px rgba(var(--text-main-rgb), 0.06)',
} as const;

const operatorZoneHeaderSx = {
  px: 1.15,
  py: 0.85,
  borderBottom: '1px solid',
  borderColor: v('--border'),
  bgcolor: 'var(--bg-card)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 1,
  minHeight: 52,
} as const;

const operatorZoneNumberSx = {
  width: 28,
  height: 28,
  borderRadius: '999px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid',
  borderColor: v('--border'),
  bgcolor: 'var(--bg-card-hover)',
  color: v('--text-secondary'),
  fontSize: '0.64rem',
  fontWeight: 900,
} as const;

const operatorZoneTitleSx = {
  fontSize: '0.8rem',
  fontWeight: 900,
  color: v('--text-main'),
  letterSpacing: '0.02em',
} as const;

const operatorPanelSx = {
  ...cardSx,
  borderRadius: '4px',
  bgcolor: 'var(--bg-card)',
  boxShadow: '0 1px 2px rgba(var(--text-main-rgb),0.04)',
} as const;

const mockupButtonSx = {
  ...microButtonSx,
  borderRadius: '4px',
  minHeight: 34,
  px: 1.2,
  letterSpacing: '0.02em',
  fontSize: '0.7rem',
} as const;

const epgCategoryOptions = ['VIDEO', 'FILLER', 'AD'] as const;
const playkastrLogRangeOptions: Array<{ value: PlaykastrLogRange; label: string }> = [
  { value: '3h', label: '3 hours' },
  { value: '12h', label: '12 hours' },
  { value: '24h', label: '24 hours' },
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
  { value: '90d', label: '90 days' },
  { value: '180d', label: '180 days' },
  { value: '365d', label: '365 days' },
];
const programTypeOptions = ['Movie', 'Episode', 'Show'] as const;
const movieRatingOptions = ['G', 'PG', 'PG-13', 'R', 'NC-17'] as const;
const tvRatingOptions = ['TV-Y', 'TV-Y7', 'TV-G', 'TV-PG', 'TV-14', 'TV-MA'] as const;
const genreOptions = [
  'Action',
  'Adventure',
  'Comedy',
  'Drama',
  'Horror',
  'Romance',
  'Science Fiction (Sci-Fi)',
  'Thriller',
  'Mystery',
  'Crime',
  'Fantasy',
  'Animation',
  'Documentary',
  'Musical',
  'Western',
  'War',
  'Historical',
  'Biography',
  'Family',
  'Sports',
  'Action-Comedy',
  'Romantic Comedy (Rom-Com)',
  'Sci-Fi Horror',
  'Action-Thriller',
  'Crime Drama',
  'Historical Drama',
  'Fantasy Adventure',
  'Animated Musical',
] as const;

const formatPlaykastrLogAction = (action?: string | null) =>
  String(action || '')
    .replace(/^PLAYKASTR_/, '')
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0) + part.slice(1).toLowerCase())
    .join(' ');

const previewPanelSx = {
  bgcolor: 'var(--bg-card)',
  border: '1px solid',
  borderColor: v('--border'),
  boxShadow: 'none',
} as const;

const DetailField: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box sx={{ bgcolor: 'var(--bg-body)', border: '1px solid', borderColor: v('--border'), px: 1, py: 0.85 }}>
    <Typography sx={fieldLabelSx}>{label}</Typography>
    <Typography sx={{ mt: 0.45, fontSize: '0.74rem', color: v('--text-main'), fontWeight: 900 }} noWrap>
      {value}
    </Typography>
  </Box>
);

const StudioStat: React.FC<{ label: string; value: React.ReactNode; helper?: React.ReactNode }> = ({ label, value, helper }) => (
  <Box sx={{ bgcolor: 'var(--bg-body)', border: '1px solid', borderColor: v('--border'), px: 1, py: 0.9 }}>
    <Typography sx={fieldLabelSx}>{label}</Typography>
    <Typography sx={{ mt: 0.35, fontSize: '0.88rem', fontWeight: 900, color: v('--text-main') }} noWrap>
      {value}
    </Typography>
    {helper ? (
      <Typography sx={{ mt: 0.22, fontSize: '0.66rem', color: v('--text-secondary'), letterSpacing: '0.04em' }} noWrap>
        {helper}
      </Typography>
    ) : null}
  </Box>
);

const SheetSection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Box sx={{ bgcolor: 'background.default', border: '1px solid', borderColor: v('--border') }}>
    <Box sx={{ px: 1, py: 0.8, bgcolor: 'var(--bg-card-hover)', borderBottom: '1px solid', borderColor: v('--border') }}>
      <Typography sx={railTitleSx}>{title}</Typography>
    </Box>
    <Box sx={{ p: 1 }}>{children}</Box>
  </Box>
);

const statusLabel = (stats?: StreamStats | null, analytics?: StreamAnalytics | null) => {
  if ((stats as any)?.isPlayKastrSource) return 'On Air';
  if (!stats?.type) return 'Standby';
  if ((analytics?.viewers || 0) > 0) return 'On Air';
  return 'Active';
};

const sourceLabel = (stats?: StreamStats | null) => {
  if (!stats?.type) return 'No active playout source';
  if ((stats as any)?.isPlayKastrSource) return 'PlayKastr';
  if ((stats as any)?.isMediaBoxSource) return 'MediaBox';
  return (stats as any)?.sourceLabel || 'Live';
};

const formatDuration = (secs?: number | null) => {
  if (!secs || secs <= 0) return '--:--';
  const total = Math.round(secs);
  const hours = Math.floor(total / 3600);
  const mins = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  if (hours > 0) return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  return `${String(mins).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const formatDurationWithUnits = (secs?: number | null) => {
  const total = Math.max(0, Math.round(Number(secs || 0)));
  const hours = Math.floor(total / 3600);
  const mins = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  return `${hours}h:${String(mins).padStart(2, '0')}m:${String(seconds).padStart(2, '0')}s`;
};

const addClockDuration = (clockValue?: string | null, secs?: number | null) => {
  const raw = String(clockValue || '').trim();
  const duration = Number(secs || 0);
  if (!raw || !Number.isFinite(duration) || duration <= 0) return '--';
  const parts = raw.split(':').map(Number);
  if (parts.some((part) => !Number.isFinite(part))) return '--';
  const [hours = 0, minutes = 0, seconds = 0] = parts;
  const total = (((hours * 3600) + (minutes * 60) + seconds + Math.round(duration)) % 86400 + 86400) % 86400;
  const outHours = Math.floor(total / 3600);
  const outMinutes = Math.floor((total % 3600) / 60);
  const outSeconds = total % 60;
  return `${String(outHours).padStart(2, '0')}:${String(outMinutes).padStart(2, '0')}:${String(outSeconds).padStart(2, '0')}`;
};

const parseClockParts = (value?: string | null) => {
  const raw = String(value || '').trim();
  if (!raw) return null;
  const parts = raw.split(':').map(Number);
  if (parts.some((part) => !Number.isFinite(part))) return null;
  const [hours = 0, minutes = 0, seconds = 0] = parts;
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59 || seconds < 0 || seconds > 59) return null;
  return { hours, minutes, seconds };
};

const normalizeClockInput = (value?: string | null) => {
  const parts = parseClockParts(value);
  if (!parts) return null;
  return `${String(parts.hours).padStart(2, '0')}:${String(parts.minutes).padStart(2, '0')}:${String(parts.seconds).padStart(2, '0')}`;
};

const getZonedDateParts = (date: Date, timeZone: string) => {
  const formatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: timeZone || 'UTC',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    weekday: 'short',
    hour12: false,
  });
  const parts = formatter.formatToParts(date);
  const read = (type: Intl.DateTimeFormatPartTypes) => parts.find((entry) => entry.type === type)?.value || '';
  const weekdayName = read('weekday');
  const weekdayMap: Record<string, number> = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
  };
  return {
    year: Number(read('year')),
    month: Number(read('month')),
    day: Number(read('day')),
    hour: Number(read('hour')),
    minute: Number(read('minute')),
    second: Number(read('second')),
    weekday: weekdayMap[weekdayName] ?? 0,
  };
};

const weekdayAllowed = (weekday: number, mask?: number | null) => {
  if (!mask) return false;
  const bit = [1, 2, 4, 8, 16, 32, 64][weekday] || 0;
  return (mask & bit) !== 0;
};

const formatCountdown = (millis?: number | null) => {
  if (millis == null || !Number.isFinite(millis)) return '--';
  const total = Math.max(0, Math.floor(millis / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  if (hours > 0) return `${hours}h ${String(minutes).padStart(2, '0')}m ${String(seconds).padStart(2, '0')}s`;
  return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
};

const parseIsoMillis = (value?: string | null) => {
  const parsed = Date.parse(String(value || ''));
  return Number.isFinite(parsed) ? parsed : null;
};

const formatClockFromIso = (value?: string | null, timeZone?: string | null) => {
  const millis = parseIsoMillis(value);
  if (millis == null) return '--';
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: timeZone || 'Africa/Nairobi',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(new Date(millis));
};

const formatClockValueForMode = (value?: string | null, mode: '24h' | 'ampm' = '24h') => {
  const parts = parseClockParts(value);
  if (!parts) return '--';
  const base = new Date();
  base.setHours(parts.hours, parts.minutes, parts.seconds, 0);
  if (mode === 'ampm') {
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    }).format(base);
  }
  return `${String(parts.hours).padStart(2, '0')}:${String(parts.minutes).padStart(2, '0')}:${String(parts.seconds).padStart(2, '0')}`;
};

const formatDateOnly = (dateLike: Date | number, timeZone: string) =>
  formatDateDDMMYY(dateLike, '--', timeZone || 'Africa/Nairobi');

const formatDateTimeLabel = (dateLike: Date | number, timeZone: string, clockValue?: string | null) => {
  const dateLabel = formatDateOnly(dateLike, timeZone);
  const timeLabel = String(clockValue || '').trim() || '--';
  return `${dateLabel} ${timeLabel}`;
};

const parseScheduledDateTime = (dateValue?: string | null, clockValue?: string | null) => {
  const safeDate = String(dateValue || '').trim();
  const safeClock = String(clockValue || '').trim();
  if (!safeDate || !safeClock) return null;
  const date = new Date(`${safeDate}T${safeClock}`);
  return Number.isNaN(date.getTime()) ? null : date;
};

const buildBlockPlaylistName = (baseName: string, randomFour: string, blockIndex: number) =>
  `${(baseName.trim() || 'playlist').replace(/\s+/g, '_')}_${randomFour}_block${String(blockIndex).padStart(2, '0')}`;

const formatPlaylistNameDate = (value: string) => {
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return formatDateDDMMYY(date).replace(/\//g, '-');
};

const formatPlaylistNameTime = (value: string) => {
  const [hoursRaw, minutesRaw] = String(value || '').split(':');
  const hours = Number(hoursRaw || 0);
  const minutes = Number(minutesRaw || 0);
  const base = new Date();
  base.setHours(hours, minutes, 0, 0);
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(base).replace(/\s+/g, '');
};

const buildBlockPlaylistNameLabel = ({
  baseName,
  randomFour,
  blockIndex,
  startDate,
  startTime,
  endDate,
  endTime,
}: {
  baseName: string;
  randomFour: string;
  blockIndex: number;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
}) =>
  `${buildBlockPlaylistName(baseName, randomFour, blockIndex)}_${formatPlaylistNameDate(startDate)}-${formatPlaylistNameTime(startTime)}_${formatPlaylistNameDate(endDate)}-${formatPlaylistNameTime(endTime)}`;

const stripGeneratedPlaylistSuffix = (name: string) =>
  String(name || '')
    .replace(/_\d{4}_block\d{2}(?:_[^_]+_[^_]+)?$/i, '')
    .replace(/_\d{4}_(daily|weekly|gap)_[^_]+_[^_]+$/i, '')
    .trim();

const readBlockNumberFromName = (name?: string | null) => {
  const match = String(name || '').match(/_block(\d{2})$/i);
  const value = match ? Number(match[1]) : NaN;
  return Number.isFinite(value) && value > 0 ? value : 1;
};

const comparePlaylistsBySchedule = (a: { name?: string | null; start_date?: string | null; clock_start?: string | null }, b: { name?: string | null; start_date?: string | null; clock_start?: string | null }) => {
  const aStart = parseScheduledDateTime(a.start_date, a.clock_start)?.getTime() ?? Number.MAX_SAFE_INTEGER;
  const bStart = parseScheduledDateTime(b.start_date, b.clock_start)?.getTime() ?? Number.MAX_SAFE_INTEGER;
  if (aStart !== bStart) return aStart - bStart;
  return String(a.name || '').localeCompare(String(b.name || ''));
};

const formatPlaylistWindowLabel = (playlist: { start_date?: string | null; clock_start?: string | null; timezone?: string | null }) => {
  const start = parseScheduledDateTime(playlist.start_date, playlist.clock_start);
  if (!start) return 'No scheduled window';
  const end = new Date(start.getTime() + (6 * 60 * 60 * 1000) - 1000);
  const zone = playlist.timezone || 'Africa/Nairobi';
  return `${formatDateOnly(start, zone)} ${formatClockValueForMode(playlist.clock_start, '24h')} - ${formatClockFromIso(end.toISOString(), zone)}`;
};

const computePlaylistWindow = (
  frequency: 'DAILY' | 'WEEKLY',
  dateValue: string,
  timeValue: string,
) => {
  const start = new Date(`${dateValue}T${timeValue}:00`);
  const startDate = Number.isNaN(start.getTime()) ? new Date() : start;
  const end = new Date(startDate.getTime() + (6 * 60 * 60 * 1000) - 1000);
  const yyyy = (value: number) => String(value).padStart(4, '0');
  const mm = (value: number) => String(value).padStart(2, '0');
  const startDateText = `${yyyy(startDate.getFullYear())}-${mm(startDate.getMonth() + 1)}-${mm(startDate.getDate())}`;
  const startTimeText = `${mm(startDate.getHours())}:${mm(startDate.getMinutes())}`;
  const endDateText = `${yyyy(end.getFullYear())}-${mm(end.getMonth() + 1)}-${mm(end.getDate())}`;
  const endTimeText = `${mm(end.getHours())}:${mm(end.getMinutes())}`;
  return {
    startDate,
    endDate: end,
    startDateText,
    startTimeText,
    endDateText,
    endTimeText,
  };
};

const addHoursToLocalDateTime = (dateValue: string, timeValue: string, hoursToAdd: number) => {
  const base = parseScheduledDateTime(dateValue, `${timeValue}:00`) || new Date();
  const next = new Date(base.getTime() + hoursToAdd * 60 * 60 * 1000);
  const yy = String(next.getFullYear()).padStart(4, '0');
  const mm = String(next.getMonth() + 1).padStart(2, '0');
  const dd = String(next.getDate()).padStart(2, '0');
  const hh = String(next.getHours()).padStart(2, '0');
  const mi = String(next.getMinutes()).padStart(2, '0');
  return { date: `${yy}-${mm}-${dd}`, time: `${hh}:${mi}` };
};

const buildAssetMediaUrl = (channel: StreamChannel | null, assetId: number | null, kind: 'file' | 'thumbnail') => {
  if (!channel || !assetId) return '';
  const channelRouteId = String(channel.channelHashId || channel.hashId || channel.id || '');
  return `/api/playkastr/channels/${channelRouteId}/assets/${assetId}/${kind}`;
};

const buildWorkspaceEditorSignature = (data: PlayKastrWorkspaceResponse | null) => {
  const studio = data?.studio;
  const playlist: any = studio?.playlist || null;
  const playlists = (studio?.playlists || []) as any[];
  const items = (playlist?.items || []) as any[];
  const playlistSignature = [
    playlist?.id,
    playlist?.playlistHashId || playlist?.playlist_hash_id || playlist?.hashId,
    playlist?.name,
    playlist?.mode,
    playlist?.clock_start,
    playlist?.start_date,
    playlist?.next_playlist_id || playlist?.nextPlaylistId,
    playlist?.updatedAt || playlist?.updated_at,
  ].join(':');
  const itemSignature = items
    .map((item) => [
      item.id,
      item.position,
      item.asset_id,
      item.kind,
      item.duration_secs,
      item.expected_duration_secs,
      item.calculated_start_utc,
      item.calculated_end_utc,
      item.updatedAt || item.updated_at,
    ].join(','))
    .join('|');
  const playlistListSignature = playlists
    .map((entry) => [
      entry.id,
      entry.playlistHashId || entry.playlist_hash_id || entry.hashId,
      entry.name,
      entry.mode,
      entry.clock_start,
      entry.start_date,
      entry.next_playlist_id || entry.nextPlaylistId,
      entry.updatedAt || entry.updated_at,
    ].join(','))
    .join('|');

  return [
    playlistSignature,
    `items:${items.length}:${itemSignature}`,
    `playlists:${playlists.length}:${playlistListSignature}`,
    `overview:${data?.overview?.playlist_count || 0}`,
  ].join('::');
};

const runtimePlayoutSignature = (playout?: PlayKastrActivePlayout | null) => {
  if (!playout) return 'none';
  const snapshot: any = playout.snapshot || {};
  const runtime: any = snapshot.runtime || {};
  const session: any = playout.session || snapshot.session || {};
  const playlist: any = playout.playlist || {};
  const nowPlaying: any = snapshot.now_playing || {};
  const upNext = Array.isArray(snapshot.up_next) ? snapshot.up_next.slice(0, 3) : [];
  return [
    session.id,
    session.status,
    session.startedAt || session.started_at,
    playlist.id,
    playlist.name,
    runtime.status,
    runtime.playlist_id,
    runtime.current_item_id,
    runtime.queue_index,
    runtime.loop_iteration,
    runtime.started_at,
    nowPlaying.item_id,
    nowPlaying.label,
    ...upNext.flatMap((item: any) => [item.item_id, item.label]),
    snapshot.output_url,
    snapshot.pending_changes,
    snapshot.can_apply_on_next_loop,
    snapshot.requires_restart,
    snapshot.apply_mode,
    snapshot.loaded_playlist_updated_at,
    snapshot.current_playlist_updated_at,
  ].join(':');
};

const AuthenticatedAssetImage = React.memo(({
  channel,
  assetId,
  alt,
  sx,
}: {
  channel: StreamChannel | null;
  assetId: number | null;
  alt?: string;
  sx?: React.ComponentProps<typeof Box>['sx'];
}) => {
  const mediaUrl = useMemo(() => buildAssetMediaUrl(channel, assetId, 'thumbnail'), [channel, assetId]);
  const src = useAuthenticatedObjectUrl(mediaUrl);

  return <Box component="img" src={src || ''} alt={alt || ''} sx={sx} />;
});

const AuthenticatedAssetVideo = React.memo(({
  channel,
  assetId,
  height,
}: {
  channel: StreamChannel | null;
  assetId: number | null;
  height: number;
}) => {
  const [src, setSrc] = useState('');
  const [streamError, setStreamError] = useState('');
  const posterUrl = useMemo(() => buildAssetMediaUrl(channel, assetId, 'thumbnail'), [channel, assetId]);
  const poster = useAuthenticatedObjectUrl(posterUrl);

  useEffect(() => {
    let cancelled = false;
    setSrc('');
    setStreamError('');
    if (!channel || !assetId) return undefined;

    getPlayKastrAssetStreamToken(channel, assetId)
      .then((payload) => {
        if (!cancelled) setSrc(buildPlayKastrAssetStreamUrl(channel, assetId, payload.token));
      })
      .catch(() => {
        if (!cancelled) setStreamError('Asset preview is unavailable.');
      });

    return () => {
      cancelled = true;
      setSrc('');
    };
  }, [assetId, channel]);

  if (!src) {
    return (
      <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'var(--bg-body)', color: 'var(--text-secondary)', fontSize: '0.76rem' }}>
        {streamError || 'Preparing preview...'}
      </Box>
    );
  }

  return (
    <video
      key={assetId || 'empty'}
      controls
      preload="metadata"
      poster={poster || undefined}
      src={src || undefined}
      style={{ width: '100%', height: `${height}px`, objectFit: 'contain', background: 'var(--bg-body)' }}
    />
  );
});

const WEEKDAY_OPTIONS = [
  { label: 'Sun', value: 1 },
  { label: 'Mon', value: 2 },
  { label: 'Tue', value: 4 },
  { label: 'Wed', value: 8 },
  { label: 'Thu', value: 16 },
  { label: 'Fri', value: 32 },
  { label: 'Sat', value: 64 },
] as const;

const formatClockNowLocal = () => {
  const now = new Date();
  return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;
};

const weekdayMaskFromDateInput = (value: string) => {
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return 0;
  const map = [1, 2, 4, 8, 16, 32, 64];
  return map[date.getDay()] || 0;
};

const effectivePlaylistItemSecs = (item?: PlayKastrPlaylistItem | null) => {
  const exactDurationMs = Number(item?.exact_duration_ms ?? 0);
  if (Number.isFinite(exactDurationMs) && exactDurationMs > 0) {
    return exactDurationMs / 1000;
  }
  const duration = Number(item?.duration_secs ?? item?.expected_duration_secs ?? 0);
  if (!Number.isFinite(duration) || duration <= 0) return 0;
  const trimIn = Number(item?.trim_in_secs || 0);
  const trimOut = item?.trim_out_secs == null ? duration : Number(item.trim_out_secs);
  const safeTrimIn = Number.isFinite(trimIn) && trimIn > 0 ? trimIn : 0;
  const safeTrimOut = Number.isFinite(trimOut) && trimOut > safeTrimIn ? Math.min(trimOut, duration) : duration;
  return Math.max(0, safeTrimOut - safeTrimIn);
};

const getPlaylistItemInLabel = (item?: PlayKastrPlaylistItem | null, timeZone?: string | null) => {
  if (item?.calculated_start_utc) return formatClockFromIso(item.calculated_start_utc, timeZone);
  return item?.scheduled_start || '--';
};

const getPlaylistItemOutLabel = (item?: PlayKastrPlaylistItem | null, timeZone?: string | null) => {
  if (item?.calculated_end_utc) return formatClockFromIso(item.calculated_end_utc, timeZone);
  return addClockDuration(item?.scheduled_start, effectivePlaylistItemSecs(item));
};

const useTickingNow = (active: boolean, intervalMs: number) => {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!active) return;
    let timer: number | null = null;
    const schedule = () => {
      if (timer) window.clearTimeout(timer);
      timer = window.setTimeout(tick, document.hidden ? 60000 : intervalMs);
    };
    const tick = () => {
      setNow(Date.now());
      schedule();
    };
    const onVisibilityChange = () => {
      setNow(Date.now());
      schedule();
    };
    schedule();
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      if (timer) window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [active, intervalMs]);

  return now;
};

const ScheduleCountdown: React.FC<{ targetAt?: number | null; timeZone?: string | null }> = React.memo(({ targetAt, timeZone }) => {
  const now = useTickingNow(targetAt != null, 1000);
  if (targetAt == null) return <>--</>;
  const zoned = getZonedDateParts(new Date(now), String(timeZone || 'Africa/Nairobi'));
  const currentPseudo = Date.UTC(zoned.year, zoned.month - 1, zoned.day, zoned.hour, zoned.minute, zoned.second);
  return <>{formatCountdown(Math.max(0, targetAt - currentPseudo))}</>;
});

const RundownDateLabel: React.FC<{ timeZone: string }> = React.memo(({ timeZone }) => {
  const now = useTickingNow(true, 60000);
  return <>{formatDateOnly(now, timeZone)}</>;
});

const RundownRemainingTime: React.FC<{
  item: PlayKastrPlaylistItem;
  onAir: boolean;
  runtimeItemSecs: number;
  runtimeProgressBaseMs: number | null;
  runtimeCurrentItemId?: number | null;
}> = React.memo(({ item, onAir, runtimeItemSecs, runtimeProgressBaseMs, runtimeCurrentItemId }) => {
  const now = useTickingNow(onAir, 1000);
  const totalSecs = effectivePlaylistItemSecs(item);
  if (!Number.isFinite(totalSecs) || totalSecs <= 0) return <>--:--</>;
  if (!onAir) return <>{formatDuration(totalSecs)}</>;

  const safeRuntimeItemSecs = Number.isFinite(runtimeItemSecs) && runtimeItemSecs >= 0 ? runtimeItemSecs : 0;
  const runtimeElapsedSecs = runtimeProgressBaseMs
    ? safeRuntimeItemSecs + Math.max(0, (now - runtimeProgressBaseMs) / 1000)
    : safeRuntimeItemSecs;
  const runtimeRemaining = Math.max(0, totalSecs - runtimeElapsedSecs);
  if (runtimeCurrentItemId != null && item.id === runtimeCurrentItemId) {
    return <>{formatDuration(runtimeRemaining)}</>;
  }

  const calculatedEndMs = parseIsoMillis(item.calculated_end_utc);
  if (calculatedEndMs != null) {
    const boundaryRemaining = Math.max(0, Math.ceil((calculatedEndMs - now) / 1000));
    return <>{formatDuration(boundaryRemaining <= 1 && runtimeRemaining > 1 ? runtimeRemaining : boundaryRemaining)}</>;
  }
  return <>{formatDuration(runtimeRemaining)}</>;
});

const normaliseStatusTone = (status?: string | null) => {
  const upper = String(status || '').toUpperCase();
  if (upper === 'READY') return { label: 'Ready', color: v('--success'), bg: 'rgba(var(--success-rgb),0.12)' };
  if (upper === 'ERROR') return { label: 'Error', color: v('--danger'), bg: 'rgba(var(--danger-rgb),0.12)' };
  return { label: upper || 'Pending', color: v('--warning'), bg: 'rgba(var(--warning-rgb),0.12)' };
};

const formatAssetStatus = (status?: string | null) => {
  const upper = String(status || '').toUpperCase();
  if (!upper) return 'Pending';
  if (upper === 'READY') return 'Ready';
  if (upper === 'ERROR') return 'Error';
  if (upper === 'NORMALISING') return 'Normalising';
  if (upper === 'PROBING') return 'Probing';
  return upper.charAt(0) + upper.slice(1).toLowerCase();
};

type RundownRowProps = {
  item: PlayKastrPlaylistItem;
  selected: boolean;
  onAir: boolean;
  next: boolean;
  runtimeItemSecs: number;
  runtimeProgressBaseMs: number | null;
  runtimeCurrentItemId?: number | null;
  outTime: string;
  inTime: string;
  metaLine: string;
  dragArmed?: boolean;
  dropMode?: boolean;
  draggable?: boolean;
  locked?: boolean;
  lockReason?: string;
  domId?: string;
  onSelect: () => void;
  onStartFrom?: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  onRemove?: () => void;
  onArmDrag?: () => void;
  canStartFrom?: boolean;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
  canRemove?: boolean;
  bulkSelected?: boolean;
  onToggleBulkSelect?: () => void;
  onDragStart?: () => void;
  onDragEnd?: () => void;
  onDropOn?: () => void;
};

const RundownRow = React.memo(({
  item,
  selected,
  onAir,
  next,
  runtimeItemSecs,
  runtimeProgressBaseMs,
  runtimeCurrentItemId,
  outTime,
  inTime,
  metaLine,
  dragArmed,
  dropMode,
  draggable = true,
  locked = false,
  lockReason,
  domId,
  onSelect,
  onStartFrom,
  onMoveUp,
  onMoveDown,
  onRemove,
  onArmDrag,
  canStartFrom,
  canMoveUp,
  canMoveDown,
  canRemove,
  bulkSelected = false,
  onToggleBulkSelect,
  onDragStart,
  onDragEnd,
  onDropOn,
}: RundownRowProps) => (
  <Box
    id={domId}
    draggable={draggable && !locked}
    onClick={onSelect}
    onDragStart={(event) => {
      if (locked || !draggable) {
        event.preventDefault();
        return;
      }
      event.dataTransfer.effectAllowed = 'move';
      onDragStart?.();
    }}
    onDragEnd={() => {
      onDragEnd?.();
    }}
    onDragOver={(event) => {
      if (locked) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
    }}
    onDrop={(event) => {
      if (locked) return;
      event.preventDefault();
      onDropOn?.();
    }}
    sx={{
      display: 'grid',
      gridTemplateColumns: '70px minmax(0, 1fr) 82px 82px 82px 88px 124px',
      gap: 0,
      minHeight: RUNDOWN_ROW_HEIGHT - 2,
      border: '1px solid',
      borderColor: v('--border'),
      bgcolor: dragArmed ? 'rgba(var(--secondary-rgb),0.08)' : selected ? 'rgba(var(--primary-rgb),0.08)' : dropMode ? 'rgba(var(--secondary-rgb),0.04)' : 'transparent',
      boxShadow: dragArmed
        ? `inset 0 0 0 1px rgba(var(--secondary-rgb),0.16)`
        : selected
        ? `inset 0 0 0 1px rgba(var(--primary-rgb),0.12)`
        : dropMode
        ? `inset 0 0 0 1px rgba(var(--secondary-rgb),0.14)`
        : 'none',
      cursor: locked ? 'not-allowed' : 'pointer',
      transition: 'background-color 120ms ease, border-color 120ms ease',
      '&:hover': {
        bgcolor: locked ? undefined : dragArmed ? 'rgba(var(--secondary-rgb),0.1)' : 'rgba(var(--primary-rgb),0.05)',
        boxShadow: locked ? undefined : dragArmed ? `inset 0 0 0 1px rgba(var(--secondary-rgb),0.22)` : `inset 0 0 0 1px rgba(var(--primary-rgb),0.16)`,
      },
    }}
  >
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{
        bgcolor: onAir ? 'rgba(var(--success-rgb),0.16)' : next ? 'rgba(var(--secondary-rgb),0.12)' : 'transparent',
      }}
    >
      <Stack direction="row" spacing={0.4} alignItems="center">
        <Checkbox
          size="small"
          checked={bulkSelected}
          disabled={locked || !canRemove}
          onClick={(event) => event.stopPropagation()}
          onChange={onToggleBulkSelect}
          sx={{ p: 0.2 }}
        />
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 900, color: v('--text-muted') }}>
          {String(item.position).padStart(2, '0')}
        </Typography>
      </Stack>
    </Stack>
    <Box sx={{ px: 1.25, py: 0.8 }}>
      <Stack direction="row" spacing={0.8} alignItems="center" sx={{ minWidth: 0 }}>
        {onAir && (
          <Chip
            label="ON AIR"
            size="small"
            sx={{
              height: 18,
              borderRadius: 0,
              bgcolor: 'rgba(var(--success-rgb),0.16)',
              color: v('--success'),
              fontWeight: 900,
              fontSize: '0.58rem',
              animation: 'playkastrOnAirPulse 1.4s ease-in-out infinite',
              '@keyframes playkastrOnAirPulse': {
                '0%': {
                  opacity: 0.8,
                  boxShadow: '0 0 0 0 rgba(var(--success-rgb),0.22)',
                },
                '70%': {
                  opacity: 1,
                  boxShadow: '0 0 0 6px rgba(var(--success-rgb),0)',
                },
                '100%': {
                  opacity: 0.82,
                  boxShadow: '0 0 0 0 rgba(var(--success-rgb),0)',
                },
              },
            }}
          />
        )}
        {next && !onAir && (
          <Chip
            label="NEXT"
            size="small"
            sx={{
              height: 18,
              borderRadius: 0,
              bgcolor: 'rgba(var(--secondary-rgb),0.16)',
              color: v('--secondary'),
              fontWeight: 900,
              fontSize: '0.58rem',
            }}
          />
        )}
        {String(item.timing_type || '').toUpperCase() === 'HARD_TIMED' && (
          <Chip
            label="HARD"
            size="small"
            sx={{
              height: 18,
              borderRadius: 0,
              bgcolor: 'rgba(var(--warning-rgb),0.16)',
              color: v('--warning'),
              fontWeight: 900,
              fontSize: '0.58rem',
            }}
          />
        )}
        <Typography sx={{ fontSize: '0.84rem', fontWeight: 800, color: v('--text-main') }} noWrap>
          {item.asset_label || item.kind}
        </Typography>
      </Stack>
      <Typography sx={{ mt: 0.24, fontSize: '0.69rem', color: v('--text-secondary'), letterSpacing: '0.03em' }} noWrap>
        {metaLine}
      </Typography>
    </Box>
      <Stack justifyContent="center" sx={{ px: 1 }}>
        <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: v('--text-main') }} noWrap>
          {inTime}
        </Typography>
      </Stack>
    <Stack justifyContent="center" sx={{ px: 1 }}>
      <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: v('--text-main') }} noWrap>
        <RundownRemainingTime
          item={item}
          onAir={onAir}
          runtimeItemSecs={runtimeItemSecs}
          runtimeProgressBaseMs={runtimeProgressBaseMs}
          runtimeCurrentItemId={runtimeCurrentItemId}
        />
      </Typography>
    </Stack>
    <Stack justifyContent="center" sx={{ px: 1 }}>
      <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: v('--text-main') }} noWrap>
        {outTime}
      </Typography>
    </Stack>
    <Stack justifyContent="center" sx={{ px: 0.7 }}>
      <Chip
        label={onAir ? 'On Air' : next ? 'Next' : locked ? 'Locked' : 'Queued'}
        size="small"
        sx={{
          ...studioPillSx,
          justifyContent: 'center',
          bgcolor: onAir
            ? 'rgba(var(--success-rgb),0.14)'
            : next
              ? 'rgba(var(--secondary-rgb),0.14)'
              : locked
                ? 'rgba(var(--warning-rgb),0.14)'
                : 'var(--bg-body)',
          color: onAir ? v('--success') : next ? v('--secondary') : locked ? v('--warning') : v('--text-secondary'),
          border: '1px solid',
          borderColor: onAir ? v('--success') : next ? v('--secondary') : locked ? v('--warning') : v('--border'),
        }}
      />
    </Stack>
    <Stack justifyContent="center" sx={{ px: 0.4 }}>
      <Stack direction="row" spacing={0.2} alignItems="center" justifyContent="center">
        <Tooltip title="Start from this asset">
          <span>
            <IconButton
              size="small"
              disabled={locked || !canStartFrom}
              onClick={(event) => {
                event.stopPropagation();
                onStartFrom?.();
              }}
              sx={{ borderRadius: 0, color: !locked && canStartFrom ? v('--success') : v('--text-disabled') }}
            >
              <PlayArrowIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={locked ? (lockReason || 'This part of the rundown is locked.') : dragArmed ? 'Tap a target row to move here' : 'Reorder item'}>
          <span>
            <IconButton
              size="small"
              disabled={locked}
              onClick={(event) => {
                event.stopPropagation();
                onArmDrag?.();
              }}
              sx={{ borderRadius: 0, color: dragArmed ? v('--secondary') : v('--text-secondary') }}
            >
              <DragIndicatorIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </span>
        </Tooltip>
        <IconButton
          size="small"
          disabled={locked || !canMoveUp}
          onClick={(event) => {
            event.stopPropagation();
            onMoveUp?.();
          }}
          sx={{ borderRadius: 0, color: !locked && canMoveUp ? v('--text-secondary') : v('--text-disabled') }}
        >
          <ArrowUpwardIcon sx={{ fontSize: 14 }} />
        </IconButton>
        <IconButton
          size="small"
          disabled={locked || !canMoveDown}
          onClick={(event) => {
            event.stopPropagation();
            onMoveDown?.();
          }}
          sx={{ borderRadius: 0, color: !locked && canMoveDown ? v('--text-secondary') : v('--text-disabled') }}
        >
          <ArrowDownwardIcon sx={{ fontSize: 14 }} />
        </IconButton>
        <IconButton
          size="small"
          disabled={locked || !canRemove}
          onClick={(event) => {
            event.stopPropagation();
            onRemove?.();
          }}
          sx={{ borderRadius: 0, color: !locked && canRemove ? 'var(--danger)' : v('--text-disabled') }}
        >
          <DeleteOutlineIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Stack>
      <Typography sx={{ mt: 0.12, fontSize: '0.58rem', fontWeight: 900, color: onAir ? v('--primary') : next ? v('--secondary') : v('--text-muted'), letterSpacing: '0.1em', textTransform: 'uppercase', textAlign: 'center' }}>
        {dragArmed ? 'Move' : onAir ? 'Live' : next ? 'Next' : 'Queued'}
      </Typography>
    </Stack>
  </Box>
), (prev, next) => (
  prev.item === next.item &&
  prev.selected === next.selected &&
  prev.onAir === next.onAir &&
  prev.next === next.next &&
  prev.runtimeItemSecs === next.runtimeItemSecs &&
  prev.runtimeProgressBaseMs === next.runtimeProgressBaseMs &&
  prev.runtimeCurrentItemId === next.runtimeCurrentItemId &&
  prev.outTime === next.outTime &&
  prev.inTime === next.inTime &&
  prev.metaLine === next.metaLine &&
  prev.dragArmed === next.dragArmed &&
  prev.dropMode === next.dropMode &&
  prev.draggable === next.draggable &&
  prev.locked === next.locked &&
  prev.lockReason === next.lockReason &&
  prev.domId === next.domId &&
  prev.canStartFrom === next.canStartFrom &&
  prev.canMoveUp === next.canMoveUp &&
  prev.canMoveDown === next.canMoveDown &&
  prev.canRemove === next.canRemove &&
  prev.bulkSelected === next.bulkSelected
));

interface PlayKastrTabProps {
  activeChannel: StreamChannel | null;
  stats?: StreamStats | null;
  analytics?: StreamAnalytics | null;
  isStreamLive?: boolean;
  user?: {
    alwaysRecord?: boolean;
    streamName?: string;
    firstName?: string | null;
    lastName?: string | null;
    country?: string | null;
  } | null;
}

const PlayKastrTabComponent: React.FC<PlayKastrTabProps> = ({ activeChannel, stats, analytics, isStreamLive = false, user = null }) => {
  const { showSnackbar } = useSnackbar();
  const isCoarsePointer = useMediaQuery('(pointer: coarse)');
  const isAssetGridMd = useMediaQuery('(min-width:900px)');
  const isAssetGridXl = useMediaQuery('(min-width:1536px)');
  const [workspace, setWorkspace] = useState<PlayKastrWorkspaceResponse | null>(null);
  const [runtimePlayout, setRuntimePlayout] = useState<PlayKastrActivePlayout | null>(null);
  const applyRuntimePlayout = useCallback((next: PlayKastrActivePlayout | null) => {
    setRuntimePlayout((current) => (
      runtimePlayoutSignature(current) === runtimePlayoutSignature(next) ? current : next
    ));
  }, []);
  const [workspaceError, setWorkspaceError] = useState<string | null>(null);
  const [playkastrPreviewType, setPlaykastrPreviewType] = useState<PreviewType>('webrtc');
  const [credentialsDialogOpen, setCredentialsDialogOpen] = useState(false);
  const [credentialsProtocol, setCredentialsProtocol] = useState<'srt' | 'rtmp'>('srt');
  const [assets, setAssets] = useState<PlayKastrAsset[]>([]);
  const [folders, setFolders] = useState<PlayKastrFolder[]>([]);
  const [destinations, setDestinations] = useState<Transcoder[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyAction, setBusyAction] = useState<'start' | 'stop' | 'reload' | 'continue' | 'skip' | 'save' | 'generate' | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [uploadQueue, setUploadQueue] = useState<UploadQueueItem[]>([]);
  const [serverUploadJobs, setServerUploadJobs] = useState<PlayKastrUploadJob[]>([]);
  const [workspaceView, setWorkspaceView] = useState<WorkspaceView>('rundown');
  const [assetViewMode, setAssetViewMode] = useState<AssetViewMode>('list');
  const [libraryQuery, setLibraryQuery] = useState('');
  const [libraryFilter, setLibraryFilter] = useState<'ALL' | 'READY' | 'PROCESSING' | 'ERROR'>('ALL');
  const [libraryPage, setLibraryPage] = useState(1);
  const [folderFilter, setFolderFilter] = useState<number | 'ALL' | 'UNFILED'>('ALL');
  const [selectedAssetId, setSelectedAssetId] = useState<number | null>(null);
  const [selectedAssetIds, setSelectedAssetIds] = useState<number[]>([]);
  const [assetScrubberPreviewOpen, setAssetScrubberPreviewOpen] = useState(false);
  const [clockDraft, setClockDraft] = useState('');
  const [scheduleDateDraft, setScheduleDateDraft] = useState(() => new Date().toISOString().slice(0, 10));
  const [clockSaving, setClockSaving] = useState(false);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [scheduleEnabledDraft, setScheduleEnabledDraft] = useState(false);
  const [scheduleRecurrenceDraft, setScheduleRecurrenceDraft] = useState<'DAILY' | 'WEEKLY'>('DAILY');
  const [scheduleWeekdayDraft, setScheduleWeekdayDraft] = useState(0);
  const [playlistActionDialog, setPlaylistActionDialog] = useState<null | 'edit' | 'delete'>(null);
  const [playlistActionTargetId, setPlaylistActionTargetId] = useState<number | null>(null);
  const [playlistRenameDraft, setPlaylistRenameDraft] = useState('');
  const [playlistFrequencyDraft, setPlaylistFrequencyDraft] = useState<'DAILY' | 'WEEKLY'>('DAILY');
  const [playlistDateDraft, setPlaylistDateDraft] = useState(() => new Date().toISOString().slice(0, 10));
  const [playlistTimeDraft, setPlaylistTimeDraft] = useState(() => formatClockNowLocal().slice(0, 5));
  const [selectedRundownId, setSelectedRundownId] = useState<number | null>(null);
  const [selectedRundownItemIds, setSelectedRundownItemIds] = useState<number[]>([]);
  const [selectedPlaylistId, setSelectedPlaylistId] = useState<number | null>(null);
  const [draggedRundownItemId, setDraggedRundownItemId] = useState<number | null>(null);
  const [draggedAssetId, setDraggedAssetId] = useState<number | null>(null);
  const [playkastrLogs, setPlaykastrLogs] = useState<PlayKastrLogEntry[]>([]);
  const [playkastrLogRange, setPlaykastrLogRange] = useState<PlaykastrLogRange>('24h');
  const [playkastrLogPage, setPlaykastrLogPage] = useState(1);
  const [playkastrLogTotal, setPlaykastrLogTotal] = useState(0);
  const [playkastrLogLoading, setPlaykastrLogLoading] = useState(false);
  const [folderPickerAnchorEl, setFolderPickerAnchorEl] = useState<HTMLElement | null>(null);
  const [folderPickerAssetId, setFolderPickerAssetId] = useState<number | null>(null);
  const [bulkFolderPickerAnchorEl, setBulkFolderPickerAnchorEl] = useState<HTMLElement | null>(null);
  const [assetTypePickerAnchorEl, setAssetTypePickerAnchorEl] = useState<HTMLElement | null>(null);
  const [assetTypePickerAssetId, setAssetTypePickerAssetId] = useState<number | null>(null);
  const [assetOptionsAnchorEl, setAssetOptionsAnchorEl] = useState<HTMLElement | null>(null);
  const [assetOptionsAssetId, setAssetOptionsAssetId] = useState<number | null>(null);
  const [metadataDialogOpen, setMetadataDialogOpen] = useState(false);
  const [metadataAssetId, setMetadataAssetId] = useState<number | null>(null);
  const [assetMetadataTitleDraft, setAssetMetadataTitleDraft] = useState('');
  const [assetMetadataSynopsisDraft, setAssetMetadataSynopsisDraft] = useState('');
  const [assetMetadataTypeDraft, setAssetMetadataTypeDraft] = useState<(typeof programTypeOptions)[number]>('Movie');
  const [assetMetadataAgeRatingDraft, setAssetMetadataAgeRatingDraft] = useState('');
  const [assetMetadataGenreDraft, setAssetMetadataGenreDraft] = useState('');
  const [assetMetadataSaving, setAssetMetadataSaving] = useState(false);
  const [createPlaylistDialogOpen, setCreatePlaylistDialogOpen] = useState(false);
  const [playlistManagerMode, setPlaylistManagerMode] = useState<PlaylistManagerMode>('open');
  const [newPlaylistNameDraft, setNewPlaylistNameDraft] = useState('');
  const [newPlaylistFrequencyUnit, setNewPlaylistFrequencyUnit] = useState<'DAILY' | 'WEEKLY'>('DAILY');
  const [newPlaylistDateDraft, setNewPlaylistDateDraft] = useState(() => new Date().toISOString().slice(0, 10));
  const [newPlaylistTimeDraft, setNewPlaylistTimeDraft] = useState(() => formatClockNowLocal().slice(0, 5));
  const [createPlaylistSaving, setCreatePlaylistSaving] = useState(false);
  const [createAutopopulateDraft, setCreateAutopopulateDraft] = useState(false);
  const [createAutopopulateAllBlocksDraft, setCreateAutopopulateAllBlocksDraft] = useState(false);
  const [removeDialogItemId, setRemoveDialogItemId] = useState<number | null>(null);
  const [importLiveDialogOpen, setImportLiveDialogOpen] = useState(false);
  const [liveSourceUrlDraft, setLiveSourceUrlDraft] = useState('');
  const [liveSourceLabelDraft, setLiveSourceLabelDraft] = useState('');
  const [liveExpectedDurationDraft, setLiveExpectedDurationDraft] = useState('1800');
  const [liveGenreDraft, setLiveGenreDraft] = useState('');
  const [liveAgeRatingDraft, setLiveAgeRatingDraft] = useState('');
  const [importLiveSaving, setImportLiveSaving] = useState(false);
  const [mediaInfoDialogOpen, setMediaInfoDialogOpen] = useState(false);
  const [mediaInfoLoading, setMediaInfoLoading] = useState(false);
  const [mediaInfoAsset, setMediaInfoAsset] = useState<PlayKastrAsset | null>(null);
  const [mediaInfo, setMediaInfo] = useState<PlayKastrMediaInfo | null>(null);
  const [insertDialogOpen, setInsertDialogOpen] = useState(false);
  const [insertScopeDraft, setInsertScopeDraft] = useState<'selected' | 'all'>('selected');
  const [insertRandomDraft, setInsertRandomDraft] = useState(true);
  const [insertSaving, setInsertSaving] = useState(false);
  const [assetScrollTop, setAssetScrollTop] = useState(0);
  const [rundownScrollTop, setRundownScrollTop] = useState(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const rundownScrollRef = useRef<HTMLDivElement | null>(null);
  const uploadFileMapRef = useRef<Record<string, File>>({});
  const uploadAbortControllersRef = useRef<Record<string, AbortController>>({});
  const lastWorkspaceErrorRef = useRef<string | null>(null);
  const workspaceViewKey = workspaceViewStorageKey(activeChannel?.id);
  const previewPreferenceKey = useMemo(
    () => `streamy.playkastrPreviewType.${activeChannel?.channelHashId || activeChannel?.hashId || activeChannel?.id || 'default'}`,
    [activeChannel?.channelHashId, activeChannel?.hashId, activeChannel?.id],
  );
  const previewPollTimerRef = useRef<number | null>(null);
  const runtimePollTimerRef = useRef<number | null>(null);
  const staticDataLastLoadedAtRef = useRef(0);
  const workspaceEditorSignatureRef = useRef<string | null>(null);

  useEffect(() => {
    setSelectedPlaylistId(null);
    setWorkspace(null);
    setRuntimePlayout(null);
    setWorkspaceError(null);
    staticDataLastLoadedAtRef.current = 0;
    workspaceEditorSignatureRef.current = null;
    lastWorkspaceErrorRef.current = null;
    setWorkspaceView('rundown');
  }, [activeChannel?.id]);

  useEffect(() => {
    try {
      const saved = window.localStorage.getItem(previewPreferenceKey);
      setPlaykastrPreviewType(saved === 'hls' ? 'hls' : 'webrtc');
    } catch {
      setPlaykastrPreviewType('webrtc');
    }
  }, [previewPreferenceKey]);

  const selectPlaykastrPreviewType = (nextType: PreviewType) => {
    setPlaykastrPreviewType(nextType);
    try {
      window.localStorage.setItem(previewPreferenceKey, nextType);
    } catch {}
  };

  const refreshAssets = async (channel: StreamChannel) => {
    const [assetData, folderData, uploadJobData] = await Promise.all([
      listPlayKastrAssets(channel),
      listPlayKastrFolders(channel),
      listPlayKastrUploadJobs(channel).catch(() => ({ items: [] })),
    ]);
    setAssets(assetData.items || []);
    setFolders(folderData.items || []);
    setServerUploadJobs(uploadJobData.items || []);
  };

  const resolvePlaylistRouteForId = (playlistId?: number | null) => {
    if (!playlistId) return null;
    const current = workspace?.studio?.playlist;
    if (Number(current?.id) === Number(playlistId)) return resolvePlayKastrPlaylistRouteId(current);
    const matched = (workspace?.studio?.playlists || []).find((playlist) => Number(playlist.id) === Number(playlistId));
    return matched ? resolvePlayKastrPlaylistRouteId(matched) : playlistId;
  };

  const refreshWorkspace = async (channel: StreamChannel, playlistId?: number | string | null) => {
    const data = await getPlayKastrWorkspace(channel, playlistId ?? resolvePlaylistRouteForId(selectedPlaylistId));
    workspaceEditorSignatureRef.current = buildWorkspaceEditorSignature(data);
    setWorkspace(data);
    applyRuntimePlayout(data.studio?.active_playout || data.activePlayout || null);
    if (data.studio?.playlist?.id) {
      setSelectedPlaylistId(data.studio.playlist.id);
    } else if (typeof playlistId === 'number') {
      setSelectedPlaylistId(playlistId);
    }
    setWorkspaceError(null);
    lastWorkspaceErrorRef.current = null;
    return data;
  };

  useEffect(() => {
    let cancelled = false;

    const clearPollTimer = () => {
      if (previewPollTimerRef.current) {
        window.clearTimeout(previewPollTimerRef.current);
        previewPollTimerRef.current = null;
      }
    };

    const run = async (showLoader = false) => {
      if (document.hidden) return;
      if (!activeChannel?.playkastrActive) {
        setWorkspace(null);
        setRuntimePlayout(null);
        setWorkspaceError(null);
        setAssets([]);
        setDestinations([]);
        setLoading(false);
        return;
      }

      if (showLoader) setLoading(true);

      const shouldRefreshStaticData =
        showLoader ||
        staticDataLastLoadedAtRef.current === 0 ||
        Date.now() - staticDataLastLoadedAtRef.current > 120000;

      const [workspaceResult, assetsResult, destinationsResult] = await Promise.allSettled([
        getPlayKastrWorkspace(activeChannel, resolvePlaylistRouteForId(selectedPlaylistId)),
        shouldRefreshStaticData ? Promise.all([listPlayKastrAssets(activeChannel), listPlayKastrFolders(activeChannel)]) : Promise.resolve(null),
        shouldRefreshStaticData ? getTranscoders(activeChannel) : Promise.resolve(null),
      ]);

      if (cancelled) return;

      if (workspaceResult.status === 'fulfilled') {
        const nextWorkspace = workspaceResult.value;
        const nextSignature = buildWorkspaceEditorSignature(nextWorkspace);
        if (showLoader || nextSignature !== workspaceEditorSignatureRef.current) {
          workspaceEditorSignatureRef.current = nextSignature;
          setWorkspace(nextWorkspace);
        }
        applyRuntimePlayout(workspaceResult.value.studio?.active_playout || workspaceResult.value.activePlayout || null);
        if (!selectedPlaylistId && workspaceResult.value.studio?.playlist?.id) {
          setSelectedPlaylistId(workspaceResult.value.studio.playlist.id);
        }
        setWorkspaceError(null);
        lastWorkspaceErrorRef.current = null;
      } else {
        const message =
          (workspaceResult.reason as any)?.response?.data?.message ||
          (workspaceResult.reason as any)?.message ||
          'Failed to load PlayKastr workspace';
        setWorkspaceError(message);
        if (lastWorkspaceErrorRef.current !== message) {
          showSnackbar(message, 'error');
          lastWorkspaceErrorRef.current = message;
        }
      }

      if (assetsResult.status === 'fulfilled' && assetsResult.value) {
        setAssets(assetsResult.value[0].items || []);
        setFolders(assetsResult.value[1].items || []);
      }
      if (destinationsResult.status === 'fulfilled' && destinationsResult.value) {
        setDestinations(destinationsResult.value || []);
      }
      if (shouldRefreshStaticData) {
        staticDataLastLoadedAtRef.current = Date.now();
      }

      setLoading(false);

      if (!cancelled) {
        clearPollTimer();
        const studioPayload = workspaceResult.status === 'fulfilled' ? workspaceResult.value?.studio : null;
        const runtimeStatus = String(studioPayload?.active_playout?.snapshot?.runtime?.status || studioPayload?.active_playout?.snapshot?.session?.status || studioPayload?.active_playout?.session?.status || studioPayload?.snapshot?.runtime?.status || studioPayload?.snapshot?.session?.status || studioPayload?.active_session?.status || '').toUpperCase();
        const nextPollDelay = runtimeStatus === 'PLAYING' || runtimeStatus === 'STARTING' ? 60000 : 15000;
        previewPollTimerRef.current = window.setTimeout(() => {
          void run(false);
        }, nextPollDelay);
      }
    };

    const onVisibility = () => {
      if (document.hidden) {
        clearPollTimer();
        return;
      }
      void run(false);
    };

    document.addEventListener('visibilitychange', onVisibility);
    run(true);

    return () => {
      cancelled = true;
      clearPollTimer();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [activeChannel, applyRuntimePlayout, selectedPlaylistId, showSnackbar]);

  useEffect(() => {
    let cancelled = false;

    const clearRuntimePollTimer = () => {
      if (runtimePollTimerRef.current) {
        window.clearTimeout(runtimePollTimerRef.current);
        runtimePollTimerRef.current = null;
      }
    };

    const run = async () => {
      if (document.hidden) return;
      if (!activeChannel?.playkastrActive) {
        setRuntimePlayout(null);
        return;
      }

      let nextDelay = 8000;
      try {
        const status = await getPlayKastrPlayoutStatus(activeChannel);
        if (cancelled) return;
        const active = status.active_playout || status.activePlayout || null;
        applyRuntimePlayout(active);
        const runtimeStatus = String(active?.snapshot?.runtime?.status || active?.snapshot?.session?.status || active?.session?.status || '').toUpperCase();
        nextDelay = runtimeStatus === 'PLAYING' || runtimeStatus === 'STARTING' ? 1000 : 8000;
      } catch (_error) {
        nextDelay = 5000;
      }

      if (!cancelled) {
        clearRuntimePollTimer();
        runtimePollTimerRef.current = window.setTimeout(() => {
          void run();
        }, nextDelay);
      }
    };

    const onVisibility = () => {
      if (document.hidden) {
        clearRuntimePollTimer();
        return;
      }
      void run();
    };

    document.addEventListener('visibilitychange', onVisibility);
    void run();

    return () => {
      cancelled = true;
      clearRuntimePollTimer();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [activeChannel, applyRuntimePlayout]);

  const handleAction = async (action: 'start' | 'stop' | 'reload' | 'continue' | 'skip' | 'save' | 'generate') => {
    if (!workspace?.studio || !activeChannel) return;
    try {
      setBusyAction(action);
      if (action === 'start') {
        await startPlayKastrPlayout(resolvePlayKastrPlaylistRouteId(workspace.studio.playlist));
      } else if (action === 'continue') {
        await continuePlayKastrPlayout(resolvePlayKastrPlaylistRouteId(workspace.studio.playlist));
      } else if (action === 'generate') {
        const snapshot = await generatePlayKastrPlaylistSnapshot(activeChannel, resolvePlayKastrPlaylistRouteId(workspace.studio.playlist));
        showSnackbar(`Rundown generated: ${snapshot.itemCount} items ready for playout and EPG.`, 'success');
      } else if (action === 'save' && !isRunning) {
        showSnackbar('Rundown edits are already saved. Use Generate to rebuild the playout and EPG snapshot.', 'info');
      } else {
        const liveSession = workspace.studio.active_playout?.snapshot?.session || workspace.studio.active_playout?.session || workspace.activePlayout?.snapshot?.session || workspace.activePlayout?.session || workspace.studio.snapshot?.session || workspace.studio.active_session || null;
        const sessionId = resolvePlayKastrSessionRouteId(liveSession);
        if (!sessionId) return;
        if (action === 'stop') await stopPlayKastrSession(sessionId);
        else if (action === 'reload') await reloadPlayKastrSession(sessionId);
        else if (action === 'save') {
          const response = await applyPlayKastrPendingChanges(sessionId);
          showSnackbar(response?.apply_description || 'Rundown saved to the active session.', 'success');
        } else await skipPlayKastrSessionItem(sessionId);
      }
      await refreshWorkspace(activeChannel);
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || error?.message || `Failed to ${action} PlayKastr`, 'error');
    } finally {
      setBusyAction(null);
    }
  };

  const handleUploadClick = () => fileInputRef.current?.click();

  const enqueueUploadFiles = (files?: FileList | File[] | null) => {
    if (!files || files.length === 0) return;
    const nextItems: UploadQueueItem[] = Array.from(files).map((file) => {
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}-${file.name}`;
      uploadFileMapRef.current[id] = file;
      return {
        id,
        name: file.name,
        size: file.size,
        progress: 0,
        status: 'pending',
        error: null,
        assetId: null,
      };
    });
    setUploadQueue((current) => [...current, ...nextItems]);
  };

  const handleClearFinishedUploads = () => {
    setUploadQueue((current) => current.filter((item) => ['pending', 'uploading', 'processing'].includes(item.status)));
    setServerUploadJobs((current) => current.filter((job) => {
      const status = String(job.status || '').toUpperCase();
      return status === 'UPLOADING' || status === 'PROCESSING';
    }));
  };

  const handleRetryUpload = (itemId: string) => {
    const file = uploadFileMapRef.current[itemId];
    if (!file) {
      showSnackbar('Original file is no longer available. Re-add it to retry.', 'error');
      return;
    }
    setUploadQueue((current) => current.map((entry) => entry.id === itemId ? { ...entry, status: 'pending', progress: 0, error: null } : entry));
  };

  const handleFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files ? Array.from(event.target.files) : [];
    event.target.value = '';
    enqueueUploadFiles(files);
  };

  const handleCancelUpload = async (item: UploadQueueItem) => {
    const controller = uploadAbortControllersRef.current[item.id];
    if (controller) {
      controller.abort();
      delete uploadAbortControllersRef.current[item.id];
    }
    delete uploadFileMapRef.current[item.id];

    if (!activeChannel) {
      setUploadQueue((current) => current.filter((entry) => entry.id !== item.id));
      return;
    }
    if (item.jobId) {
      try {
        await cancelPlayKastrUploadJob(activeChannel, item.jobId);
      } catch (_error) {
        // The backend may have already cleaned it up.
      }
    }
    setUploadQueue((current) => current.filter((entry) => entry.id !== item.id));
    setServerUploadJobs((current) => current.filter((entry) => Number(entry.id) !== Number(item.jobId || 0)));
    showSnackbar('Upload cancelled.', 'success');
  };

  const handleToggleSelectAllAssets = () => {
    if (allFilteredSelected) {
      setSelectedAssetIds((current) => current.filter((id) => !paginatedAssets.some((asset) => asset.id === id)));
      return;
    }
    setSelectedAssetIds((current) => Array.from(new Set([...current, ...paginatedAssets.map((asset) => asset.id)])));
  };

  const handleDownloadTechnicalReport = async () => {
    if (!activeChannel) return;
    try {
      const blob = await downloadPlayKastrTechnicalReport(activeChannel);
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `${String(activeChannel?.name || 'playkaster').toLowerCase()}-technical-report.csv`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
      showSnackbar('Technical report downloaded.', 'success');
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || error?.message || 'Failed to download technical report', 'error');
    }
  };

  const openMediaInfoDialog = async (asset: PlayKastrAsset) => {
    if (!activeChannel) return;
    setMediaInfoAsset(asset);
    setMediaInfoDialogOpen(true);
    setMediaInfo(null);
    setMediaInfoLoading(true);
    try {
      const details = await getPlayKastrMediaInfo(activeChannel, asset.id);
      setMediaInfo(details);
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || error?.message || 'Failed to load media info', 'error');
    } finally {
      setMediaInfoLoading(false);
    }
  };

  const handleSelectPlaylist = async (playlistId: number) => {
    if (!activeChannel) return;
    try {
      const routeId = resolvePlaylistRouteForId(playlistId);
      setSelectedPlaylistId(playlistId);
      setLoading(true);
      await refreshWorkspace(activeChannel, routeId);
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || error?.message || 'Failed to switch playlist', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenPlaylistManager = (mode: PlaylistManagerMode = 'open') => {
    setPlaylistManagerMode(mode);
    setCreatePlaylistDialogOpen(true);
  };

  const handleCreatePlaylist = async () => {
    handleOpenPlaylistManager('create');
  };

  const handleSubmitImportLive = async () => {
    if (!activeChannel || !studio?.playlist?.id) return;
    const sourceUrl = liveSourceUrlDraft.trim();
    const sourceLabel = liveSourceLabelDraft.trim();
    const expectedDurationSecs = Number(liveExpectedDurationDraft || 0);
    const liveGenre = liveGenreDraft.trim();
    const liveAgeRating = liveAgeRatingDraft.trim();
    if (!sourceUrl) {
      showSnackbar('Live source URL is required.', 'error');
      return;
    }
    if (!Number.isFinite(expectedDurationSecs) || expectedDurationSecs <= 0) {
      showSnackbar('Expected live duration must be greater than zero.', 'error');
      return;
    }
    if (!liveGenre) {
      showSnackbar('Genre is required for imported live.', 'error');
      return;
    }
    if (!liveAgeRating) {
      showSnackbar('Age rating is required for imported live.', 'error');
      return;
    }
    try {
      setImportLiveSaving(true);
      await appendPlayKastrLiveItem(activeChannel, resolvePlayKastrPlaylistRouteId(studio.playlist), {
        sourceUrl,
        sourceLabel: sourceLabel || null,
        expectedDurationSecs,
        epgType: 'Live',
        epgGenre: liveGenre,
        epgAgeRating: liveAgeRating,
        epgTitle: sourceLabel || 'Live Feed',
      });
      setImportLiveDialogOpen(false);
      setLiveSourceUrlDraft('');
      setLiveSourceLabelDraft('');
      setLiveExpectedDurationDraft('1800');
      setLiveGenreDraft('');
      setLiveAgeRatingDraft('');
      await refreshWorkspace(activeChannel, studio.playlist.id);
      showSnackbar('Live source added to rundown.', 'success');
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || error?.message || 'Failed to import live feed', 'error');
    } finally {
      setImportLiveSaving(false);
    }
  };

  const handleSubmitCreatePlaylist = async () => {
    if (!activeChannel) return;
    try {
      setCreatePlaylistSaving(true);
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Africa/Nairobi';
      const randomFour = String(Math.floor(1000 + Math.random() * 9000));
      const baseName = (newPlaylistNameDraft.trim() || 'playlist').replace(/\s+/g, '_');
      const recurrence = 'BLOCK_6H';
      const createdBlocks = [];
      for (let index = 0; index < 4; index += 1) {
        const blockStart = addHoursToLocalDateTime(newPlaylistDateDraft, newPlaylistTimeDraft, index * 6);
        const windowParts = computePlaylistWindow(newPlaylistFrequencyUnit, blockStart.date, blockStart.time);
        const created = await createPlayKastrPlaylist(activeChannel, {
          name: buildBlockPlaylistNameLabel({
            baseName,
            randomFour,
            blockIndex: index + 1,
            startDate: windowParts.startDateText,
            startTime: windowParts.startTimeText,
            endDate: windowParts.endDateText,
            endTime: windowParts.endTimeText,
          }),
          mode: 'SEQUENTIAL',
          timezone,
          startDate: windowParts.startDateText,
          clockStart: `${windowParts.startTimeText}:00`,
          recurrence,
          weekdays: null,
        });
        createdBlocks.push(created);
      }
      for (let index = 0; index < createdBlocks.length; index += 1) {
        const playlist = createdBlocks[index];
        const nextPlaylist = createdBlocks[index + 1] || null;
        await updatePlayKastrPlaylist(activeChannel, Number(playlist.id), {
          nextPlaylistId: nextPlaylist?.playlistHashId || nextPlaylist?.playlist_hash_id || nextPlaylist?.hashId || nextPlaylist?.id || null,
        });
      }
      if (createAutopopulateDraft) {
        const blocksToPopulate = createAutopopulateAllBlocksDraft ? createdBlocks : createdBlocks.slice(0, 1);
        for (const playlist of blocksToPopulate) {
          await populatePlayKastrPlaylistBlock(activeChannel, resolvePlayKastrPlaylistRouteId(playlist));
        }
      }
      setCreatePlaylistDialogOpen(false);
      await refreshWorkspace(activeChannel, createdBlocks[0]?.id || null);
      showSnackbar('Four playlist blocks created.', 'success');
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || error?.message || 'Failed to create playlist', 'error');
    } finally {
      setCreatePlaylistSaving(false);
    }
  };

  const getBlockLoopGroup = () => {
    const all = studio?.playlists || [];
    const current = studio?.playlist;
    if (!current?.id) return [];
    const currentDate = current.start_date || null;
    const sameDay = all.filter((playlist) => {
      if (currentDate && playlist.start_date !== currentDate) return false;
      const clock = parseClockParts(playlist.clock_start);
      if (!clock) return false;
      return clock.minutes === 0 && clock.seconds === 0 && [0, 6, 12, 18].includes(clock.hours);
    });
    const source = sameDay.some((playlist) => Number(playlist.id) === Number(current.id)) ? sameDay : all;
    return [...source]
      .filter((playlist) => playlist.id)
      .sort((a, b) => {
        const aClock = parseClockParts(a.clock_start);
        const bClock = parseClockParts(b.clock_start);
        const aSecs = aClock ? (aClock.hours * 3600) + (aClock.minutes * 60) + aClock.seconds : Number.MAX_SAFE_INTEGER;
        const bSecs = bClock ? (bClock.hours * 3600) + (bClock.minutes * 60) + bClock.seconds : Number.MAX_SAFE_INTEGER;
        return aSecs - bSecs || Number(a.id) - Number(b.id);
      });
  };

  const playlistHasLoopAllChain = () => {
    const currentId = Number(studio?.playlist?.id || 0);
    if (!currentId || String(studio?.playlist?.mode || '').toUpperCase() === 'LOOP') return false;
    const byId = new Map((studio?.playlists || []).map((playlist) => [Number(playlist.id), playlist]));
    const seen = new Set<number>();
    let cursor = Number(studio?.playlist?.next_playlist_id || 0);
    while (cursor && byId.has(cursor) && !seen.has(cursor)) {
      if (cursor === currentId) return true;
      seen.add(cursor);
      cursor = Number(byId.get(cursor)?.next_playlist_id || 0);
    }
    return cursor === currentId;
  };

  const handleCyclePlaylistMode = async () => {
    if (!activeChannel || !studio?.playlist?.id) return;
    const currentMode = String(studio.playlist.mode || 'SEQUENTIAL').toUpperCase();
    const currentlyLoopAll = playlistHasLoopAllChain();
    try {
      let message = 'Playlist mode set to Sequential.';
      if (currentMode === 'LOOP') {
        const group = getBlockLoopGroup();
        if (group.length < 2) {
          await updatePlayKastrPlaylist(activeChannel, studio.playlist.id, { mode: 'SEQUENTIAL' });
          message = 'Playlist mode set to Sequential.';
        } else {
          for (let index = 0; index < group.length; index += 1) {
            const playlist = group[index];
            const next = group[(index + 1) % group.length];
            await updatePlayKastrPlaylist(activeChannel, playlist.id, { mode: 'SEQUENTIAL', nextPlaylistId: Number(next.id) });
          }
          message = 'Playlist mode set to loop all blocks.';
        }
      } else if (currentlyLoopAll) {
        const group = getBlockLoopGroup();
        for (const playlist of group) {
          await updatePlayKastrPlaylist(activeChannel, playlist.id, { mode: 'SEQUENTIAL', nextPlaylistId: null });
        }
        message = 'Playlist mode set to Sequential.';
      } else {
        await updatePlayKastrPlaylist(activeChannel, studio.playlist.id, { mode: 'LOOP' });
        message = 'Playlist mode set to loop this block.';
      }
      const activeSessionId = studio?.snapshot?.session?.id || studio?.active_session?.id || null;
      if (isRunning && activeSessionId) {
        await reloadPlayKastrSession(activeSessionId);
      }
      await refreshWorkspace(activeChannel, studio.playlist.id);
      showSnackbar(isRunning ? `${message} Applied to the live session.` : message, 'success');
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || error?.message || 'Failed to update playlist mode', 'error');
    }
  };

  const handleClearAllItems = async () => {
    if (!activeChannel || !studio?.playlist?.id || playlistItems.length === 0) return;
    if (!window.confirm('Clear all rundown items from this playlist?')) return;
    try {
      await clearPlayKastrPlaylist(activeChannel, resolvePlayKastrPlaylistRouteId(studio.playlist));
      await refreshWorkspace(activeChannel, studio.playlist.id);
      showSnackbar('Rundown cleared.', 'success');
      setSelectedRundownId(null);
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || error?.message || 'Failed to clear rundown', 'error');
    }
  };

  const handlePopulateCurrentBlock = async () => {
    if (!activeChannel || !studio?.playlist?.id) return;
    try {
      await populatePlayKastrPlaylistBlock(activeChannel, resolvePlayKastrPlaylistRouteId(studio.playlist));
      await refreshWorkspace(activeChannel, studio.playlist.id);
      showSnackbar('Block filler populated.', 'success');
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || error?.message || 'Failed to populate block filler', 'error');
    }
  };

  const handleDeletePlaylist = async (playlistId?: number | null) => {
    const targetId = Number(playlistId || playlistActionTargetId || 0);
    if (!activeChannel || !targetId) return;
    const targetPlaylist = (studio?.playlists || []).find((playlist) => playlist.id === targetId);
    const hasActiveSession = !!(studio?.active_session?.id || studio?.snapshot?.session?.id) && Number(studio?.playlist?.id) === targetId;
    const confirmMessage = hasActiveSession
      ? `Delete playlist "${targetPlaylist?.name || 'selected playlist'}"? The active playout session will be stopped first.`
      : `Delete playlist "${targetPlaylist?.name || 'selected playlist'}"?`;
    if (!window.confirm(confirmMessage)) return;
    try {
      const sessionId = Number(studio?.playlist?.id) === targetId ? (studio?.snapshot?.session?.id || studio?.active_session?.id) : null;
      if (sessionId) {
        await stopPlayKastrSession(sessionId);
      }
      await archivePlayKastrPlaylist(activeChannel, targetId);
      setSelectedPlaylistId(null);
      setPlaylistActionDialog(null);
      await refreshWorkspace(activeChannel, null);
      showSnackbar('Playlist deleted.', 'success');
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || error?.message || 'Failed to delete playlist', 'error');
    }
  };

  const handleSaveClockTime = async () => {
    if (!activeChannel || !studio?.playlist?.id || clockSaving) return;
    const currentValue = studio.playlist.clock_start || '';
    const nextValue = clockDraft.trim();
    if (nextValue === currentValue) return;
    setClockSaving(true);
    try {
      await updatePlayKastrPlaylist(activeChannel, studio.playlist.id, {
        clockStart: nextValue || null,
      });
      await refreshWorkspace(activeChannel, studio.playlist.id);
      showSnackbar('Playlist start updated.', 'success');
    } catch (error: any) {
      setClockDraft(currentValue);
      showSnackbar(error?.response?.data?.message || error?.message || 'Failed to update start time', 'error');
    } finally {
      setClockSaving(false);
    }
  };

  const handleSaveScheduleModal = async () => {
    if (!activeChannel || !studio?.playlist?.id || clockSaving) return;
    setClockSaving(true);
    try {
      await updatePlayKastrPlaylist(activeChannel, studio.playlist.id, {
        startDate: scheduleEnabledDraft ? scheduleDateDraft : null,
        clockStart: scheduleEnabledDraft ? (clockDraft.trim() || formatClockNowLocal()) : null,
        recurrence: scheduleEnabledDraft ? 'BLOCK_6H' : null,
        weekdays: null,
      });
      await refreshWorkspace(activeChannel, studio.playlist.id);
      setScheduleModalOpen(false);
      showSnackbar('Schedule updated.', 'success');
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || error?.message || 'Failed to update schedule', 'error');
    } finally {
      setClockSaving(false);
    }
  };

  const handleToggleScheduleEnabled = async () => {
    if (!activeChannel || !studio?.playlist?.id || clockSaving) return;
    const scheduleEnabled = !!String(studio.playlist.clock_start || '').trim();
    setClockSaving(true);
    try {
      if (scheduleEnabled) {
        await updatePlayKastrPlaylist(activeChannel, studio.playlist.id, {
          startDate: null,
          clockStart: null,
          recurrence: null,
          weekdays: null,
        });
      } else {
        const nextClock = clockDraft.trim() || formatClockNowLocal();
        await updatePlayKastrPlaylist(activeChannel, studio.playlist.id, {
          startDate: scheduleDateDraft,
          clockStart: nextClock,
          recurrence: 'BLOCK_6H',
          weekdays: null,
        });
      }
      await refreshWorkspace(activeChannel, studio.playlist.id);
      showSnackbar(scheduleEnabled ? 'Schedule disabled.' : 'Schedule enabled.', 'success');
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || error?.message || 'Failed to update schedule state', 'error');
    } finally {
      setClockSaving(false);
    }
  };

  const handleRenamePlaylistDialog = async () => {
    const targetId = Number(playlistActionTargetId || 0);
    const baseName = playlistRenameDraft.trim();
    if (!activeChannel || !targetId || !baseName) return;
    try {
      const randomFour = String(Math.floor(1000 + Math.random() * 9000));
      const targetPlaylist = (studio?.playlists || []).find((playlist) => Number(playlist.id) === targetId);
      const windowParts = computePlaylistWindow(playlistFrequencyDraft, playlistDateDraft, playlistTimeDraft);
      const generatedName = buildBlockPlaylistNameLabel({
        baseName,
        randomFour,
        blockIndex: readBlockNumberFromName(targetPlaylist?.name),
        startDate: windowParts.startDateText,
        startTime: windowParts.startTimeText,
        endDate: windowParts.endDateText,
        endTime: windowParts.endTimeText,
      });
      await updatePlayKastrPlaylist(activeChannel, targetId, {
        name: generatedName,
        startDate: windowParts.startDateText,
        clockStart: `${windowParts.startTimeText}:00`,
        recurrence: 'BLOCK_6H',
        weekdays: null,
      });
      setPlaylistActionDialog(null);
      await refreshWorkspace(activeChannel, targetId);
      showSnackbar('Playlist updated.', 'success');
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || error?.message || 'Failed to update playlist', 'error');
    }
  };

  const handleChangeScheduleRecurrence = async (value: 'DAILY' | 'WEEKLY') => {
    if (!activeChannel || !studio?.playlist?.id || clockSaving) return;
    setClockSaving(true);
    try {
      await updatePlayKastrPlaylist(activeChannel, studio.playlist.id, {
        recurrence: 'BLOCK_6H',
        weekdays: null,
      });
      await refreshWorkspace(activeChannel, studio.playlist.id);
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || error?.message || 'Failed to update recurrence', 'error');
    } finally {
      setClockSaving(false);
    }
  };

  const handleToggleScheduleWeekday = async (bit: number) => {
    if (!activeChannel || !studio?.playlist?.id || clockSaving) return;
    const currentMask = Number(studio.playlist.weekdays || 0);
    const nextMask = currentMask & bit ? currentMask & ~bit : currentMask | bit;
    setClockSaving(true);
    try {
      await updatePlayKastrPlaylist(activeChannel, studio.playlist.id, {
        recurrence: 'WEEKLY',
        weekdays: nextMask || bit,
      });
      await refreshWorkspace(activeChannel, studio.playlist.id);
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || error?.message || 'Failed to update weekdays', 'error');
    } finally {
      setClockSaving(false);
    }
  };

  const handleChangeNextPlaylist = async (value: string) => {
    if (!activeChannel || !studio?.playlist?.id) return;
    const nextPlaylist = value
      ? orderedPlaylists.find((playlist) => resolvePlayKastrPlaylistRouteId(playlist) === value || String(playlist.id) === value)
      : null;
    const nextPlaylistId = nextPlaylist ? resolvePlayKastrPlaylistRouteId(nextPlaylist) : null;
    try {
      await updatePlayKastrPlaylist(activeChannel, resolvePlayKastrPlaylistRouteId(studio.playlist), {
        nextPlaylistId,
      });
      await refreshWorkspace(activeChannel, studio.playlist.id);
      showSnackbar(nextPlaylistId ? 'Next playlist updated.' : 'Next playlist cleared.', 'success');
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || error?.message || 'Failed to update next playlist', 'error');
    }
  };

  const handleCreateFolder = async () => {
    if (!activeChannel) return;
    const name = window.prompt('New folder name')?.trim();
    if (!name) return;
    try {
      await createPlayKastrFolder(activeChannel, name);
      await refreshAssets(activeChannel);
      showSnackbar('Folder created.', 'success');
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || error?.message || 'Failed to create folder', 'error');
    }
  };

  const handleRenameFolder = async () => {
    if (!activeChannel || typeof folderFilter !== 'number') return;
    const folder = folders.find((entry) => entry.id === folderFilter);
    if (!folder) return;
    const name = window.prompt('Rename folder', folder.name)?.trim();
    if (!name || name === folder.name) return;
    try {
      await renamePlayKastrFolder(activeChannel, folder.id, name);
      await refreshAssets(activeChannel);
      showSnackbar('Folder renamed.', 'success');
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || error?.message || 'Failed to rename folder', 'error');
    }
  };

  const handleDeleteFolder = async () => {
    if (!activeChannel || typeof folderFilter !== 'number') return;
    const folder = folders.find((entry) => entry.id === folderFilter);
    if (!folder) return;
    if (!window.confirm(`Delete folder "${folder.name}"? Assets will be moved out of it.`)) return;
    try {
      await deletePlayKastrFolder(activeChannel, folder.id);
      setFolderFilter('ALL');
      await refreshAssets(activeChannel);
      showSnackbar('Folder deleted.', 'success');
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || error?.message || 'Failed to delete folder', 'error');
    }
  };

  const openFolderPicker = (anchorEl: HTMLElement, targetAsset?: PlayKastrAsset | null) => {
    const asset = targetAsset || selectedAsset;
    if (!asset) return;
    setFolderPickerAnchorEl(anchorEl);
    setFolderPickerAssetId(asset.id);
  };

  const closeFolderPicker = () => {
    setFolderPickerAnchorEl(null);
    setFolderPickerAssetId(null);
    setBulkFolderPickerAnchorEl(null);
  };

  const openAssetTypePicker = (anchorEl: HTMLElement, targetAsset?: PlayKastrAsset | null) => {
    const asset = targetAsset || selectedAsset;
    if (!asset) return;
    setAssetTypePickerAnchorEl(anchorEl);
    setAssetTypePickerAssetId(asset.id);
  };

  const closeAssetTypePicker = () => {
    setAssetTypePickerAnchorEl(null);
    setAssetTypePickerAssetId(null);
  };

  const openAssetOptionsMenu = (anchorEl: HTMLElement, targetAsset?: PlayKastrAsset | null) => {
    const asset = targetAsset || selectedAsset;
    if (!asset) return;
    setAssetOptionsAnchorEl(anchorEl);
    setAssetOptionsAssetId(asset.id);
  };

  const closeAssetOptionsMenu = () => {
    setAssetOptionsAnchorEl(null);
    setAssetOptionsAssetId(null);
  };

  const openAssetMetadataDialog = (targetAsset?: PlayKastrAsset | null) => {
    const asset = targetAsset || selectedAsset;
    if (!asset) return;
    setMetadataAssetId(asset.id);
    setAssetMetadataTitleDraft(String(asset.epgTitleDefault || asset.label || ''));
    setAssetMetadataSynopsisDraft(String(asset.epgSynopsisDefault || ''));
    setAssetMetadataTypeDraft((String(asset.epgTypeDefault || 'Movie') as (typeof programTypeOptions)[number]) || 'Movie');
    setAssetMetadataAgeRatingDraft(String(asset.epgAgeRatingDefault || ''));
    setAssetMetadataGenreDraft(String(asset.epgGenreDefault || ''));
    setMetadataDialogOpen(true);
  };

  const handleSaveAssetMetadata = async () => {
    const asset = assets.find((entry) => entry.id === metadataAssetId) || selectedAsset;
    if (!activeChannel || !asset) return;
    try {
      setAssetMetadataSaving(true);
      await updatePlayKastrAsset(activeChannel, asset.id, {
        epgTitleDefault: assetMetadataTitleDraft.trim() || null,
        epgSynopsisDefault: assetMetadataSynopsisDraft.trim().slice(0, 300) || null,
        epgTypeDefault: assetMetadataTypeDraft || null,
        epgAgeRatingDefault: assetMetadataAgeRatingDraft || null,
        epgGenreDefault: assetMetadataGenreDraft || null,
      });
      await refreshAssets(activeChannel);
      setMetadataDialogOpen(false);
      showSnackbar('Program metadata saved.', 'success');
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || error?.message || 'Failed to save program metadata', 'error');
    } finally {
      setAssetMetadataSaving(false);
    }
  };

  const handleMoveAssetToFolder = async (folderId: number | null) => {
    if (!activeChannel) return;
    try {
      if (folderPickerAssetId === -1) {
        const targetAssets = assets.filter((entry) => selectedAssetIds.includes(entry.id));
        if (targetAssets.length === 0) return;
        await Promise.all(targetAssets.map((asset) => updatePlayKastrAsset(activeChannel, asset.id, { folderId })));
        setSelectedAssetIds([]);
      } else {
        const asset = assets.find((entry) => entry.id === folderPickerAssetId) || selectedAsset;
        if (!asset) return;
        await updatePlayKastrAsset(activeChannel, asset.id, { folderId });
      }
      await refreshAssets(activeChannel);
      closeFolderPicker();
      showSnackbar('Asset folder updated.', 'success');
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || error?.message || 'Failed to update asset folder', 'error');
    }
  };

  const handleDeleteSelectedAssets = async () => {
    if (!activeChannel || selectedAssetIds.length === 0) return;
    if (!window.confirm(`Delete ${selectedAssetIds.length} selected asset(s)?`)) return;
    try {
      const targetAssets = assets.filter((entry) => selectedAssetIds.includes(entry.id));
      await Promise.all(targetAssets.map((asset) => deletePlayKastrAsset(activeChannel, asset.id)));
      setSelectedAssetIds([]);
      setSelectedAssetId((current) => (current && selectedAssetIds.includes(current) ? null : current));
      await refreshAssets(activeChannel);
      showSnackbar('Selected assets deleted.', 'success');
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || error?.message || 'Failed to delete selected assets', 'error');
    }
  };

  const openBulkFolderPicker = (anchorEl: HTMLElement) => {
    if (selectedAssetIds.length === 0) return;
    setFolderPickerAssetId(-1);
    setBulkFolderPickerAnchorEl(anchorEl);
    setFolderPickerAnchorEl(anchorEl);
  };

  const clearSelectedAssets = () => {
    setSelectedAssetIds([]);
  };

  const handleSetAssetClass = async (assetClass: 'VIDEO' | 'FILLER' | 'AD', targetAsset?: PlayKastrAsset | null) => {
    const asset = targetAsset || assets.find((entry) => entry.id === assetTypePickerAssetId) || selectedAsset;
    if (!activeChannel || !asset) return;
    try {
      await updatePlayKastrAsset(activeChannel, asset.id, { assetClass });
      await refreshAssets(activeChannel);
      closeAssetTypePicker();
      showSnackbar(`Asset tagged as ${assetClass.toLowerCase()}.`, 'success');
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || error?.message || 'Failed to update asset type', 'error');
    }
  };

  const handleRenameAsset = async (targetAsset?: PlayKastrAsset | null) => {
    const asset = targetAsset || selectedAsset;
    if (!activeChannel || !asset) return;
    const label = window.prompt('Rename asset', asset.label)?.trim();
    if (!label || label === asset.label) return;
    try {
      await updatePlayKastrAsset(activeChannel, asset.id, { label });
      await refreshAssets(activeChannel);
      showSnackbar('Asset renamed.', 'success');
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || error?.message || 'Failed to rename asset', 'error');
    }
  };

  const handleCleanupOriginalAsset = async (targetAsset?: PlayKastrAsset | null) => {
    const asset = targetAsset || selectedAsset;
    if (!activeChannel || !asset) return;
    if (!window.confirm(`Delete the old original file for "${asset.label}" and keep only the normalised PlayKastr copy?`)) return;
    try {
      await cleanupPlayKastrAssetOriginal(activeChannel, asset.id);
      await refreshAssets(activeChannel);
      await refreshWorkspace(activeChannel).catch(() => null);
      showSnackbar('Original asset file cleaned up.', 'success');
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || error?.message || 'Failed to clean up original asset', 'error');
    }
  };

  const handleDeleteAsset = async (targetAsset?: PlayKastrAsset | null) => {
    const asset = targetAsset || selectedAsset;
    if (!activeChannel || !asset) return;
    if (!window.confirm(`Delete asset "${asset.label}"?`)) return;
    try {
      await deletePlayKastrAsset(activeChannel, asset.id);
      setSelectedAssetId((current) => (current === asset.id ? null : current));
      setSelectedAssetIds((current) => current.filter((id) => id !== asset.id));
      await refreshAssets(activeChannel);
      showSnackbar('Asset deleted.', 'success');
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || error?.message || 'Failed to delete asset', 'error');
    }
  };

  const handleDownloadXmltv = async () => {
    if (!activeChannel || !studio?.playlist?.id) return;
    const blob = await downloadPlayKastrXmltv(activeChannel, studio.playlist);
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${String(activeChannel?.name || 'playkaster').toLowerCase()}-epg.xml`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadGracenote = async () => {
    if (!activeChannel || !studio?.playlist?.id) return;
    const blob = await downloadPlayKastrGracenote(activeChannel, studio.playlist);
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${String(activeChannel?.name || 'playkaster').toLowerCase()}-gracenote.json`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(url);
  };

  const playkastrInactive = !activeChannel?.playkastrActive;

  const nowState = statusLabel(stats, analytics);
  const currentSource = sourceLabel(stats);
  const studio = workspace?.studio || null;
  const activePlayout = runtimePlayout || studio?.active_playout || workspace?.activePlayout || null;
  const snapshot = activePlayout?.snapshot || studio?.snapshot || null;
  const playlistItems = studio?.playlist?.items || [];
  const currentItem = snapshot?.now_playing?.label || snapshot?.runtime?.current_label || 'No active item';
  const nextItem = snapshot?.up_next?.[0]?.label || 'Nothing queued';
  const playlistName = studio?.playlist?.name || 'No playlist configured';
  const activePlaylistHashRouteId = resolvePlayKastrPlaylistHashRouteId(studio?.playlist || null);
  const activeSessionRouteId = resolvePlayKastrSessionRouteId(activePlayout?.snapshot?.session || activePlayout?.session || studio?.snapshot?.session || studio?.active_session || null);
  const playlistMode = String(studio?.playlist?.mode || 'SEQUENTIAL').toUpperCase();
  const playlistLoopAll = playlistHasLoopAllChain();
  const playlistModeLabel = playlistMode === 'LOOP' ? 'Loop block' : playlistLoopAll ? 'Loop all blocks' : 'Sequential';
  const orderedPlaylists = useMemo(
    () => [...(studio?.playlists || [])].sort(comparePlaylistsBySchedule),
    [studio?.playlists],
  );
  const playlistManagerGroups = useMemo(() => {
    const today = new Date();
    const todayIso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    const tomorrowIso = `${tomorrow.getFullYear()}-${String(tomorrow.getMonth() + 1).padStart(2, '0')}-${String(tomorrow.getDate()).padStart(2, '0')}`;
    const groups: Array<{ label: string; items: typeof orderedPlaylists }> = [
      { label: 'Today', items: [] },
      { label: 'Tomorrow', items: [] },
      { label: 'Upcoming', items: [] },
      { label: 'Unscheduled', items: [] },
    ];
    orderedPlaylists.forEach((playlist) => {
      const startDate = String(playlist.start_date || '').slice(0, 10);
      if (!startDate) groups[3].items.push(playlist);
      else if (startDate === todayIso) groups[0].items.push(playlist);
      else if (startDate === tomorrowIso) groups[1].items.push(playlist);
      else groups[2].items.push(playlist);
    });
    return groups.filter((group) => group.items.length > 0);
  }, [orderedPlaylists]);
  const activePlayoutStatus = String(activePlayout?.snapshot?.session?.status || activePlayout?.session?.status || snapshot?.session?.status || studio?.active_session?.status || '').toUpperCase();
  const isRunning = activePlayoutStatus === 'PLAYING';
  const playingPlaylistId = Number(activePlayout?.playlist?.id || (snapshot?.runtime as any)?.playlist_id || (snapshot?.session as any)?.playlistId || (snapshot?.session as any)?.playlist_id || 0) || null;
  const loadedPlaylistId = Number(studio?.playlist?.id || 0) || null;
  const selectedPlaylistIsPlaying = !!isRunning && !!playingPlaylistId && !!loadedPlaylistId && playingPlaylistId === loadedPlaylistId;
  const playingPlaylistName = isRunning
    ? (activePlayout?.playlist?.name || (selectedPlaylistIsPlaying ? playlistName : 'Unknown playlist'))
    : 'No active playlist';
  const playingItemLabel = isRunning ? currentItem : 'No active item';
  const playoutIdentityDetail = isRunning
    ? `${playingPlaylistName} / ${playingItemLabel}`
    : `Loaded ${playlistName}`;
  const selectedPlaylistNotice = selectedPlaylistIsPlaying
    ? 'Loaded playlist is on air'
    : isRunning
      ? `Editing ${playlistName}; on air is ${playingPlaylistName}`
      : `Loaded ${playlistName}`;
  const mediaSummary = workspace?.overview?.media_summary;
  const playlistCount = workspace?.overview?.playlist_count || 0;
  const itemCount = playlistItems.length;
  const readyCount = assets.filter((asset) => String(asset.normStatus || '').toUpperCase() === 'READY').length;
  const processingCount = assets.filter((asset) => !['READY', 'ERROR'].includes(String(asset.normStatus || '').toUpperCase())).length;
  const outputRouteReady = Boolean(snapshot?.output_url) || isRunning;
  const pendingLiveChanges = Boolean(snapshot?.pending_changes);
  const liveSaveFeedback = isRunning
    ? (snapshot?.apply_description || (pendingLiveChanges ? 'Generate and apply rundown changes at the next safe boundary.' : 'No pending rundown changes.'))
    : 'Generate the playout snapshot used by PlayKaster runtime and EPG export.';
  const liveSaveStateLabel = pendingLiveChanges ? 'Pending save' : 'Generated';
  const playableDestinations = useMemo(
    () =>
      destinations.filter((entry) => {
        if (String(entry.protocol || '').toLowerCase() === 'hls') return false;
        return !!String(entry.destination || '').trim();
      }),
    [destinations],
  );
  const connectedDestinations = useMemo(
    () =>
      playableDestinations.filter((entry) => {
        const healthState = String(entry.destinationHealth?.status || '').toLowerCase();
        const runtimeState = String(entry.status?.status || '').toLowerCase();
        return (
          healthState === 'connected' ||
          runtimeState === 'connected' ||
          runtimeState === 'running'
        );
      }),
    [playableDestinations],
  );
  const hasConfiguredOutputs = playableDestinations.length > 0;
  const destinationMonitorState =
    playableDestinations.length === 0
      ? 'missing'
      : connectedDestinations.length > 0
        ? 'connected'
        : 'standby';
  const destinationMonitorLabel =
    playableDestinations.length === 0
      ? 'No outputs'
      : connectedDestinations.length > 0
        ? `${connectedDestinations.length} live`
        : `${playableDestinations.length} armed`;
  const outputStatusTone =
    destinationMonitorState === 'connected'
      ? { color: v('--success'), bg: 'rgba(var(--success-rgb),0.14)' }
      : destinationMonitorState === 'standby'
        ? { color: v('--warning'), bg: 'rgba(var(--warning-rgb),0.14)' }
        : { color: v('--warning'), bg: 'rgba(var(--warning-rgb),0.14)' };
  const scheduledPlaylists = useMemo(() => {
    const all = studio?.playlists || [];
    return all
      .filter((playlist) => !!String(playlist.clock_start || '').trim())
      .map((playlist) => {
        const timezone = String(playlist.timezone || 'Africa/Nairobi');
        const clock = parseClockParts(playlist.clock_start);
        const recurrence = String(playlist.recurrence || 'DAILY').toUpperCase();
        const startDate = String(playlist.start_date || '').trim();
        if (!clock) {
          return {
            ...playlist,
            timezone,
            eligibleToday: false,
            nextAt: null as number | null,
            countdownMs: null as number | null,
          };
        }
        const nowDate = new Date();
        const zoned = getZonedDateParts(nowDate, timezone);
        let nextAt: number | null = null;
        let startGate = -Infinity;
        if (startDate) {
          const parsed = new Date(`${startDate}T00:00:00`);
          if (!Number.isNaN(parsed.getTime())) {
            startGate = Date.UTC(parsed.getFullYear(), parsed.getMonth(), parsed.getDate(), clock.hours, clock.minutes, clock.seconds);
          }
        }
        for (let offset = 0; offset < 8; offset += 1) {
          const weekday = (zoned.weekday + offset) % 7;
          const allowed = recurrence !== 'WEEKLY' || weekdayAllowed(weekday, playlist.weekdays);
          if (!allowed) continue;
          const target = Date.UTC(zoned.year, zoned.month - 1, zoned.day + offset, clock.hours, clock.minutes, clock.seconds);
          const current = Date.UTC(zoned.year, zoned.month - 1, zoned.day, zoned.hour, zoned.minute, zoned.second);
          if (target < startGate) continue;
          if (offset === 0 && target < current) continue;
          nextAt = target;
          break;
        }
        const todayTarget = Date.UTC(zoned.year, zoned.month - 1, zoned.day, clock.hours, clock.minutes, clock.seconds);
        const todayEligible = (recurrence !== 'WEEKLY' || weekdayAllowed(zoned.weekday, playlist.weekdays)) && todayTarget >= startGate;
        const currentPseudo = Date.UTC(zoned.year, zoned.month - 1, zoned.day, zoned.hour, zoned.minute, zoned.second);
        return {
          ...playlist,
          timezone,
          eligibleToday: todayEligible,
          nextAt,
          countdownMs: nextAt != null ? Math.max(0, nextAt - currentPseudo) : null,
        };
      })
      .sort((a, b) => {
        if (a.nextAt == null && b.nextAt == null) return a.name.localeCompare(b.name);
        if (a.nextAt == null) return 1;
        if (b.nextAt == null) return -1;
        return a.nextAt - b.nextAt;
      });
  }, [studio?.playlists]);
  const nextScheduledPlaylist = scheduledPlaylists[0] || null;
  const currentScheduledPlaylist = scheduledPlaylists.find((playlist) => Number(playlist.id) === Number(studio?.playlist?.id)) || null;
  const todayScheduledPlaylists = scheduledPlaylists.filter((playlist) => playlist.eligibleToday);
  const nextChainedPlaylist = orderedPlaylists.find((playlist) => Number(playlist.id) === Number(studio?.playlist?.next_playlist_id || 0)) || null;
  const nextChainedSchedule = nextChainedPlaylist
    ? scheduledPlaylists.find((playlist) => Number(playlist.id) === Number(nextChainedPlaylist.id)) || null
    : null;
  const channelRouteId = String(activeChannel?.channelHashId || activeChannel?.hashId || activeChannel?.id || '');
  const credentialStreamName = activeChannel?.name || stats?.streamName || '';
  const credentialStreamKeyPin = activeChannel?.streamKeyPin || stats?.streamKeyPin || '';
  const credentialFullStreamKey = `${credentialStreamName}${credentialStreamKeyPin}`;
  const credentialSrtStreamId = `publish:live/${credentialFullStreamKey}`;
  const credentialPublicHost = window.location.hostname;
  const credentialSrtUrl = `srt://${credentialPublicHost}:8890?streamid=${credentialSrtStreamId}`;
  const credentialRtmpUrl = `rtmp://${credentialPublicHost}/live/${credentialFullStreamKey}`;
  const credentialRtmpServerUrl = `rtmp://${credentialPublicHost}/live/`;
  const playlistTimeZone = String(studio?.playlist?.timezone || 'Africa/Nairobi');
  const playlistWindowStart = playlistItems[0]
    ? getPlaylistItemInLabel(playlistItems[0], playlistTimeZone)
    : (studio?.playlist?.clock_start || '--');
  const currentPlaylistDurationSecs = Number(studio?.timing_summary?.current_playlist_secs ?? playlistItems.reduce((sum, item) => sum + effectivePlaylistItemSecs(item), 0));
  const lastPlaylistItem = playlistItems[playlistItems.length - 1];
  const playlistWindowEnd = lastPlaylistItem
    ? getPlaylistItemOutLabel(lastPlaylistItem, playlistTimeZone)
    : '--';
  const currentPlaylistDueAt = parseScheduledDateTime(studio?.playlist?.start_date, studio?.playlist?.clock_start);
  const currentPlaylistEndAt = parseIsoMillis(lastPlaylistItem?.calculated_end_utc)
    ? new Date(parseIsoMillis(lastPlaylistItem?.calculated_end_utc) as number)
    : (currentPlaylistDueAt && lastPlaylistItem
      ? new Date(currentPlaylistDueAt.getTime() + playlistItems.reduce((sum, item) => sum + (effectivePlaylistItemSecs(item) * 1000), 0))
      : null);
  const nextPlaylistDueAt = parseScheduledDateTime(nextChainedPlaylist?.start_date, nextChainedPlaylist?.clock_start);
  const playlistWindowLimitAt = currentPlaylistDueAt
    ? new Date(currentPlaylistDueAt.getTime() + (6 * 60 * 60 * 1000) - 1000)
    : null;
  const timingSummary = studio?.timing_summary || null;
  const blockFillerDurationSecs = Number(timingSummary?.block_filler_secs ?? 0);
  const totalPlaylistDurationSecs = Number(timingSummary?.combined_playlist_secs ?? currentPlaylistDurationSecs ?? 0);
  const targetPlaylistWindowSecs = Number(timingSummary?.target_window_secs ?? (6 * 60 * 60));
  const playlistWindowDeltaSecs = Number(timingSummary?.gap_delta_secs ?? (targetPlaylistWindowSecs - totalPlaylistDurationSecs));
  const playlistWindowDeltaLabel = playlistWindowDeltaSecs === 0
    ? 'Remaining 0h:00m:00s'
    : playlistWindowDeltaSecs > 0
      ? `Remaining ${formatDurationWithUnits(playlistWindowDeltaSecs)}`
      : `Excess ${formatDurationWithUnits(Math.abs(playlistWindowDeltaSecs))}`;
  const playlistWindowDeltaTone = playlistWindowDeltaSecs === 0
    ? { color: v('--text-secondary'), bg: 'var(--bg-body)' }
    : { color: v('--danger'), bg: 'rgba(var(--danger-rgb),0.14)' };
  const nextScheduleLabel = nextChainedPlaylist?.clock_start || '--';
  const nextScheduleDate = nextChainedPlaylist?.start_date || (playlistWindowLimitAt ? formatDateOnly(playlistWindowLimitAt, String(studio?.playlist?.timezone || 'Africa/Nairobi')) : '--');
  const nextScheduleName = nextChainedPlaylist?.name || (playlistWindowLimitAt ? 'Block window end' : 'No linked block');
  const nextScheduleCountdown = <ScheduleCountdown targetAt={nextChainedSchedule?.nextAt} timeZone={nextChainedSchedule?.timezone} />;
  const playkastrLogPageCount = Math.max(1, Math.ceil(playkastrLogTotal / 25));
  const rundownDateLabel = <RundownDateLabel timeZone={String(studio?.playlist?.timezone || 'Africa/Nairobi')} />;
  const currentPlaylistStartLabel = useMemo(() => {
    const zone = String(studio?.playlist?.timezone || 'Africa/Nairobi');
    const sourceDate = studio?.playlist?.start_date
      ? new Date(`${studio.playlist.start_date}T00:00:00`)
      : (currentScheduledPlaylist?.nextAt ? new Date(currentScheduledPlaylist.nextAt) : new Date());
    return formatDateTimeLabel(sourceDate, zone, studio?.playlist?.clock_start);
  }, [currentScheduledPlaylist?.nextAt, studio?.playlist?.clock_start, studio?.playlist?.start_date, studio?.playlist?.timezone]);
  const currentPlaylistStartDisplay = useMemo(
    () => formatClockValueForMode(studio?.playlist?.clock_start, '24h'),
    [studio?.playlist?.clock_start],
  );
  const epgXmltvUrl = useMemo(() => `${window.location.origin}/api/playkastr/channels/${channelRouteId}/epg/xmltv${activePlaylistHashRouteId ? `?playlistId=${activePlaylistHashRouteId}` : ''}`, [channelRouteId, activePlaylistHashRouteId]);
  const epgJsonUrl = useMemo(() => `${window.location.origin}/api/playkastr/channels/${channelRouteId}/epg/gracenote${activePlaylistHashRouteId ? `?playlistId=${activePlaylistHashRouteId}` : ''}`, [channelRouteId, activePlaylistHashRouteId]);
  const scheduleEnabled = !!String(studio?.playlist?.clock_start || '').trim();
  const scheduleRecurrence = 'DAILY';
  const scheduleWeekdayMask = Number(studio?.playlist?.weekdays || 0);
  const timedEpgItems = useMemo(
    () => workspaceView === 'epg' ? playlistItems.filter((item) => !!(item.scheduled_start || item.calculated_start_utc)) : [],
    [playlistItems, workspaceView],
  );
  const destinationTrafficKbps = playableDestinations.reduce((sum, entry) => (
    sum +
    Math.max(
      Number(entry.destinationHealth?.metrics?.trafficKbps || 0),
      Number(entry.status?.stats?.currentKbps || 0),
      Number(entry.status?.stats?.bitrateKbps || 0),
      Number(entry.status?.stats?.bitrate || 0),
    )
  ), 0);
  const destinationIps = playableDestinations
    .map((entry) => entry.destinationHealth?.metrics?.ip || entry.status?.stats?.ip || entry.status?.stats?.remoteIp || entry.status?.stats?.clientIp || null)
    .filter(Boolean)
    .slice(0, 2) as string[];
  const destinationUpdatedAt = playableDestinations
    .map((entry) => entry.destinationHealth?.metrics?.updatedAt || entry.status?.lastErrorTime || entry.status?.startedAt || null)
    .filter(Boolean)[0] || null;
  const previewActive = isRunning && !!(stats as any)?.isPlayKastrSource;
  const previewShouldRun = previewActive;
  const playoutAlert = !hasConfiguredOutputs
    ? { severity: 'warning' as const, title: 'No outputs configured', detail: 'Configure push outputs in Destinations before starting a broadcast.' }
    : !previewActive && isRunning
      ? { severity: 'warning' as const, title: 'Broadcast running without preview', detail: 'Playkaster is on air, but the monitor is not yet reading the live path.' }
      : !isRunning
        ? { severity: 'info' as const, title: 'Playout standing by', detail: 'Load a playlist and start broadcast when ready.' }
        : connectedDestinations.length === 0
          ? { severity: 'warning' as const, title: 'Outputs armed but not connected', detail: 'The live path is active, but no configured output is currently reporting connected state.' }
          : { severity: 'success' as const, title: 'Broadcast healthy', detail: 'Preview and destinations are both receiving the live path.' };
  const sourceStateLabel = previewActive ? 'Playkaster owns live path' : isRunning ? 'Claim in progress' : 'Live path idle';
  const filteredAssets = useMemo(() => {
    const q = libraryQuery.trim().toLowerCase();
    return assets.filter((asset) => {
      const status = String(asset.normStatus || '').toUpperCase();
      const matchesQuery = !q || asset.label.toLowerCase().includes(q);
      const matchesFolder =
        folderFilter === 'ALL'
          ? true
          : folderFilter === 'UNFILED'
            ? !asset.folderId
            : asset.folderId === folderFilter;
      const matchesFilter =
        libraryFilter === 'ALL'
          ? true
          : libraryFilter === 'READY'
            ? status === 'READY'
            : libraryFilter === 'PROCESSING'
              ? !['READY', 'ERROR'].includes(status)
              : status === 'ERROR';
      return matchesQuery && matchesFilter && matchesFolder;
    });
    }, [assets, libraryFilter, libraryQuery, folderFilter]);
  const assetColumnCount = assetViewMode === 'icon'
    ? (workspaceView === 'library' ? (isAssetGridXl ? 3 : isAssetGridMd ? 2 : 1) : 2)
    : 1;
  const libraryPageCount = Math.max(1, Math.ceil(filteredAssets.length / LIBRARY_PAGE_SIZE));
  const clampedLibraryPage = Math.min(libraryPage, libraryPageCount);
  const libraryPageStart = (clampedLibraryPage - 1) * LIBRARY_PAGE_SIZE;
  const paginatedAssets = filteredAssets.slice(libraryPageStart, libraryPageStart + LIBRARY_PAGE_SIZE);
  const assetVirtualRowHeight = assetViewMode === 'icon' ? ASSET_ICON_ROW_HEIGHT : ASSET_LIST_ROW_HEIGHT;
  const assetVirtualRowCount = Math.ceil(paginatedAssets.length / assetColumnCount);
  const assetVirtualStartRow = Math.max(0, Math.floor(assetScrollTop / assetVirtualRowHeight) - VIRTUAL_OVERSCAN_ROWS);
  const assetVirtualEndRow = Math.min(
    assetVirtualRowCount,
    Math.ceil((assetScrollTop + 1400) / assetVirtualRowHeight) + VIRTUAL_OVERSCAN_ROWS,
  );
  const visibleAssetStartIndex = assetVirtualStartRow * assetColumnCount;
  const visibleAssetEndIndex = Math.min(paginatedAssets.length, assetVirtualEndRow * assetColumnCount);
  const visibleAssets = paginatedAssets.slice(visibleAssetStartIndex, visibleAssetEndIndex);
  const assetVirtualTop = assetVirtualStartRow * assetVirtualRowHeight;
  const assetVirtualHeight = Math.max(assetVirtualRowCount * assetVirtualRowHeight, assetVirtualRowHeight);
  const selectedAssetIdSet = useMemo(() => new Set(selectedAssetIds), [selectedAssetIds]);
  const filteredAssetIdSet = useMemo(() => new Set(filteredAssets.map((asset) => asset.id)), [filteredAssets]);
  const uploadQueueCounts = useMemo(() => uploadQueue.reduce(
    (counts, item) => {
      if (item.status === 'pending') counts.pending += 1;
      if (item.status === 'uploading') counts.uploading += 1;
      if (item.status === 'processing') counts.processing += 1;
      return counts;
    },
    { pending: 0, uploading: 0, processing: 0 },
  ), [uploadQueue]);
  useEffect(() => {
    setUploadQueue((current) => {
      const localPending = current.filter((item) => !item.jobId);
      const mappedJobs = serverUploadJobs.map(mapUploadJobToQueueItem);
      return [...localPending, ...mappedJobs];
    });
  }, [serverUploadJobs]);

  useEffect(() => {
    if (!workspaceViewKey) return;
    window.sessionStorage.removeItem(workspaceViewKey);
    setWorkspaceView('rundown');
  }, [workspaceViewKey]);

  useEffect(() => {
    if (!workspaceViewKey) return;
    window.sessionStorage.setItem(workspaceViewKey, workspaceView);
  }, [workspaceViewKey, workspaceView]);

  useEffect(() => {
    if (workspaceView === 'rundown' && assetViewMode !== 'list') {
      setAssetViewMode('list');
    }
  }, [assetViewMode, workspaceView]);

  const readyFilteredAssets = useMemo(
    () => filteredAssets.filter((asset) => String(asset.normStatus || '').toUpperCase() === 'READY'),
    [filteredAssets],
  );
  const allReadyAssets = useMemo(
    () => assets.filter((asset) => String(asset.normStatus || '').toUpperCase() === 'READY'),
    [assets],
  );
  const selectedReadyAssets = useMemo(
    () => allReadyAssets.filter((asset) => selectedAssetIdSet.has(asset.id)),
    [allReadyAssets, selectedAssetIdSet],
  );
  const allFilteredSelected = paginatedAssets.length > 0 && paginatedAssets.every((asset) => selectedAssetIdSet.has(asset.id));
  const someFilteredSelected = paginatedAssets.some((asset) => selectedAssetIdSet.has(asset.id));

  useEffect(() => {
    if (!selectedAssetId && filteredAssets[0]?.id) setSelectedAssetId(filteredAssets[0].id);
    if (selectedAssetId && filteredAssets.every((asset) => asset.id !== selectedAssetId)) {
      setSelectedAssetId(filteredAssets[0]?.id || null);
    }
  }, [filteredAssets, selectedAssetId]);

  useEffect(() => {
    setAssetScrubberPreviewOpen(false);
  }, [selectedAssetId]);

  useEffect(() => {
    setSelectedAssetIds((current) => current.filter((id) => filteredAssetIdSet.has(id)));
  }, [filteredAssetIdSet]);

  useEffect(() => {
    setAssetScrollTop(0);
  }, [assetViewMode, folderFilter, libraryFilter, libraryPage, libraryQuery, workspaceView]);

  useEffect(() => {
    setLibraryPage(1);
  }, [assetViewMode, folderFilter, libraryFilter, libraryQuery, workspaceView]);

  useEffect(() => {
    if (libraryPage > libraryPageCount) setLibraryPage(libraryPageCount);
  }, [libraryPage, libraryPageCount]);

  useEffect(() => {
    if (!selectedRundownId && playlistItems[0]?.id) setSelectedRundownId(playlistItems[0].id);
    if (selectedRundownId && playlistItems.every((item) => item.id !== selectedRundownId)) {
      setSelectedRundownId(playlistItems[0]?.id || null);
    }
    setSelectedRundownItemIds((current) => current.filter((id) => playlistItems.some((item) => item.id === id)));
  }, [playlistItems, selectedRundownId]);

  useEffect(() => {
    setClockDraft(studio?.playlist?.clock_start || '');
    setScheduleDateDraft(studio?.playlist?.start_date || new Date().toISOString().slice(0, 10));
  }, [studio?.playlist?.id, studio?.playlist?.clock_start, studio?.playlist?.start_date]);

  useEffect(() => {
    const enabled = !!String(studio?.playlist?.clock_start || '').trim();
    setScheduleEnabledDraft(enabled);
    setScheduleRecurrenceDraft(String(studio?.playlist?.recurrence || 'DAILY').toUpperCase() === 'WEEKLY' ? 'WEEKLY' : 'DAILY');
    setScheduleWeekdayDraft(Number(studio?.playlist?.weekdays || 0));
    setPlaylistActionTargetId(studio?.playlist?.id || null);
    setPlaylistRenameDraft(studio?.playlist?.name || '');
  }, [studio?.playlist?.id, studio?.playlist?.clock_start, studio?.playlist?.recurrence, studio?.playlist?.weekdays, studio?.playlist?.name]);

  useEffect(() => {
    let cancelled = false;
    const loadLogs = async () => {
      if (!activeChannel || workspaceView !== 'logs') return;
      try {
        setPlaykastrLogLoading(true);
        const data = await listPlayKastrLogs(activeChannel, playkastrLogRange, playkastrLogPage);
        if (cancelled) return;
        setPlaykastrLogs(data.items || []);
        setPlaykastrLogTotal(Number(data.total || 0));
      } catch (error: any) {
        if (cancelled) return;
        showSnackbar(error?.response?.data?.message || error?.message || 'Failed to load playout logs', 'error');
      } finally {
        if (!cancelled) setPlaykastrLogLoading(false);
      }
    };
    void loadLogs();
    return () => {
      cancelled = true;
    };
  }, [activeChannel, workspaceView, playkastrLogRange, playkastrLogPage, showSnackbar]);

  useEffect(() => {
    if (uploadQueue.length === 0) return;
    setUploadQueue((current) => current.map((item) => {
      const linkedAsset = item.assetId ? assets.find((asset) => asset.id === item.assetId) : null;
      if (!linkedAsset || item.jobId) return item;
      const normStatus = String(linkedAsset.normStatus || '').toUpperCase();
      if (normStatus === 'READY') {
        return { ...item, status: 'ready', progress: 100, error: null };
      }
      if (normStatus === 'ERROR') {
        return { ...item, status: 'error', progress: 0, error: linkedAsset.normError || 'Processing failed' };
      }
      return {
        ...item,
        status: 'processing',
        progress: Math.max(1, Math.min(99, Number(linkedAsset.normProgress || item.progress || 0))),
      };
    }));
  }, [assets, uploadQueue.length]);

  useEffect(() => {
    const pendingUpload = uploadQueue.find((item) => item.status === 'pending') || null;
    if (!activeChannel || !pendingUpload || uploading) return;
    const run = async () => {
      const file = uploadFileMapRef.current[pendingUpload.id];
      if (!file) {
        setUploadQueue((current) => current.map((item) => item.id === pendingUpload.id ? { ...item, status: 'error', error: 'Missing upload payload. Re-add the file to continue.' } : item));
        return;
      }
      try {
        const controller = new AbortController();
        uploadAbortControllersRef.current[pendingUpload.id] = controller;
        setUploading(true);
        setUploadProgress(0);
        setUploadQueue((current) => current.map((item) => item.id === pendingUpload.id ? { ...item, status: 'uploading', progress: 0, error: null } : item));
        const job = await uploadPlayKastrAsset(activeChannel, file, undefined, (pct) => {
          setUploadProgress(pct);
          setUploadQueue((current) => current.map((item) => item.id === pendingUpload.id ? { ...item, progress: pct } : item));
        }, controller.signal);
        delete uploadAbortControllersRef.current[pendingUpload.id];
        delete uploadFileMapRef.current[pendingUpload.id];
        setUploadQueue((current) => current.filter((item) => item.id !== pendingUpload.id));
        setServerUploadJobs((current) => {
          const filtered = current.filter((item) => item.id !== job.id);
          return [job, ...filtered];
        });
        await refreshAssets(activeChannel);
        await refreshWorkspace(activeChannel).catch(() => null);
        showSnackbar(`${pendingUpload.name} accepted for processing.`, 'success');
      } catch (error: any) {
        delete uploadAbortControllersRef.current[pendingUpload.id];
        const aborted = error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED';
        if (aborted) {
          setUploadQueue((current) => current.filter((item) => item.id !== pendingUpload.id));
          delete uploadFileMapRef.current[pendingUpload.id];
          return;
        }
        setUploadQueue((current) => current.map((item) => item.id === pendingUpload.id ? {
          ...item,
          status: 'error',
          error: error?.response?.data?.message || error?.message || 'Upload failed',
        } : item));
        showSnackbar(error?.response?.data?.message || error?.message || 'PlayKastr upload failed', 'error');
      } finally {
        setUploading(false);
        setUploadProgress(null);
      }
    };
    void run();
  }, [activeChannel, uploadQueue, uploading]);

  useEffect(() => {
    if (!activeChannel) return;
    const hasActiveServerJobs = serverUploadJobs.some((job) => {
      const status = String(job.status || '').toUpperCase();
      return status === 'UPLOADING' || status === 'PROCESSING';
    });
    if (!hasActiveServerJobs) return;
    const timer = window.setTimeout(() => {
      void refreshAssets(activeChannel);
    }, 2000);
    return () => window.clearTimeout(timer);
  }, [activeChannel, serverUploadJobs]);

  useEffect(() => {
    const hasFinished = uploadQueue.some((item) => item.status === 'ready' || item.status === 'error');
    if (!hasFinished) return;
    const timer = window.setTimeout(() => {
      setUploadQueue((current) => current.filter((item) => item.status !== 'ready' && item.status !== 'error'));
      setServerUploadJobs((current) => current.filter((job) => {
        const status = String(job.status || '').toUpperCase();
        return status !== 'READY' && status !== 'ERROR';
      }));
    }, 6000);
    return () => window.clearTimeout(timer);
  }, [uploadQueue]);

  const selectedAsset = filteredAssets.find((asset) => asset.id === selectedAssetId) || filteredAssets[0] || null;
  const selectedRundownItem = playlistItems.find((item) => item.id === selectedRundownId) || playlistItems[0] || null;
  const removeDialogItem = playlistItems.find((item) => item.id === removeDialogItemId) || null;
  const assetsById = useMemo(() => new Map(assets.map((asset) => [asset.id, asset])), [assets]);
  const assetsByLabel = useMemo(() => new Map(assets.map((asset) => [asset.label, asset])), [assets]);
  const currentRuntimeItemId = selectedPlaylistIsPlaying ? (snapshot?.runtime?.current_item_id ?? null) : null;
  const currentRundownId = currentRuntimeItemId ?? snapshot?.now_playing?.item_id ?? playlistItems.find((item) => item.is_current)?.id ?? null;
  const nextRundownId = snapshot?.up_next?.[0]?.item_id ?? playlistItems.find((item) => item.is_next)?.id ?? null;
  const currentRuntimePosition = snapshot?.runtime?.current_item_pos ?? null;
  const isCurrentRundownItem = (item?: PlayKastrPlaylistItem | null) => !!item && Number(item.id) === Number(currentRundownId);
  const isNextRundownItem = (item?: PlayKastrPlaylistItem | null) => !!item && Number(item.id) === Number(nextRundownId);
  const processingLabel = processingCount > 0 ? `${processingCount} processing` : 'all ready';
  const previewItemKey = `${activeChannel?.id ?? 'none'}:${activeSessionRouteId || 'none'}:${isRunning ? 'live' : 'idle'}`;
  const nextHardTimedItem = useMemo(() => playlistItems.find((item) => !!item.protected_boundary) || null, [playlistItems]);
  const currentAgeRatingOptions = assetMetadataTypeDraft === 'Movie' ? movieRatingOptions : tvRatingOptions;
  const libraryModeActive = workspaceView === 'library';
  const rundownModeActive = workspaceView === 'rundown';
  const playoutModeActive = workspaceView === 'playout';
  const epgModeActive = workspaceView === 'epg';
  const logsModeActive = workspaceView === 'logs';
  const workspaceNavItems: WorkspaceNavItem[] = [
    { key: 'library', label: 'Library', icon: <LibraryMusicIcon fontSize="small" /> },
    { key: 'rundown', label: 'Rundown', icon: <ViewTimelineIcon fontSize="small" /> },
    { key: 'playout', label: 'Overview', icon: <TvIcon fontSize="small" /> },
    { key: 'epg', label: 'EPG', icon: <ScheduleIcon fontSize="small" /> },
    { key: 'logs', label: 'Logs', icon: <ViewListIcon fontSize="small" /> },
  ];
  const headerPreviewActive = !playoutModeActive;
  const renderPlaykastrPreviewTypeControl = () => (
    <Stack direction="row" spacing={0.45} alignItems="center" sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: '0.62rem', fontWeight: 900, letterSpacing: '0.08em', textTransform: 'uppercase', color: v('--text-secondary') }}>
        Preview
      </Typography>
      <Tooltip title="WebRTC, default, faster, Opus audio. HLS, slower, higher compatibility." arrow>
        <IconButton size="small" sx={{ borderRadius: 0, width: 22, height: 22, color: v('--text-secondary') }}>
          <InfoOutlinedIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Tooltip>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(54px, 1fr))', border: '1px solid', borderColor: v('--border'), bgcolor: 'var(--bg-body)', minHeight: 28 }}>
        {([
          ['webrtc', 'WebRTC'],
          ['hls', 'HLS'],
        ] as const).map(([key, label], index) => {
          const selected = playkastrPreviewType === key;
          return (
            <Button
              key={key}
              size="small"
              onClick={() => selectPlaykastrPreviewType(key)}
              sx={{
                ...microButtonSx,
                minHeight: 26,
                minWidth: 0,
                px: 0.8,
                border: 0,
                borderLeft: index === 0 ? 0 : '1px solid',
                borderColor: v('--border'),
                bgcolor: selected ? 'rgba(var(--primary-rgb),0.14)' : 'transparent',
                color: selected ? v('--primary') : v('--text-main'),
              }}
            >
              {label}
            </Button>
          );
        })}
      </Box>
    </Stack>
  );
  const libraryPaginationControl = filteredAssets.length > LIBRARY_PAGE_SIZE ? (
    <Stack direction="row" spacing={0.7} alignItems="center" justifyContent="space-between" sx={{ mt: 0.4 }}>
      <Typography sx={{ fontSize: '0.72rem', color: v('--text-secondary'), fontWeight: 700 }}>
        {libraryPageStart + 1}-{Math.min(filteredAssets.length, libraryPageStart + LIBRARY_PAGE_SIZE)} of {filteredAssets.length}
      </Typography>
      <Stack direction="row" spacing={0.45}>
        <Button
          size="small"
          variant="outlined"
          disabled={clampedLibraryPage <= 1}
          onClick={() => setLibraryPage((page) => Math.max(1, page - 1))}
          sx={microButtonSx}
        >
          Previous
        </Button>
        <Button
          size="small"
          variant="outlined"
          disabled={clampedLibraryPage >= libraryPageCount}
          onClick={() => setLibraryPage((page) => Math.min(libraryPageCount, page + 1))}
          sx={microButtonSx}
        >
          Next
        </Button>
      </Stack>
    </Stack>
  ) : null;
  const processingRatio = assets.length > 0 ? Math.min(100, Math.round((readyCount / assets.length) * 100)) : 0;
  const rundownVirtualStartIndex = Math.max(0, Math.floor(rundownScrollTop / RUNDOWN_ROW_HEIGHT) - VIRTUAL_OVERSCAN_ROWS);
  const rundownVirtualEndIndex = Math.min(
    playlistItems.length,
    Math.ceil((rundownScrollTop + 1400) / RUNDOWN_ROW_HEIGHT) + VIRTUAL_OVERSCAN_ROWS,
  );
  const visibleRundownItems = playlistItems.slice(rundownVirtualStartIndex, rundownVirtualEndIndex);
  const rundownVirtualTop = rundownVirtualStartIndex * RUNDOWN_ROW_HEIGHT;
  const rundownVirtualHeight = Math.max(playlistItems.length * RUNDOWN_ROW_HEIGHT, RUNDOWN_ROW_HEIGHT);
  const polledRuntimeItemSecs = Number((snapshot?.runtime as any)?.current_item_secs || 0);
  const runtimeProgressBaseMs = parseIsoMillis((snapshot?.runtime as any)?.last_progress_at) ?? parseIsoMillis((snapshot?.runtime as any)?.started_at);
  const scrollToActiveRundownRow = (behavior: ScrollBehavior = 'smooth') => {
    if (!currentRundownId) return;
    const container = rundownScrollRef.current;
    if (!container) return;
    const activeIndex = playlistItems.findIndex((item) => Number(item.id) === Number(currentRundownId));
    if (activeIndex >= 0) {
      const targetTop = Math.max(0, (activeIndex * RUNDOWN_ROW_HEIGHT) - 36);
      container.scrollTo({ top: targetTop, behavior });
      setRundownScrollTop(targetTop);
      return;
    }
    const row = document.getElementById(`playkastr-rundown-item-${currentRundownId}`);
    if (!row) return;
    const containerRect = container.getBoundingClientRect();
    const rowRect = row.getBoundingClientRect();
    const padding = 36;
    if (rowRect.top < containerRect.top + padding || rowRect.bottom > containerRect.bottom - padding) {
      const targetTop = container.scrollTop + (rowRect.top - containerRect.top) - padding;
      container.scrollTo({ top: Math.max(0, targetTop), behavior });
    }
  };
  useEffect(() => {
    scrollToActiveRundownRow('smooth');
  }, [currentRundownId, currentRuntimePosition]);
  const getRundownItemAsset = (item?: PlayKastrPlaylistItem | null) =>
    (item?.asset_id ? assetsById.get(item.asset_id) : null) || (item?.asset_label ? assetsByLabel.get(item.asset_label) : null) || null;
  const frozenZoneSeconds = studio?.frozen_zone_seconds ?? 0;
  const getRundownMetaLine = (item?: PlayKastrPlaylistItem | null) => {
    const asset = getRundownItemAsset(item);
    const logicalKind = String(item?.kind || '').toUpperCase();
    const tagType = String(asset?.assetClass || (logicalKind === 'FILL' ? 'GAP' : item?.kind) || 'VIDEO').toUpperCase();
    const duration = formatDuration(item?.duration_secs ?? item?.expected_duration_secs);
    const type = item?.epg_type || asset?.epgTypeDefault || (logicalKind === 'LIVE' ? 'Live' : logicalKind === 'FILL' ? 'Gap' : '--');
    const genre = item?.epg_genre || asset?.epgGenreDefault || asset?.epgCategoryDefault || '--';
    const ageRating = item?.epg_age_rating || asset?.epgAgeRatingDefault || '--';
    const timingType = String(item?.timing_type || 'SOFT_TIMED').replace(/_/g, '-');
    const assetClassification = String(item?.asset_classification || 'PRIMARY').replace(/_/g, '-');
    const frozen = item?.is_locked ? 'LOCKED' : null;
    const timingDecision = String(item?.timing_decision || '').trim();
    const timingDecisionLabel = timingDecision && timingDecision !== 'normal'
      ? timingDecision.replace(/_/g, '-').toUpperCase()
      : null;
    const boundaryLabel = item?.boundary_time ? `BOUNDARY ${formatClockFromIso(item.boundary_time, playlistTimeZone)}` : null;
    return [tagType, duration, type, genre, ageRating, timingType, assetClassification, timingDecisionLabel, boundaryLabel, frozen].filter(Boolean).join(' / ');
  };
  useEffect(() => {
    if (assetMetadataAgeRatingDraft && !(currentAgeRatingOptions as readonly string[]).includes(assetMetadataAgeRatingDraft)) {
      setAssetMetadataAgeRatingDraft('');
    }
  }, [assetMetadataTypeDraft, currentAgeRatingOptions, assetMetadataAgeRatingDraft]);

  const handleAddSelectedAssetToRundown = async (targetAsset?: PlayKastrAsset | null) => {
    const asset = targetAsset || selectedAsset;
    const assetReady = String(asset?.normStatus || '').toUpperCase() === 'READY';
    if (!activeChannel || !studio?.playlist?.id || !asset || !assetReady) return;
    try {
      const insertPosition =
        typeof selectedRundownItem?.position === 'number'
          ? selectedRundownItem.position + 1
          : undefined;
      await appendPlayKastrPlaylistItem(activeChannel, resolvePlayKastrPlaylistRouteId(studio.playlist), asset.id, 'ASSET', insertPosition);
      await refreshWorkspace(activeChannel, studio.playlist.id);
      showSnackbar(insertPosition != null ? 'Asset inserted into rundown.' : 'Asset added to rundown.', 'success');
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || error?.message || 'Failed to add asset to rundown', 'error');
    }
  };

  const handleInsertAssetIntoRundown = async (asset: PlayKastrAsset, position: number, targetItem?: PlayKastrPlaylistItem | null) => {
    const assetReady = String(asset?.normStatus || '').toUpperCase() === 'READY';
    if (!activeChannel || !studio?.playlist?.id || !assetReady) return;
    try {
      await appendPlayKastrPlaylistItem(activeChannel, resolvePlayKastrPlaylistRouteId(studio.playlist), asset.id, 'ASSET', position);
      await refreshWorkspace(activeChannel, studio.playlist.id);
      showSnackbar('Asset inserted into rundown.', 'success');
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || error?.message || 'Failed to insert asset into rundown', 'error');
    }
  };

  const handleToggleAssetSelection = (assetId: number) => {
    setSelectedAssetIds((current) =>
      current.includes(assetId) ? current.filter((id) => id !== assetId) : [...current, assetId]
    );
  };

  const handleOpenInsertDialog = (scope: 'selected' | 'all' = selectedReadyAssets.length > 0 ? 'selected' : 'all') => {
    if (!studio?.playlist?.id) return;
    setInsertScopeDraft(scope);
    setInsertRandomDraft(true);
    setInsertDialogOpen(true);
  };

  const handleSubmitInsertDialog = async () => {
    if (!activeChannel || !studio?.playlist?.id) return;
    const sourceAssets = insertScopeDraft === 'selected' ? selectedReadyAssets : allReadyAssets;
    if (sourceAssets.length === 0) {
      showSnackbar(insertScopeDraft === 'selected' ? 'Select one or more ready assets to insert.' : 'No ready assets are available.', 'error');
      return;
    }
    const assetsToInsert = insertRandomDraft
      ? [...sourceAssets].sort(() => Math.random() - 0.5)
      : [...sourceAssets];
    try {
      setInsertSaving(true);
      for (let index = 0; index < assetsToInsert.length; index += 1) {
        const asset = assetsToInsert[index];
        await appendPlayKastrPlaylistItem(activeChannel, resolvePlayKastrPlaylistRouteId(studio.playlist), asset.id, 'ASSET');
      }
      await refreshWorkspace(activeChannel, studio.playlist.id);
      if (insertScopeDraft === 'selected') setSelectedAssetIds([]);
      setInsertDialogOpen(false);
      showSnackbar(`${assetsToInsert.length} asset${assetsToInsert.length === 1 ? '' : 's'} inserted into rundown.`, 'success');
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || error?.message || 'Failed to insert assets', 'error');
    } finally {
      setInsertSaving(false);
    }
  };

  const handleLoadSelectedAssets = () => {
    handleOpenInsertDialog('selected');
  };

  const handleLoadCurrentFolder = async () => {
    if (!activeChannel || !studio?.playlist?.id || typeof folderFilter !== 'number') return;
    const readyAssets = filteredAssets.filter((asset) => String(asset.normStatus || '').toUpperCase() === 'READY');
    if (readyAssets.length === 0) {
      showSnackbar('No ready assets in this folder.', 'error');
      return;
    }
    try {
      for (const asset of readyAssets) {
        await appendPlayKastrPlaylistItem(activeChannel, resolvePlayKastrPlaylistRouteId(studio.playlist), asset.id, 'ASSET');
      }
      await refreshWorkspace(activeChannel, studio.playlist.id);
      showSnackbar(`${readyAssets.length} asset${readyAssets.length === 1 ? '' : 's'} loaded from folder.`, 'success');
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || error?.message || 'Failed to load folder', 'error');
    }
  };

  const handleReplaceWithCurrentFolder = async () => {
    if (!activeChannel || !studio?.playlist?.id || typeof folderFilter !== 'number') return;
    const readyAssets = filteredAssets.filter((asset) => String(asset.normStatus || '').toUpperCase() === 'READY');
    if (readyAssets.length === 0) {
      showSnackbar('No ready assets in this folder.', 'error');
      return;
    }
    if (!window.confirm(`Replace the current rundown with ${readyAssets.length} asset${readyAssets.length === 1 ? '' : 's'} from this folder?`)) {
      return;
    }
    try {
      await clearPlayKastrPlaylist(activeChannel, resolvePlayKastrPlaylistRouteId(studio.playlist));
      for (const asset of readyAssets) {
        await appendPlayKastrPlaylistItem(activeChannel, resolvePlayKastrPlaylistRouteId(studio.playlist), asset.id, 'ASSET');
      }
      await refreshWorkspace(activeChannel, studio.playlist.id);
      showSnackbar('Rundown replaced from folder.', 'success');
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || error?.message || 'Failed to replace rundown from folder', 'error');
    }
  };

  const choosePlaylistId = (title: string, defaultPlaylistId?: number | null) => {
    const playlists = studio?.playlists || [];
    if (playlists.length === 0) {
      showSnackbar('No playlists available.', 'error');
      return null;
    }
    const defaultPlaylist = playlists.find((playlist) => Number(playlist.id) === Number(defaultPlaylistId || studio?.playlist?.id)) || playlists[0];
    const promptLabel = `${title}\n${playlists.map((playlist, index) => `${index + 1}. ${playlist.name}`).join('\n')}\n\nEnter playlist number or exact name:`;
    const input = window.prompt(promptLabel, defaultPlaylist?.name || '')?.trim();
    if (!input) return null;
    const numeric = Number(input);
    if (Number.isFinite(numeric) && numeric >= 1 && numeric <= playlists.length) {
      return playlists[numeric - 1].id;
    }
    const matched = playlists.find((playlist) => playlist.name.toLowerCase() === input.toLowerCase());
    if (matched) return matched.id;
    showSnackbar('Playlist not found.', 'error');
    return null;
  };

  const handleLoadFolderToChosenPlaylist = async (replace = false) => {
    if (!activeChannel || typeof folderFilter !== 'number') return;
    const targetPlaylistId = choosePlaylistId(replace ? 'Replace which playlist from this folder?' : 'Append this folder to which playlist?');
    if (!targetPlaylistId) return;
    const readyAssets = filteredAssets.filter((asset) => String(asset.normStatus || '').toUpperCase() === 'READY');
    if (readyAssets.length === 0) {
      showSnackbar('No ready assets in this folder.', 'error');
      return;
    }
    try {
      if (replace) {
        await clearPlayKastrPlaylist(activeChannel, targetPlaylistId);
      }
      for (const asset of readyAssets) {
        await appendPlayKastrPlaylistItem(activeChannel, targetPlaylistId, asset.id, 'ASSET');
      }
      await refreshWorkspace(activeChannel, targetPlaylistId);
      showSnackbar(replace ? 'Playlist replaced from folder.' : 'Folder appended to playlist.', 'success');
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || error?.message || 'Failed to load folder into playlist', 'error');
    }
  };

  const handleDragStartRundown = (itemId: number) => {
    setDraggedRundownItemId(itemId);
  };

  const handleArmRundownMove = (itemId: number) => {
    setDraggedRundownItemId((current) => (current === itemId ? null : itemId));
  };

  const handleStartFromAsset = async (targetItem?: PlayKastrPlaylistItem | null) => {
    const itemToStart = targetItem || selectedRundownItem;
    if (!activeChannel || !studio?.playlist?.id || !itemToStart) return;
    try {
      await startPlayKastrPlaylistFromItem(activeChannel, resolvePlayKastrPlaylistRouteId(studio.playlist), resolvePlayKastrItemRouteId(itemToStart));
      await refreshWorkspace(activeChannel, studio.playlist.id);
      setSelectedRundownId(itemToStart.id);
      showSnackbar('Playlist reordered to start from this asset.', 'success');
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || error?.message || 'Failed to start from this asset', 'error');
    } finally {
      setDraggedRundownItemId(null);
    }
  };

  const handleDropRundown = async (targetItemId: number) => {
    if (draggedAssetId) {
      const draggedAsset = assets.find((asset) => asset.id === draggedAssetId) || null;
      const targetIndex = playlistItems.findIndex((item) => item.id === targetItemId);
      const targetItem = targetIndex >= 0 ? playlistItems[targetIndex] : null;
      setDraggedAssetId(null);
      if (!draggedAsset || targetIndex < 0) return;
      await handleInsertAssetIntoRundown(draggedAsset, targetIndex, targetItem);
      return;
    }
    if (!activeChannel || !studio?.playlist?.id || !draggedRundownItemId || draggedRundownItemId === targetItemId) {
      setDraggedRundownItemId(null);
      return;
    }
    const reordered = [...playlistItems];
    const fromIndex = reordered.findIndex((item) => item.id === draggedRundownItemId);
    const toIndex = reordered.findIndex((item) => item.id === targetItemId);
    if (fromIndex < 0 || toIndex < 0) {
      setDraggedRundownItemId(null);
      return;
    }
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);
    try {
      await replacePlayKastrPlaylistItems(
        activeChannel,
        studio.playlist.id,
        reordered.map((item, index) => ({
          id: item.id,
          position: index,
          kind: item.kind,
          assetId: item.asset_id ?? null,
          sourceUrl: item.source_url ?? null,
          sourceLabel: item.source_label ?? null,
          expectedDurationSecs: item.expected_duration_secs ?? null,
          epgType: item.epg_type ?? null,
          epgAgeRating: item.epg_age_rating ?? null,
          epgGenre: item.epg_genre ?? null,
          trimInSecs: item.trim_in_secs ?? null,
          trimOutSecs: item.trim_out_secs ?? null,
          scheduledStart: item.scheduled_start ?? null,
          timingType: item.timing_type ?? null,
          onOverrun: item.on_overrun ?? 'cut',
          transition: item.transition ?? 'cut',
        }))
      );
      await refreshWorkspace(activeChannel, studio.playlist.id);
      showSnackbar('Rundown reordered.', 'success');
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || error?.message || 'Failed to reorder rundown', 'error');
    } finally {
      setDraggedRundownItemId(null);
    }
  };

  const handleMoveSelectedUp = async (targetItem?: PlayKastrPlaylistItem | null) => {
    const itemToMove = targetItem || selectedRundownItem;
    if (!activeChannel || !studio?.playlist?.id || !itemToMove) return;
    const index = playlistItems.findIndex((item) => item.id === itemToMove.id);
    if (index <= 0) return;
    try {
      await movePlayKastrPlaylistItem(activeChannel, resolvePlayKastrPlaylistRouteId(studio.playlist), resolvePlayKastrItemRouteId(itemToMove), 'up');
      await refreshWorkspace(activeChannel, studio.playlist.id);
      showSnackbar('Rundown item moved up.', 'success');
      setSelectedRundownId(itemToMove.id);
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || error?.message || 'Failed to move rundown item', 'error');
    }
  };

  const handleMoveSelectedDown = async (targetItem?: PlayKastrPlaylistItem | null) => {
    const itemToMove = targetItem || selectedRundownItem;
    if (!activeChannel || !studio?.playlist?.id || !itemToMove) return;
    const index = playlistItems.findIndex((item) => item.id === itemToMove.id);
    if (index < 0 || index >= playlistItems.length - 1) return;
    try {
      await movePlayKastrPlaylistItem(activeChannel, resolvePlayKastrPlaylistRouteId(studio.playlist), resolvePlayKastrItemRouteId(itemToMove), 'down');
      await refreshWorkspace(activeChannel, studio.playlist.id);
      showSnackbar('Rundown item moved down.', 'success');
      setSelectedRundownId(itemToMove.id);
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || error?.message || 'Failed to move rundown item', 'error');
    }
  };

  const handleRemoveSelectedItem = async (targetItem?: PlayKastrPlaylistItem | null) => {
    const itemToRemove = targetItem || selectedRundownItem;
    if (!activeChannel || !studio?.playlist?.id || !itemToRemove) return;
    try {
      const filtered = playlistItems.filter((item) => item.id !== itemToRemove.id);
      await removePlayKastrPlaylistItem(activeChannel, resolvePlayKastrPlaylistRouteId(studio.playlist), resolvePlayKastrItemRouteId(itemToRemove));
      await refreshWorkspace(activeChannel, studio.playlist.id);
      showSnackbar('Rundown item removed.', 'success');
      setSelectedRundownId(filtered[0]?.id || null);
      setRemoveDialogItemId(null);
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || error?.message || 'Failed to remove rundown item', 'error');
    }
  };

  const selectableRundownItemIds = playlistItems.filter((item) => !!item.can_remove && !item.is_locked).map((item) => item.id);
  const selectableRundownItemIdSet = new Set(selectableRundownItemIds);
  const selectedRundownItemIdSet = new Set(selectedRundownItemIds);
  const selectedRundownDeleteIds = selectedRundownItemIds.filter((id) => selectableRundownItemIdSet.has(id));
  const allRundownRowsSelected = selectableRundownItemIds.length > 0 && selectableRundownItemIds.every((id) => selectedRundownItemIdSet.has(id));
  const someRundownRowsSelected = selectedRundownDeleteIds.length > 0;

  const handleToggleRundownItemSelection = (item: PlayKastrPlaylistItem) => {
    if (!item.can_remove || item.is_locked) return;
    setSelectedRundownItemIds((current) =>
      current.includes(item.id)
        ? current.filter((id) => id !== item.id)
        : [...current, item.id],
    );
  };

  const handleToggleSelectAllRundownItems = () => {
    setSelectedRundownItemIds(allRundownRowsSelected ? [] : selectableRundownItemIds);
  };

  const handleDeleteSelectedRundownItems = async () => {
    if (!activeChannel || !studio?.playlist?.id || selectedRundownDeleteIds.length === 0) return;
    if (!window.confirm(`Delete ${selectedRundownDeleteIds.length} selected rundown item${selectedRundownDeleteIds.length === 1 ? '' : 's'}?`)) return;
    const selectedItems = playlistItems.filter((item) => selectedRundownDeleteIds.includes(item.id));
    try {
      await removePlayKastrPlaylistItems(
        activeChannel,
        resolvePlayKastrPlaylistRouteId(studio.playlist),
        selectedItems.map((item) => resolvePlayKastrItemRouteId(item)),
      );
      setSelectedRundownItemIds([]);
      setSelectedRundownId(null);
      await refreshWorkspace(activeChannel, studio.playlist.id);
      showSnackbar('Selected rundown items deleted.', 'success');
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || error?.message || 'Failed to delete selected rundown items', 'error');
    }
  };

  const handleCopyEpgUrl = async (url: string, label: string) => {
    try {
      await navigator.clipboard.writeText(url);
      showSnackbar(`${label} copied.`, 'success');
    } catch {
      showSnackbar(`Failed to copy ${label.toLowerCase()}.`, 'error');
    }
  };

  const handleCopyText = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      showSnackbar(`${label} copied.`, 'success');
    } catch {
      showSnackbar(`Failed to copy ${label.toLowerCase()}.`, 'error');
    }
  };

  const credentialFieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 0,
      bgcolor: 'var(--bg-body)',
    },
  };

  const credentialCopyAdornment = (value: string, label: string) => (
    <InputAdornment position="end">
      <Tooltip title={`Copy ${label}`}>
        <span>
          <IconButton onClick={() => void handleCopyText(value, label)} edge="end" disabled={!credentialStreamKeyPin}>
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </InputAdornment>
  );

  const handleAssetOptionsAction = async (
    action: 'add' | 'metadata' | 'info' | 'folder' | 'rename' | 'delete',
    event?: React.MouseEvent<HTMLElement>
  ) => {
    const asset = assets.find((entry) => entry.id === assetOptionsAssetId) || null;
    if (!asset) {
      closeAssetOptionsMenu();
      return;
    }
    closeAssetOptionsMenu();
    switch (action) {
      case 'add':
        await handleAddSelectedAssetToRundown(asset);
        break;
      case 'metadata':
        openAssetMetadataDialog(asset);
        break;
      case 'info':
        await openMediaInfoDialog(asset);
        break;
      case 'folder':
        if (event?.currentTarget) openFolderPicker(event.currentTarget, asset);
        break;
      case 'rename':
        await handleRenameAsset(asset);
        break;
      case 'delete':
        await handleDeleteAsset(asset);
        break;
      default:
        break;
    }
  };

  const renderCollapsedAssetActions = (asset: PlayKastrAsset, iconSize = 28) => {
    const canAddToRundown = String(asset.normStatus || '').toUpperCase() === 'READY' && !!studio?.playlist?.id;
    return (
      <Stack direction="row" spacing={0.45} flexWrap="wrap" useFlexGap>
        <Tooltip title="Add to rundown">
          <span>
            <IconButton
              size="small"
              disabled={!canAddToRundown}
              onClick={(event) => {
                event.stopPropagation();
                void handleAddSelectedAssetToRundown(asset);
              }}
              sx={!canAddToRundown
                ? { ...actionIconSx, ...disabledActionSx, width: iconSize, height: iconSize }
                : { ...actionIconSx, width: iconSize, height: iconSize, color: v('--primary'), borderColor: v('--primary') }}
            >
              <PlaylistAddOutlinedIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={`Change type (${String(asset.assetClass || 'VIDEO')})`}>
          <span>
            <IconButton
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                openAssetTypePicker(event.currentTarget, asset);
              }}
              sx={{ ...actionIconSx, width: iconSize, height: iconSize, color: v('--secondary'), borderColor: v('--secondary') }}
            >
              <LocalOfferOutlinedIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Asset options">
          <span>
            <IconButton
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                openAssetOptionsMenu(event.currentTarget, asset);
              }}
              sx={{ ...actionIconSx, width: iconSize, height: iconSize }}
            >
              <MoreHorizIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    );
  };

  const renderAssetCard = (asset: PlayKastrAsset, checked: boolean, selected: boolean) => (
    <Box
      key={asset.id}
      onClick={() => setSelectedAssetId(asset.id)}
      draggable={String(asset.normStatus || '').toUpperCase() === 'READY' && !!studio?.playlist?.id}
      onDragStart={(event) => {
        event.stopPropagation();
        event.dataTransfer.effectAllowed = 'copyMove';
        setDraggedAssetId(asset.id);
      }}
      onDragEnd={() => setDraggedAssetId(null)}
      sx={{
        px: 1.2,
        py: 0.92,
        border: '1px solid',
        borderColor: v('--border'),
        bgcolor: selected ? 'rgba(var(--primary-rgb),0.08)' : 'transparent',
        boxShadow: selected ? `inset 0 0 0 1px rgba(var(--primary-rgb),0.10)` : 'none',
        cursor: String(asset.normStatus || '').toUpperCase() === 'READY' && !!studio?.playlist?.id ? 'grab' : 'pointer',
        minHeight: assetViewMode === 'icon' ? 166 : 'auto',
      }}
    >
      <Stack spacing={assetViewMode === 'icon' ? 0.85 : 0.45}>
        {assetViewMode === 'icon' && (
          <AuthenticatedAssetImage
            channel={activeChannel}
            assetId={asset.id}
            alt={asset.label}
            sx={{
              width: '100%',
              height: 88,
              objectFit: 'cover',
              bgcolor: 'var(--bg-card-hover)',
              border: '1px solid',
              borderColor: v('--border'),
            }}
          />
        )}
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <Checkbox size="small" checked={checked} onClick={(event) => event.stopPropagation()} onChange={() => handleToggleAssetSelection(asset.id)} sx={{ p: 0.2, mt: 0.1 }} />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 900, color: v('--text-main') }} noWrap>{asset.label}</Typography>
            <Typography sx={{ mt: 0.2, fontSize: '0.69rem', color: v('--text-secondary') }} noWrap={assetViewMode !== 'icon'}>
              H264 / {formatDuration(asset.durationSecs)} / {asset.folderName || 'Unfiled'} / {String(asset.assetClass || 'VIDEO')}
            </Typography>
          </Box>
          <Chip label={String(asset.normStatus || '').toUpperCase()} size="small" sx={{ ...studioPillSx, bgcolor: String(asset.normStatus || '').toUpperCase() === 'READY' ? 'rgba(var(--success-rgb),0.12)' : 'var(--bg-body)', color: String(asset.normStatus || '').toUpperCase() === 'READY' ? v('--success') : v('--text-secondary') }} />
        </Stack>
        {renderCollapsedAssetActions(asset)}
      </Stack>
    </Box>
  );

  const handleDownloadPlaykastrLogsCsv = async () => {
    if (!activeChannel) return;
    try {
      const blob = await downloadPlayKastrLogsCsv(activeChannel, playkastrLogRange);
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `${String(activeChannel?.name || 'playkaster').toLowerCase()}-playout-logs.csv`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
      showSnackbar('Playout log CSV downloaded.', 'success');
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || error?.message || 'Failed to download playout logs', 'error');
    }
  };

  const handleClearPlaykastrLogs = () => {
    setPlaykastrLogs([]);
    setPlaykastrLogTotal(0);
    showSnackbar('Playout logs cleared from view.', 'success');
  };

  if (playkastrInactive) {
    return (
      <DashboardTab
        stats={stats || null}
        analytics={analytics || null}
        isStreamLive={isStreamLive}
        user={user}
        activeChannel={activeChannel}
      />
    );
  }

  if (loading && !workspace && assets.length === 0) {
    return (
      <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: 'calc(100dvh - 112px)',
        maxHeight: 'calc(100dvh - 112px)',
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
    <Stack spacing={1} sx={{ height: '100%', minHeight: 0, overflow: 'hidden' }}>
      <input
        ref={fileInputRef}
        type="file"
        hidden
        multiple
        accept=".mp4,.mov,.mkv,video/mp4,video/quicktime,video/x-matroska"
        onChange={handleFileSelected}
      />

      {false && activeChannel && (
      <Paper elevation={0} sx={{ ...cardSx, p: 0, overflow: 'hidden' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 312px' },
            minHeight: { xs: 'auto', lg: 224 },
          }}
        >
          <Box sx={{ p: { xs: 1.1, md: 1.35 } }}>
            <Stack spacing={0.95}>
              <Stack
                direction={{ xs: 'column', lg: 'row' }}
                spacing={1.2}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', lg: 'center' }}
              >
                <Stack spacing={0.7} sx={{ minWidth: 0 }}>
                  <Stack direction="row" spacing={0.8} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography sx={{ ...railTitleSx, color: v('--secondary') }}>PlayKastr</Typography>
                    <Chip label={nowState} size="small" sx={{ ...studioPillSx, bgcolor: isRunning ? 'rgba(var(--primary-rgb),0.14)' : 'var(--bg-body)', color: isRunning ? v('--primary') : v('--text-secondary') }} />
                    <Chip label={currentSource} size="small" sx={{ ...studioPillSx, bgcolor: 'rgba(var(--secondary-rgb),0.14)', color: v('--secondary') }} />
                    <Chip label={destinationMonitorLabel} size="small" sx={{ ...studioPillSx, bgcolor: outputStatusTone.bg, color: outputStatusTone.color }} />
                  </Stack>
                  <Typography sx={{ fontSize: { xs: '1.04rem', md: '1.34rem' }, fontWeight: 900, color: v('--text-main'), letterSpacing: '0.01em' }}>
                    {playlistName}
                  </Typography>
                  <Typography sx={{ fontSize: '0.68rem', color: v('--text-secondary'), letterSpacing: '0.03em' }}>
                    {activeChannel?.name || 'Channel'} / Broadcast automation, ingest control, playlist timing, and playout.
                  </Typography>
                  <Box
                    sx={{
                      ...cardSx,
                      px: 0.7,
                      py: 0.4,
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', lg: 'auto 1fr auto' },
                      gap: 0.6,
                      alignItems: 'center',
                      bgcolor: 'var(--bg-card-hover)',
                      maxWidth: '100%',
                    }}
                  >
                    <Stack direction="row" spacing={0.55} flexWrap="wrap" useFlexGap alignItems="center">
                      <Chip label="Library" clickable onClick={() => setWorkspaceView('library')} size="small" sx={{ ...studioPillSx, bgcolor: libraryModeActive ? 'rgba(var(--secondary-rgb),0.12)' : 'var(--bg-body)', color: libraryModeActive ? v('--secondary') : v('--text-secondary'), border: '1px solid', borderColor: libraryModeActive ? v('--secondary') : v('--border') }} />
                      <Chip label="Rundown" clickable onClick={() => setWorkspaceView('rundown')} size="small" sx={{ ...studioPillSx, bgcolor: workspaceView === 'rundown' ? 'rgba(var(--primary-rgb),0.12)' : 'var(--bg-body)', color: workspaceView === 'rundown' ? v('--primary') : v('--text-secondary'), border: '1px solid', borderColor: workspaceView === 'rundown' ? v('--primary') : v('--border') }} />
                      <Chip label="Overview" clickable onClick={() => setWorkspaceView('playout')} size="small" sx={{ ...studioPillSx, bgcolor: playoutModeActive ? 'rgba(var(--accent-rgb),0.12)' : 'var(--bg-body)', color: playoutModeActive ? v('--accent') : v('--text-secondary'), border: '1px solid', borderColor: playoutModeActive ? v('--accent') : v('--border') }} />
                      <Chip label="EPG" clickable onClick={() => setWorkspaceView('epg')} size="small" sx={{ ...studioPillSx, bgcolor: epgModeActive ? 'rgba(var(--success-rgb),0.12)' : 'var(--bg-body)', color: epgModeActive ? v('--success') : v('--text-secondary'), border: '1px solid', borderColor: epgModeActive ? v('--success') : v('--border') }} />
                      <Chip label="Playout Logs" clickable onClick={() => setWorkspaceView('logs')} size="small" sx={{ ...studioPillSx, bgcolor: logsModeActive ? 'rgba(var(--warning-rgb),0.12)' : 'var(--bg-body)', color: logsModeActive ? v('--warning') : v('--text-secondary'), border: '1px solid', borderColor: logsModeActive ? v('--warning') : v('--border') }} />
                    </Stack>
                    <Typography sx={{ fontSize: '0.64rem', color: v('--text-secondary'), letterSpacing: '0.04em' }} noWrap>
                      {libraryModeActive
                        ? `${assets.length} assets / ${readyCount} ready / ${folders.length} folders`
                        : epgModeActive
                          ? `${todayScheduledPlaylists.length} scheduled / ${itemCount} programs / ${playlistWindowStart} to ${playlistWindowEnd}`
                          : logsModeActive
                            ? `${playkastrLogTotal} log entries / ${playkastrLogRangeOptions.find((entry) => entry.value === playkastrLogRange)?.label || '24 hours'}`
                          : playoutModeActive
                            ? `${destinationMonitorLabel} / ${currentItem} / ${nextItem}`
                            : `${playlistCount} playlists / ${itemCount} items / ${readyCount} ready`}
                    </Typography>
                    <Stack direction="row" spacing={0.6} justifyContent={{ xs: 'flex-start', lg: 'flex-end' }} flexWrap="wrap" useFlexGap>
                      <Chip label={isRunning ? 'On Air' : 'Standby'} size="small" sx={{ ...studioPillSx, bgcolor: isRunning ? 'rgba(var(--primary-rgb),0.12)' : 'var(--bg-body)', color: isRunning ? v('--primary') : v('--text-secondary') }} />
                      <Chip label={destinationMonitorLabel} size="small" sx={{ ...studioPillSx, bgcolor: outputStatusTone.bg, color: outputStatusTone.color }} />
                    </Stack>
                  </Box>
                </Stack>

              </Stack>

            </Stack>
          </Box>

          <Box sx={{ p: { xs: 1, md: 1.1 }, bgcolor: 'var(--bg-body)' }}>
            <Stack spacing={1}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography sx={{ ...railTitleSx, color: v('--text-secondary') }}>
                  {playoutModeActive ? 'Overview' : 'Program Preview'}
                </Typography>
                <Chip label={previewActive ? 'Live Feed' : 'Waiting'} size="small" sx={{ ...studioPillSx, bgcolor: previewActive ? 'rgba(var(--secondary-rgb),0.14)' : 'var(--bg-card)', color: previewActive ? v('--secondary') : v('--text-secondary') }} />
              </Stack>
              {headerPreviewActive ? (
                <Box sx={{ ...previewPanelSx, minHeight: { xs: 220, md: 250, lg: 100 }, height: { xs: 220, md: 250, lg: '100%' } }}>
                  <MemoizedVideoPlayer
                    key={`playkastr-header-preview-${previewItemKey}`}
                    channelId={activeChannel?.id || 0}
                    channelHashId={activeChannel?.channelHashId || activeChannel?.hashId || null}
                    streamName={activeChannel?.name || ''}
                    streamKeyPin={activeChannel?.streamKeyPin || ''}
                    isLive={previewActive}
                    previewType={playkastrPreviewType}
                    autoPlay
                  />
                </Box>
              ) : (
                <Box sx={{ ...previewPanelSx, minHeight: { xs: 220, md: 250, lg: 100 }, display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1, p: 1.1 }}>
                  <DetailField label="Source" value={sourceStateLabel} />
                  <DetailField label="Preview" value={previewActive ? 'Connected' : 'Waiting'} />
                  <DetailField label="Session" value={isRunning ? 'Playing' : 'Waiting'} />
                  <DetailField label="Outputs" value={destinationMonitorLabel} />
                </Box>
              )}
            </Stack>
          </Box>
        </Box>
      </Paper>
      )}

      <Box
        sx={{
          ...operatorPanelSx,
          px: 1,
          py: 0.75,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '190px 88px minmax(260px, 1fr) 92px 92px 150px auto' },
          gap: 0,
          alignItems: 'center',
          borderColor: 'var(--border-strong)',
        }}
      >
        <Stack direction="row" spacing={0.9} alignItems="center" sx={{ minWidth: 0, pr: 1.2, borderRight: '1px solid', borderColor: v('--border') }}>
          <Box sx={{ width: 28, height: 28, borderRadius: '4px', bgcolor: v('--primary'), color: v('--on-primary'), display: 'grid', placeItems: 'center', fontWeight: 900, fontSize: '0.66rem' }}>
            PK
          </Box>
          <Typography sx={{ fontSize: '1rem', fontWeight: 900, color: v('--text-main') }} noWrap>
            PlayKaster
          </Typography>
        </Stack>
        <Stack spacing={0.2} sx={{ px: 1.1, borderRight: '1px solid', borderColor: v('--border'), minWidth: 0 }}>
          <Typography sx={railTitleSx}>Channel</Typography>
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 900, color: v('--text-main') }} noWrap>{activeChannel?.name || 'Channel'}</Typography>
        </Stack>
        <Stack spacing={0.2} sx={{ px: 1.1, borderRight: '1px solid', borderColor: v('--border'), minWidth: 0 }}>
          <Typography sx={railTitleSx}>Playlist</Typography>
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 900, color: v('--text-main') }} noWrap>{playlistName}</Typography>
        </Stack>
        <Stack spacing={0.2} sx={{ px: 1.1, borderRight: '1px solid', borderColor: v('--border'), minWidth: 0 }}>
          <Typography sx={railTitleSx}>Time</Typography>
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 900, color: v('--text-main') }} noWrap>{rundownDateLabel}</Typography>
        </Stack>
        <Stack spacing={0.2} sx={{ px: 1.1, borderRight: '1px solid', borderColor: v('--border'), minWidth: 0 }}>
          <Typography sx={railTitleSx}>State</Typography>
          <Chip label={nowState} size="small" sx={{ ...studioPillSx, width: 'fit-content', bgcolor: isRunning ? 'rgba(var(--success-rgb),0.12)' : 'var(--bg-body)', color: isRunning ? v('--success') : v('--text-secondary'), border: '1px solid', borderColor: isRunning ? v('--success') : v('--border') }} />
        </Stack>
        <Stack spacing={0.2} sx={{ px: 1.1, borderRight: '1px solid', borderColor: v('--border'), minWidth: 0 }}>
          <Typography sx={railTitleSx}>Output Alarms</Typography>
          <Stack direction="row" spacing={0.9}>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 900, color: v('--warning') }}>{playableDestinations.length - connectedDestinations.length}</Typography>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 900, color: v('--danger') }}>{connectedDestinations.length === 0 && hasConfiguredOutputs ? 1 : 0}</Typography>
          </Stack>
        </Stack>
        <Stack direction="row" spacing={0.6} justifyContent="flex-end" flexWrap="wrap" useFlexGap sx={{ pl: 1 }}>
          <Tooltip title="SRT/RTMP credentials">
            <IconButton
              onClick={() => setCredentialsDialogOpen(true)}
              sx={{ ...actionIconSx, color: v('--secondary'), borderColor: v('--secondary') }}
            >
              <VpnKeyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Chip label="Alerts Armed" size="small" sx={{ ...studioPillSx, bgcolor: 'rgba(var(--success-rgb),0.12)', color: v('--success'), border: '1px solid', borderColor: v('--success') }} />
        </Stack>
      </Box>

      <Box
        sx={{
          ...operatorPanelSx,
          p: 0.45,
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(5, minmax(0, 1fr))', md: 'repeat(5, max-content)' },
          gap: 0.45,
          alignItems: 'center',
          overflowX: 'auto',
        }}
      >
        {workspaceNavItems.map((item) => {
          const active = workspaceView === item.key;
          return (
            <Button
              key={item.key}
              onClick={() => setWorkspaceView(item.key)}
              startIcon={item.icon}
              variant={active ? 'contained' : 'outlined'}
              sx={{
                ...mockupButtonSx,
                minWidth: { xs: 0, md: 104 },
                justifyContent: 'center',
                bgcolor: active ? v('--primary') : v('--bg-body'),
                color: active ? v('--on-primary') : v('--text-secondary'),
                borderColor: active ? v('--primary') : v('--border'),
                '&:hover': {
                  bgcolor: active ? v('--primary') : 'var(--bg-card-hover)',
                  borderColor: active ? v('--primary') : v('--text-secondary'),
                },
                '& .MuiButton-startIcon': {
                  mr: { xs: 0, sm: 0.5 },
                },
              }}
            >
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                {item.label}
              </Box>
            </Button>
          );
        })}
      </Box>

      {!hasConfiguredOutputs && (
        <Alert severity="warning" sx={{ borderRadius: 0 }}>
          Configure at least one push destination in Destinations. PlayKastr publishes into the channel live path and your existing outputs will consume it.
        </Alert>
      )}

      {libraryModeActive && (
        <Box
          sx={{
            ...cardSx,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(260px, 24%) minmax(0, 76%)' },
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ bgcolor: 'var(--bg-card-hover)', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ px: 1, pt: 1, pb: 0.4 }}>
              <Box sx={{ ...cardSx, bgcolor: 'var(--bg-body)', p: 1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.8 }}>
                  <Stack direction="row" spacing={0.8} alignItems="center">
                    <PreviewIcon sx={{ fontSize: 16, color: v('--secondary') }} />
                    <Typography sx={railTitleSx}>Media Scrubber</Typography>
                  </Stack>
                  <Tooltip title={assetScrubberPreviewOpen ? 'Close asset preview' : 'Preview selected asset'}>
                    <span>
                      <IconButton
                        size="small"
                        disabled={!selectedAsset}
                        onClick={() => setAssetScrubberPreviewOpen((open) => !open)}
                        sx={!selectedAsset ? { ...actionIconSx, ...disabledActionSx, width: 28, height: 28 } : { ...actionIconSx, width: 28, height: 28, color: v('--secondary'), borderColor: v('--secondary') }}
                      >
                        <PreviewIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
                <Box sx={{ ...previewPanelSx, minHeight: 188, overflow: 'hidden' }}>
                  {selectedAsset && assetScrubberPreviewOpen ? (
                    <AuthenticatedAssetVideo channel={activeChannel} assetId={selectedAsset.id} height={188} />
                  ) : selectedAsset ? (
                    <Stack spacing={0.75} sx={{ height: 188, p: 0.8, justifyContent: 'center' }}>
                      <AuthenticatedAssetImage
                        channel={activeChannel}
                        assetId={selectedAsset.id}
                        alt={selectedAsset.label}
                        sx={{
                          width: '100%',
                          height: 124,
                          objectFit: 'cover',
                          bgcolor: 'var(--bg-card-hover)',
                          border: '1px solid',
                          borderColor: v('--border'),
                        }}
                      />
                      <Typography sx={{ fontSize: '0.74rem', color: v('--text-secondary') }} noWrap>
                        {selectedAsset.label}
                      </Typography>
                    </Stack>
                  ) : (
                    <Box sx={{ height: 188, display: 'flex', alignItems: 'center', justifyContent: 'center', px: 1.2 }}>
                      <Typography sx={{ fontSize: '0.76rem', color: v('--text-secondary') }}>
                        Select an asset to preview it before adding it to the rundown.
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
            <Box sx={{ px: 1.2, py: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <FolderOpenOutlinedIcon sx={{ color: v('--accent'), fontSize: 18 }} />
                <Typography sx={railTitleSx}>Library Folders</Typography>
              </Stack>
            </Box>
            <Stack spacing={0.6} sx={{ p: 1, overflowY: 'auto', minHeight: 0 }}>
              <Chip label="All Content" clickable onClick={() => setFolderFilter('ALL')} size="small" sx={{ borderRadius: 0, justifyContent: 'flex-start', bgcolor: folderFilter === 'ALL' ? 'rgba(var(--accent-rgb),0.12)' : 'background.default', color: folderFilter === 'ALL' ? v('--accent') : v('--text-secondary'), border: '1px solid', borderColor: folderFilter === 'ALL' ? v('--accent') : v('--border') }} />
              <Chip label="Unfiled" clickable onClick={() => setFolderFilter('UNFILED')} size="small" sx={{ borderRadius: 0, justifyContent: 'flex-start', bgcolor: folderFilter === 'UNFILED' ? 'rgba(var(--accent-rgb),0.12)' : 'background.default', color: folderFilter === 'UNFILED' ? v('--accent') : v('--text-secondary'), border: '1px solid', borderColor: folderFilter === 'UNFILED' ? v('--accent') : v('--border') }} />
              {folders.map((folder) => {
                const selected = folderFilter === folder.id;
                return (
                  <Chip
                    key={folder.id}
                    label={folder.name}
                    clickable
                    onClick={() => setFolderFilter(folder.id)}
                    size="small"
                    sx={{ borderRadius: 0, justifyContent: 'flex-start', bgcolor: selected ? 'rgba(var(--accent-rgb),0.12)' : 'background.default', color: selected ? v('--accent') : v('--text-secondary'), border: '1px solid', borderColor: selected ? v('--accent') : v('--border') }}
                  />
                );
              })}
            </Stack>
          </Box>

          <Box
            sx={{ minHeight: 0, display: 'flex', flexDirection: 'column', bgcolor: 'var(--bg-body)' }}
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => {
              event.preventDefault();
              enqueueUploadFiles(event.dataTransfer?.files ? Array.from(event.dataTransfer.files) : []);
            }}
          >
            <Box sx={{ px: 1.35, py: 1.1 }}>
              <Stack spacing={1}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
                  <Box>
                    <Typography sx={{ ...railTitleSx, color: v('--text-secondary') }}>Library Workspace</Typography>
                    <Typography sx={{ mt: 0.35, fontSize: '0.85rem', color: v('--text-main'), fontWeight: 900 }}>
                      Browse folders, upload content, and prepare assets before loading them into the rundown.
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={0.55} flexWrap="wrap" useFlexGap>
                    <Tooltip title="Upload">
                      <span>
                        <IconButton onClick={handleUploadClick} sx={{ ...prominentActionIconSx, bgcolor: v('--secondary') }}>
                          <CloudUploadIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="New Folder">
                      <span>
                        <IconButton onClick={handleCreateFolder} sx={{ ...actionIconSx, color: v('--accent'), borderColor: v('--accent') }}>
                          <CreateNewFolderOutlinedIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Technical Report">
                      <span>
                        <IconButton onClick={() => void handleDownloadTechnicalReport()} sx={{ ...actionIconSx, color: v('--accent'), borderColor: v('--accent') }}>
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Load Selected">
                      <span>
                        <IconButton
                          onClick={handleLoadSelectedAssets}
                          disabled={!studio?.playlist?.id || selectedReadyAssets.length === 0}
                          sx={!studio?.playlist?.id || selectedReadyAssets.length === 0 ? { ...actionIconSx, ...disabledActionSx } : { ...actionIconSx, color: v('--primary'), borderColor: v('--primary') }}
                        >
                          <PlaylistAddOutlinedIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Insert all ready assets">
                      <span>
                        <IconButton
                          onClick={() => handleOpenInsertDialog('all')}
                          disabled={!studio?.playlist?.id || allReadyAssets.length === 0}
                          sx={!studio?.playlist?.id || allReadyAssets.length === 0 ? { ...actionIconSx, ...disabledActionSx } : { ...actionIconSx, color: v('--secondary'), borderColor: v('--secondary') }}
                        >
                          <LibraryMusicIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Stack direction="row" spacing={0.35}>
                      <Tooltip title="List view">
                        <IconButton size="small" onClick={() => setAssetViewMode('list')} sx={assetViewMode === 'list' ? { ...actionIconSx, color: v('--secondary'), borderColor: v('--secondary') } : actionIconSx}>
                          <ViewListIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Icon view">
                        <IconButton size="small" onClick={() => setAssetViewMode('icon')} sx={assetViewMode === 'icon' ? { ...actionIconSx, color: v('--secondary'), borderColor: v('--secondary') } : actionIconSx}>
                          <GridViewIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>
                </Stack>
                <TextField
                  size="small"
                  placeholder="Search files..."
                  value={libraryQuery}
                  onChange={(event) => setLibraryQuery(event.target.value)}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 0,
                      bgcolor: 'background.default',
                    },
                  }}
                />
                <Stack direction="row" spacing={0.7} flexWrap="wrap" useFlexGap alignItems="center">
                  {[
                    { key: 'ALL', label: 'All' },
                    { key: 'READY', label: 'Ready' },
                    { key: 'PROCESSING', label: 'Processing' },
                    { key: 'ERROR', label: 'Error' },
                  ].map((filter) => {
                    const selected = libraryFilter === filter.key;
                    return (
                      <Chip
                        key={filter.key}
                        label={filter.label}
                        clickable
                        onClick={() => setLibraryFilter(filter.key as typeof libraryFilter)}
                        size="small"
                        sx={{ borderRadius: 0, height: 22, fontWeight: 800, bgcolor: selected ? 'rgba(var(--primary-rgb),0.12)' : 'background.default', color: selected ? v('--primary') : v('--text-secondary'), border: '1px solid', borderColor: selected ? v('--primary') : v('--border') }}
                      />
                    );
                  })}
                  <Stack direction="row" spacing={0.8} alignItems="center" sx={{ ml: 'auto' }}>
                    <Checkbox checked={allFilteredSelected} indeterminate={!allFilteredSelected && someFilteredSelected} onChange={handleToggleSelectAllAssets} size="small" sx={{ p: 0.4 }} />
                    <Typography sx={{ fontSize: '0.72rem', color: v('--text-secondary'), fontWeight: 700 }}>
                      Select Page
                    </Typography>
                  </Stack>
                </Stack>
                {libraryPaginationControl}
                {uploadQueue.length > 0 && (
                  <Box sx={{ ...cardSx, bgcolor: 'var(--bg-body)', p: 1 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.8 }}>
                      <Typography sx={railTitleSx}>Upload Queue</Typography>
                      <Typography sx={{ fontSize: '0.68rem', color: v('--text-secondary') }}>
                        Auto-clears when done
                      </Typography>
                    </Stack>
                    <Stack spacing={0.7} sx={{ maxHeight: 176, overflowY: 'auto' }}>
                      {uploadQueue.map((item) => (
                        <Box key={item.id} sx={{ ...cardSx, p: 0.85, bgcolor: 'var(--bg-card-hover)' }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Typography sx={{ fontSize: '0.76rem', fontWeight: 800, color: v('--text-main') }} noWrap>
                                {item.name}
                              </Typography>
                              <Typography sx={{ mt: 0.2, fontSize: '0.68rem', color: v('--text-secondary') }} noWrap>
                                {Math.max(1, Math.round(item.size / (1024 * 1024)))} MB / {item.status}{item.status === 'processing' ? ` / ${Math.max(1, Math.round(item.progress || 0))}%` : ''}
                              </Typography>
                            </Box>
                            {item.status === 'error' && !item.jobId ? (
                              <Button size="small" variant="outlined" onClick={() => handleRetryUpload(item.id)} sx={microButtonSx}>
                                Retry
                              </Button>
                            ) : (
                              <Stack direction="row" spacing={0.5} alignItems="center">
                                <Chip label={item.status} size="small" sx={{ ...studioPillSx, bgcolor: item.status === 'ready' ? 'rgba(var(--success-rgb),0.12)' : 'rgba(var(--secondary-rgb),0.12)', color: item.status === 'ready' ? v('--success') : v('--secondary') }} />
                                {item.status !== 'ready' && (
                                  <Tooltip title="Cancel upload">
                                    <IconButton size="small" onClick={() => void handleCancelUpload(item)} sx={{ ...actionIconSx, width: 26, height: 26, color: 'var(--danger)', borderColor: 'var(--danger)' }}>
                                      <StopCircleIcon sx={{ fontSize: 14 }} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Stack>
                            )}
                          </Stack>
                          <LinearProgress variant="determinate" value={item.status === 'ready' ? 100 : Math.max(3, item.progress || 0)} sx={{ mt: 0.65, borderRadius: 0, height: 6 }} />
                          {item.error && (
                            <Typography sx={{ mt: 0.4, fontSize: '0.66rem', color: 'var(--danger)' }}>
                              {item.error}
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                )}
              </Stack>
            </Box>
            <Box
              sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}
              onScroll={(event) => setAssetScrollTop(event.currentTarget.scrollTop)}
            >
              {filteredAssets.length > 0 ? (
                <Box
                  sx={{
                    height: assetVirtualHeight,
                    position: 'relative',
                    p: 0.75,
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: assetVirtualTop,
                      left: 6,
                      right: 6,
                      display: 'grid',
                      gridTemplateColumns: assetViewMode === 'icon'
                        ? { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(3, minmax(0, 1fr))' }
                        : '1fr',
                      gap: 0.75,
                    }}
                  >
                    {visibleAssets.map((asset) => renderAssetCard(
                      asset,
                      selectedAssetIdSet.has(asset.id),
                      asset.id === selectedAsset?.id,
                    ))}
                  </Box>
                </Box>
              ) : (
                <Box sx={{ p: 2 }}>
                  <Typography sx={{ fontSize: '0.8rem', color: v('--text-secondary') }}>
                    No assets match this folder or filter.
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      )}

      {playoutModeActive && (
        <Box
          sx={{
            ...cardSx,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 62%) minmax(320px, 38%)' },
            gap: 0,
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ p: 1.25, bgcolor: 'var(--bg-card-hover)', minHeight: 0 }}>
            <Stack spacing={1.1} sx={{ height: '100%' }}>
              <Alert severity={playoutAlert.severity} sx={{ borderRadius: 0, py: 0.35, '& .MuiAlert-message': { width: '100%' } }}>
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 900 }}>
                  {playoutAlert.title}
                </Typography>
                <Typography sx={{ fontSize: '0.72rem' }}>
                  {playoutAlert.detail}
                </Typography>
              </Alert>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Stack direction="row" spacing={1} alignItems="center">
                  <TvIcon sx={{ color: v('--primary'), fontSize: 18 }} />
                  <Typography sx={railTitleSx}>Channel Monitor</Typography>
                </Stack>
                <Chip label={previewActive ? 'Live Feed' : 'Standby'} size="small" sx={{ ...studioPillSx, bgcolor: previewActive ? 'rgba(var(--secondary-rgb),0.14)' : 'var(--bg-body)', color: previewActive ? v('--secondary') : v('--text-secondary') }} />
              </Stack>
              <Box sx={{ ...previewPanelSx, minHeight: { xs: 240, md: 340 }, flex: 1 }}>
                <MemoizedVideoPlayer
                  key={`playkastr-playout-preview-${previewItemKey}`}
                  channelId={activeChannel.id}
                  channelHashId={activeChannel.channelHashId || activeChannel.hashId || null}
                  streamName={activeChannel.name}
                  streamKeyPin={activeChannel.streamKeyPin}
                  isLive={previewActive}
                  previewType={playkastrPreviewType}
                  autoPlay
                />
              </Box>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(5, minmax(0, 1fr))' },
                  gap: 1,
                }}
              >
                <StudioStat label="Source" value={sourceStateLabel} helper={previewActive ? 'preview locked' : 'waiting on live path'} />
                <StudioStat label="Current Item" value={currentItem} helper={nowState} />
                <StudioStat label="Next Item" value={nextItem} helper="queued next" />
                <StudioStat label="On-Air Playlist" value={playingPlaylistName} helper={selectedPlaylistIsPlaying ? `${itemCount} loaded items` : `Loaded: ${playlistName}`} />
                <StudioStat label="Live Edits" value={liveSaveStateLabel} helper={liveSaveFeedback} />
                <StudioStat label="Outputs" value={destinationMonitorLabel} helper={hasConfiguredOutputs ? `${connectedDestinations.length} connected` : 'configure in Destinations'} />
              </Box>
              <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
                <Button onClick={() => void handleAction('start')} startIcon={<PlaylistPlayIcon />} variant="contained" disabled={!studio || !!busyAction || isRunning || !hasConfiguredOutputs} sx={{ ...microButtonSx }}>
                  Start Broadcast/Stream
                </Button>
                <Button onClick={() => void handleAction('reload')} startIcon={<SyncIcon />} variant="outlined" disabled={!studio || !!busyAction || !isRunning} sx={{ ...microButtonSx }}>
                  Reload Session
                </Button>
                <Button onClick={() => void handleAction('save')} startIcon={<SaveOutlinedIcon />} variant="outlined" disabled={!studio || !!busyAction || !isRunning || !pendingLiveChanges} title="Apply pending rundown edits to the active playout at the next safe boundary." sx={{ ...microButtonSx }}>
                  Save Live Edits
                </Button>
                <Button onClick={() => void handleAction('generate')} startIcon={<PlaylistAddOutlinedIcon />} variant="outlined" disabled={!studio || !!busyAction || playlistItems.length === 0} title="Generate the playout and EPG snapshot from the saved rundown." sx={{ ...microButtonSx }}>
                  Generate Snapshot
                </Button>
                <Button onClick={() => void handleAction('continue')} startIcon={<PlayArrowIcon />} variant="outlined" disabled={!studio || !!busyAction || isRunning || !hasConfiguredOutputs || playlistItems.length === 0} sx={{ ...microButtonSx }}>
                  Continue Playlist
                </Button>
                <Button onClick={() => void handleAction('skip')} startIcon={<SkipNextIcon />} variant="outlined" disabled={!studio || !!busyAction || !isRunning} sx={{ ...microButtonSx }}>
                  Skip Next
                </Button>
                <Button onClick={() => void handleAction('stop')} startIcon={<StopCircleIcon />} variant="outlined" disabled={!studio || !!busyAction || !isRunning} color="error" sx={{ ...microButtonSx }}>
                  Stop Session
                </Button>
              </Stack>
            </Stack>
          </Box>

          <Box sx={{ p: 1.1, bgcolor: 'var(--bg-body)', minHeight: 0 }}>
            <Stack spacing={1}>
              <SheetSection title="Program Sheet">
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                    gap: 0.7,
                  }}
                >
                  <DetailField label="On-Air Playlist" value={playingPlaylistName} />
                  <DetailField label="Loaded Playlist" value={playlistName} />
                  <DetailField label="Clock Start" value={studio?.playlist?.clock_start || 'Immediate'} />
                  <DetailField label="Mode" value={playlistModeLabel} />
                  <DetailField label="Broadcast Route" value="Shared channel live path" />
                </Box>
              </SheetSection>

              <SheetSection title="Destination Monitor">
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                    gap: 0.7,
                  }}
                >
                  <DetailField label="State" value={destinationMonitorLabel} />
                  <DetailField label="Traffic" value={`${destinationTrafficKbps > 0 ? Math.round(destinationTrafficKbps) : 0} kbps`} />
                  <DetailField label="Remote IP" value={destinationIps.join(', ') || '--'} />
                  <DetailField label="Updated" value={destinationUpdatedAt ? new Date(destinationUpdatedAt).toLocaleTimeString() : '--'} />
                </Box>
                {!hasConfiguredOutputs && (
                  <Typography sx={{ mt: 0.8, fontSize: '0.74rem', color: v('--text-secondary') }}>
                    Configure push outputs in Destinations. Playkaster uses the same live path your channel already delivers from.
                  </Typography>
                )}
              </SheetSection>

              <SheetSection title="Source Monitor">
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                    gap: 0.7,
                  }}
                >
                  <DetailField label="Source Mode" value={currentSource} />
                  <DetailField label="Preview" value={previewActive ? 'Connected' : 'Waiting'} />
                  <DetailField label="Session" value={isRunning ? 'Playing' : 'Standby'} />
                  <DetailField label="Clock" value={studio?.playlist?.clock_start || 'Immediate'} />
                </Box>
              </SheetSection>

              <SheetSection title="Schedule">
                <Stack spacing={0.7}>
                  <DetailField label="Loaded Playlist" value={playlistName} />
                  <DetailField label="Next Playlist" value={`${nextScheduleName} / ${nextScheduleLabel}`} />
                  <DetailField label="Countdown" value={nextScheduleCountdown} />
                  <DetailField label="Today" value={todayScheduledPlaylists.length > 0 ? `${todayScheduledPlaylists.length} clocked playlist${todayScheduledPlaylists.length === 1 ? '' : 's'}` : 'No clocked playlists'} />
                  <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap>
                    <Chip
                      label={scheduleEnabled ? 'Scheduled' : 'Schedule Off'}
                      clickable
                      onClick={() => void handleToggleScheduleEnabled()}
                      size="small"
                      sx={{
                        ...studioPillSx,
                        bgcolor: scheduleEnabled ? 'rgba(var(--success-rgb),0.12)' : 'var(--bg-body)',
                        color: scheduleEnabled ? v('--success') : v('--text-secondary'),
                        border: '1px solid',
                        borderColor: scheduleEnabled ? v('--success') : v('--border'),
                      }}
                    />
                    <TextField
                      select
                      size="small"
                      disabled={!scheduleEnabled || clockSaving}
                      value={scheduleRecurrence}
                      onChange={(event) => void handleChangeScheduleRecurrence(event.target.value as 'DAILY' | 'WEEKLY')}
                      sx={{
                        minWidth: 120,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 0,
                          bgcolor: 'var(--bg-body)',
                          fontSize: '0.72rem',
                          fontWeight: 800,
                        },
                      }}
                    >
                      <MenuItem value="DAILY" sx={{ fontSize: '0.74rem', fontWeight: 700 }}>6-hour block</MenuItem>
                    </TextField>
                  </Stack>
                  <Box sx={{ display: 'flex', gap: 0.55, flexWrap: 'wrap' }}>
                    {todayScheduledPlaylists.length > 0 ? todayScheduledPlaylists.slice(0, 4).map((playlist) => (
                      <Chip
                        key={playlist.id}
                        label={`${playlist.clock_start} ${playlist.name}`}
                        size="small"
                        sx={{ ...studioPillSx, bgcolor: 'var(--bg-body)', color: v('--text-secondary'), border: '1px solid', borderColor: v('--border') }}
                      />
                    )) : (
                      <Typography sx={{ fontSize: '0.74rem', color: v('--text-secondary') }}>
                        Add a clock start to any playlist and it will appear here.
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </SheetSection>

              <SheetSection title="EPG">
                <Stack spacing={0.6}>
                  <DetailField label="Program Window" value={`${playlistWindowStart} to ${playlistWindowEnd}`} />
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 0.7,
                    }}
                  >
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: v('--text-secondary'), letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                      Copy EPG URL
                    </Typography>
                    <Stack direction="row" spacing={0.6}>
                      <Tooltip title="Copy XMLTV EPG URL">
                        <IconButton size="small" onClick={() => void handleCopyEpgUrl(epgXmltvUrl, 'XMLTV EPG URL')} sx={actionIconSx}>
                          <ContentCopyIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Copy JSON EPG URL">
                        <IconButton size="small" onClick={() => void handleCopyEpgUrl(epgJsonUrl, 'JSON EPG URL')} sx={actionIconSx}>
                          <ContentCopyIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Box>
                  <DetailField label="Feed" value={playlistName} />
                  <Typography sx={{ fontSize: '0.74rem', color: v('--text-secondary') }}>
                    XMLTV and Gracenote export will build from this timed rundown window.
                  </Typography>
                </Stack>
              </SheetSection>
            </Stack>
          </Box>
        </Box>
      )}

      {epgModeActive && (
        <Box
          sx={{
            ...cardSx,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 52%) minmax(0, 48%)' },
            gap: 0,
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ p: 1.2, bgcolor: 'var(--bg-card-hover)', minHeight: 0 }}>
            <Stack spacing={1}>
              <SheetSection title="EPG Summary">
                <Stack spacing={0.7}>
                  <DetailField label="Feed" value={playlistName} />
                  <DetailField label="Program Window" value={`${playlistWindowStart} to ${playlistWindowEnd}`} />
                  <DetailField label="Loaded Playlist" value={playlistName} />
                  <DetailField label="Next Playlist" value={`${nextScheduleName} / ${nextScheduleLabel}`} />
                </Stack>
              </SheetSection>
              <SheetSection title="XMLTV Export">
                <Stack spacing={0.8}>
                  <Typography sx={{ fontSize: '0.74rem', color: v('--text-secondary') }}>
                    Download the XMLTV feed generated from the timed rundown.
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={() => void handleDownloadXmltv()}
                    disabled={playlistItems.length === 0}
                    sx={{ ...microButtonSx, alignSelf: 'flex-start' }}
                  >
                    Download XMLTV
                  </Button>
                </Stack>
              </SheetSection>
            </Stack>
          </Box>

          <Box sx={{ p: 1.2, bgcolor: 'background.default', minHeight: 0, overflow: 'auto' }}>
            <Stack spacing={0.9}>
              <SheetSection title="Timed Programs">
                <Stack spacing={0.8}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 0.7 }}>
                    <DetailField label="Total Items" value={String(playlistItems.length)} />
                    <DetailField label="Timed Items" value={String(timedEpgItems.length)} />
                    <DetailField label="Untimed Items" value={String(Math.max(0, playlistItems.length - timedEpgItems.length))} />
                  </Box>
                  {timedEpgItems[0] ? (
                    <Box sx={{ ...cardSx, p: 1, bgcolor: 'var(--bg-body)' }}>
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 900, color: v('--text-main') }} noWrap>
                        {timedEpgItems[0].epg_title || timedEpgItems[0].asset_label || timedEpgItems[0].kind}
                      </Typography>
                      <Typography sx={{ mt: 0.25, fontSize: '0.7rem', color: v('--text-secondary') }} noWrap>
                        First timed: {getPlaylistItemInLabel(timedEpgItems[0], playlistTimeZone)} to {getPlaylistItemOutLabel(timedEpgItems[0], playlistTimeZone)} / {formatDuration(timedEpgItems[0].duration_secs)}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography sx={{ fontSize: '0.74rem', color: v('--text-secondary') }}>
                      No timed programs yet. Build a rundown and set a clock before exporting EPG.
                    </Typography>
                  )}
                </Stack>
              </SheetSection>
              <SheetSection title="Gracenote Export">
                <Stack spacing={0.8}>
                  <Typography sx={{ fontSize: '0.74rem', color: v('--text-secondary') }}>
                    Download the Gracenote export generated from the timed rundown.
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={() => void handleDownloadGracenote()}
                    disabled={playlistItems.length === 0}
                    sx={{ ...microButtonSx, alignSelf: 'flex-start' }}
                  >
                    Download Gracenote
                  </Button>
                </Stack>
              </SheetSection>
              <SheetSection title="Scheduled Playlists">
                <Stack spacing={0.55}>
                  {scheduledPlaylists.length > 0 ? scheduledPlaylists.map((playlist) => (
                    <Box key={playlist.id} sx={{ ...cardSx, p: 1, bgcolor: 'var(--bg-body)' }}>
                      <Typography sx={{ fontSize: '0.8rem', fontWeight: 900, color: v('--text-main') }}>{playlist.name}</Typography>
                      <Typography sx={{ mt: 0.2, fontSize: '0.68rem', color: v('--text-secondary') }}>
                        {playlist.clock_start} / {String(playlist.recurrence || 'DAILY').toUpperCase()} / {formatCountdown(playlist.countdownMs)}
                      </Typography>
                    </Box>
                  )) : (
                    <Typography sx={{ fontSize: '0.74rem', color: v('--text-secondary') }}>
                      No scheduled playlists configured yet.
                    </Typography>
                  )}
                </Stack>
              </SheetSection>
            </Stack>
          </Box>
        </Box>
      )}

      {logsModeActive && (
        <Box
          sx={{
            ...cardSx,
            display: 'grid',
            gridTemplateColumns: '1fr',
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ p: 1.2, bgcolor: 'var(--bg-card-hover)', minHeight: 0, overflow: 'auto' }}>
            <Stack spacing={1}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
                <Stack spacing={0.45}>
                  <Typography sx={{ ...railTitleSx, color: v('--warning') }}>Playout Logs</Typography>
                  <Typography sx={{ fontSize: '0.76rem', color: v('--text-secondary') }}>
                    Every rundown change, EPG edit, asset action, and playout lifecycle event for this channel.
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={0.55}>
                  <Tooltip title="Clear Logs">
                    <IconButton size="small" onClick={handleClearPlaykastrLogs} sx={actionIconSx}>
                      <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Download CSV">
                    <IconButton size="small" onClick={() => void handleDownloadPlaykastrLogsCsv()} sx={actionIconSx}>
                      <DownloadIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Stack>

              <Stack direction="row" spacing={0.55} flexWrap="wrap" useFlexGap>
                {playkastrLogRangeOptions.map((option) => {
                  const selected = playkastrLogRange === option.value;
                  return (
                    <Chip
                      key={option.value}
                      label={option.label}
                      clickable
                      onClick={() => {
                        setPlaykastrLogRange(option.value);
                        setPlaykastrLogPage(1);
                      }}
                      size="small"
                      sx={{
                        ...studioPillSx,
                        bgcolor: selected ? 'rgba(var(--warning-rgb),0.12)' : 'var(--bg-body)',
                        color: selected ? v('--warning') : v('--text-secondary'),
                        border: '1px solid',
                        borderColor: selected ? v('--warning') : v('--border'),
                      }}
                    />
                  );
                })}
              </Stack>

              <SheetSection title="Event Stream">
                <Stack spacing={0.65}>
                  {playkastrLogLoading ? (
                    <Box sx={{ py: 5, display: 'flex', justifyContent: 'center' }}>
                      <CircularProgress size={28} />
                    </Box>
                  ) : playkastrLogs.length > 0 ? playkastrLogs.map((entry) => {
                    const details =
                      entry.details && typeof entry.details === 'object'
                        ? Object.entries(entry.details)
                            .filter(([, value]) => value !== null && value !== undefined && value !== '')
                            .map(([key, value]) => `${key}: ${String(value)}`)
                            .join(' / ')
                        : String(entry.details || '');
                    return (
                      <Box key={entry.id} sx={{ ...cardSx, p: 1, bgcolor: 'var(--bg-body)' }}>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
                          <Typography sx={{ fontSize: '0.78rem', fontWeight: 900, color: v('--text-main') }}>
                            {formatPlaykastrLogAction(entry.action)}
                          </Typography>
                          <Typography sx={{ fontSize: '0.68rem', color: v('--text-secondary') }}>
                            {formatDateTimeDDMMYY(entry.createdAt)}
                          </Typography>
                        </Stack>
                        <Typography sx={{ mt: 0.32, fontSize: '0.72rem', color: v('--text-secondary') }}>
                          {details || 'No additional details recorded.'}
                        </Typography>
                      </Box>
                    );
                  }) : (
                    <Box sx={{ ...cardSx, p: 2, bgcolor: 'var(--bg-body)' }}>
                      <Typography sx={{ fontSize: '0.8rem', color: v('--text-secondary') }}>
                        No Playkaster events found for this time range.
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </SheetSection>

              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography sx={{ fontSize: '0.72rem', color: v('--text-secondary') }}>
                  Page {playkastrLogPage} of {playkastrLogPageCount} / {playkastrLogTotal} entries
                </Typography>
                <Stack direction="row" spacing={0.7}>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={playkastrLogPage <= 1}
                    onClick={() => setPlaykastrLogPage((current) => Math.max(1, current - 1))}
                    sx={microButtonSx}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={playkastrLogPage >= playkastrLogPageCount}
                    onClick={() => setPlaykastrLogPage((current) => Math.min(playkastrLogPageCount, current + 1))}
                    sx={microButtonSx}
                  >
                    Next
                  </Button>
                </Stack>
              </Stack>
            </Stack>
          </Box>
        </Box>
      )}

        <Box
          sx={{
            display: libraryModeActive || playoutModeActive || epgModeActive || logsModeActive ? 'none' : 'grid',
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(280px, 20%) minmax(0, 58%) minmax(300px, 22%)' },
            gap: 1,
            alignItems: 'stretch',
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
          }}
        >
        <Paper
          elevation={0}
          onDragOver={(event) => event.preventDefault()}
          onDrop={(event) => {
            event.preventDefault();
            enqueueUploadFiles(event.dataTransfer?.files ? Array.from(event.dataTransfer.files) : []);
          }}
          sx={{ ...operatorPanelSx, height: '100%', order: { xs: 2, lg: 1 }, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}
        >
          <Box sx={operatorZoneHeaderSx}>
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
              <Box sx={{ ...operatorZoneNumberSx, borderColor: v('--secondary'), color: v('--secondary') }}>01</Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={operatorZoneTitleSx}>Media Library</Typography>
                <Typography sx={{ fontSize: '0.66rem', color: v('--text-secondary') }} noWrap>
                  {filteredAssets.length} assets / {readyCount} ready
                </Typography>
              </Box>
            </Stack>
            <Chip label="List" size="small" sx={{ ...studioPillSx, bgcolor: 'rgba(var(--secondary-rgb),0.1)', color: v('--secondary') }} />
          </Box>
          <Stack spacing={0.8} sx={{ flex: 1, minHeight: 0, overflow: 'hidden', p: 1 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" spacing={1} alignItems="center">
                <LibraryMusicIcon sx={{ color: v('--secondary'), fontSize: 18 }} />
                <Typography sx={railTitleSx}>Assets</Typography>
              </Stack>
              <Stack direction="row" spacing={0.45} alignItems="center">
                <Chip
                  label={`${filteredAssets.length} items`}
                  size="small"
                  sx={{ borderRadius: 0, fontWeight: 800, bgcolor: 'var(--bg-body)' }}
                />
                <Tooltip title="Refresh assets">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => activeChannel && void refreshAssets(activeChannel)}
                      disabled={!activeChannel}
                      sx={!activeChannel ? { ...actionIconSx, ...disabledActionSx } : actionIconSx}
                    >
                      <SyncIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={uploading ? 'Upload more assets' : 'Upload asset'}>
                  <IconButton size="small" onClick={handleUploadClick} sx={{ ...actionIconSx, color: v('--on-secondary'), bgcolor: v('--secondary'), borderColor: v('--secondary') }}>
                    <CloudUploadIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>

            <TextField
              size="small"
              placeholder="Search media..."
              value={libraryQuery}
              onChange={(event) => setLibraryQuery(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 16, color: v('--text-secondary') }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '4px',
                  bgcolor: 'var(--bg-body)',
                  fontSize: '0.76rem',
                },
              }}
            />

              <Stack direction="row" spacing={0.7} flexWrap="wrap" useFlexGap>
                {[
                  { key: 'ALL', label: 'All' },
                  { key: 'READY', label: 'Ready' },
                  { key: 'PROCESSING', label: 'Processing' },
                  { key: 'ERROR', label: 'Error' },
                ].map((filter) => {
                const selected = libraryFilter === filter.key;
                return (
                  <Chip
                    key={filter.key}
                    label={filter.label}
                    clickable
                    onClick={() => setLibraryFilter(filter.key as typeof libraryFilter)}
                    size="small"
                    sx={{
                      borderRadius: 0,
                      height: 22,
                      fontWeight: 800,
                      bgcolor: selected ? 'rgba(var(--primary-rgb),0.12)' : 'background.default',
                      color: selected ? v('--primary') : v('--text-secondary'),
                      border: '1px solid',
                      borderColor: selected ? v('--primary') : v('--border'),
                    }}
                  />
                  );
                })}
              </Stack>
              <Stack direction="row" spacing={0.8} alignItems="center" sx={{ mt: 0.6 }}>
                <Checkbox
                  checked={allFilteredSelected}
                  indeterminate={!allFilteredSelected && someFilteredSelected}
                  onChange={handleToggleSelectAllAssets}
                  size="small"
                  sx={{ p: 0.4 }}
                />
                <Typography sx={{ fontSize: '0.72rem', color: v('--text-secondary'), fontWeight: 700 }}>
                  Select Page
                </Typography>
              </Stack>
              {libraryPaginationControl}

            {selectedAssetIds.length > 0 && (
              <Stack direction="row" spacing={0.55} flexWrap="wrap" useFlexGap alignItems="center" sx={{ mt: 0.15 }}>
                <Chip
                  label={`${selectedAssetIds.length} selected`}
                  size="small"
                  sx={{ borderRadius: 0, height: 22, fontWeight: 800, bgcolor: 'rgba(var(--secondary-rgb),0.12)', color: v('--secondary') }}
                />
                <Tooltip title="Load selected assets">
                  <span>
                    <IconButton
                      size="small"
                      disabled={selectedReadyAssets.length === 0 || !studio?.playlist?.id}
                      onClick={handleLoadSelectedAssets}
                      sx={selectedReadyAssets.length === 0 || !studio?.playlist?.id ? { ...actionIconSx, ...disabledActionSx } : { ...actionIconSx, color: v('--primary'), borderColor: v('--primary') }}
                    >
                      <PlaylistAddOutlinedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Move selected to folder">
                  <span>
                    <IconButton
                      size="small"
                      disabled={selectedAssetIds.length === 0}
                      onClick={(event) => openBulkFolderPicker(event.currentTarget)}
                      sx={selectedAssetIds.length === 0 ? { ...actionIconSx, ...disabledActionSx } : actionIconSx}
                    >
                      <FolderOpenOutlinedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Delete selected assets">
                  <span>
                    <IconButton
                      size="small"
                      disabled={selectedAssetIds.length === 0}
                      onClick={() => void handleDeleteSelectedAssets()}
                      sx={selectedAssetIds.length === 0 ? { ...actionIconSx, ...disabledActionSx } : { ...actionIconSx, color: 'var(--danger)', borderColor: 'var(--danger)' }}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Clear selection">
                  <span>
                    <IconButton size="small" onClick={clearSelectedAssets} sx={actionIconSx}>
                      <MoreHorizIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            )}

            {(uploadQueue.length > 0 || processingCount > 0) && (
              <Box sx={{ ...cardSx, bgcolor: 'var(--bg-body)', p: 1 }}>
                <Stack spacing={0.75}>
                  {uploadQueue.length > 0 && (
                    <>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography sx={railTitleSx}>Upload Queue</Typography>
                        <Typography sx={{ fontSize: '0.68rem', color: v('--text-secondary') }}>
                          Auto-clears when done
                        </Typography>
                      </Stack>
                      <Typography sx={{ color: v('--text-secondary'), fontSize: '0.72rem' }}>
                        {uploadQueueCounts.pending} pending / {uploadQueueCounts.uploading} uploading / {uploadQueueCounts.processing} processing
                      </Typography>
                    </>
                  )}
                  {processingCount > 0 && (
                    <>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography sx={railTitleSx}>Normalisation</Typography>
                        <Typography sx={{ fontSize: '0.68rem', color: v('--text-secondary') }}>
                          {readyCount} of {assets.length || 0} ready
                        </Typography>
                      </Stack>
                      <LinearProgress variant="determinate" value={processingRatio} sx={{ borderRadius: 0, height: 8 }} />
                    </>
                  )}
                </Stack>
              </Box>
            )}

            <Stack direction="row" spacing={0.7} flexWrap="wrap" useFlexGap alignItems="center">
              <Chip
                label="All folders"
                clickable
                onClick={() => setFolderFilter('ALL')}
                size="small"
                sx={{
                  borderRadius: 0,
                  height: 22,
                  fontWeight: 800,
                  bgcolor: folderFilter === 'ALL' ? 'rgba(var(--accent-rgb),0.12)' : 'background.default',
                  color: folderFilter === 'ALL' ? v('--accent') : v('--text-secondary'),
                  border: '1px solid',
                  borderColor: folderFilter === 'ALL' ? v('--accent') : v('--border'),
                }}
              />
              <Chip
                label="Unfiled"
                clickable
                onClick={() => setFolderFilter('UNFILED')}
                size="small"
                sx={{
                  borderRadius: 0,
                  height: 22,
                  fontWeight: 800,
                  bgcolor: folderFilter === 'UNFILED' ? 'rgba(var(--accent-rgb),0.12)' : 'background.default',
                  color: folderFilter === 'UNFILED' ? v('--accent') : v('--text-secondary'),
                  border: '1px solid',
                  borderColor: folderFilter === 'UNFILED' ? v('--accent') : v('--border'),
                }}
              />
              {folders.map((folder) => {
                const selected = folderFilter === folder.id;
                return (
                  <Chip
                    key={folder.id}
                    label={folder.name}
                    clickable
                    onClick={() => setFolderFilter(folder.id)}
                    size="small"
                    sx={{
                      borderRadius: 0,
                      height: 22,
                      fontWeight: 800,
                      bgcolor: selected ? 'rgba(var(--accent-rgb),0.12)' : 'background.default',
                      color: selected ? v('--accent') : v('--text-secondary'),
                      border: '1px solid',
                      borderColor: selected ? v('--accent') : v('--border'),
                    }}
                  />
                );
              })}
            </Stack>

            <Box
              sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pr: 0.15 }}
              onScroll={(event) => setAssetScrollTop(event.currentTarget.scrollTop)}
            >
              {filteredAssets.length > 0 ? (
                <Box
                  sx={{
                    height: assetVirtualHeight,
                    position: 'relative',
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: assetVirtualTop,
                      left: 0,
                      right: 0,
                      display: 'grid',
                      gridTemplateColumns: '1fr',
                      gap: 0.75,
                    }}
                  >
                  {visibleAssets.map((asset) => {
                const tone = normaliseStatusTone(asset.normStatus);
                const selected = asset.id === selectedAsset?.id;
                const checked = selectedAssetIdSet.has(asset.id);
                const nowPlaying = asset.label === currentItem;
                const upNext = asset.label === nextItem;
                const assetReady = String(asset.normStatus || '').toUpperCase() === 'READY';
                return (
                  <Box
                    key={asset.id}
                    onClick={() => setSelectedAssetId(asset.id)}
                    draggable={assetReady && !!studio?.playlist?.id}
                    onDragStart={(event) => {
                      event.stopPropagation();
                      event.dataTransfer.effectAllowed = 'copyMove';
                      setDraggedAssetId(asset.id);
                    }}
                    onDragEnd={() => setDraggedAssetId(null)}
                    sx={{
                      border: '1px solid',
                      borderColor: v('--border'),
                      bgcolor: selected ? 'rgba(var(--primary-rgb),0.08)' : 'transparent',
                      boxShadow: selected ? `inset 0 0 0 1px rgba(var(--primary-rgb),0.10)` : 'none',
                      cursor: assetReady && !!studio?.playlist?.id ? 'grab' : 'pointer',
                      transition: 'background-color 120ms ease, border-color 120ms ease',
                      '&:hover': {
                        bgcolor: 'rgba(var(--primary-rgb),0.05)',
                        boxShadow: `inset 0 0 0 1px rgba(var(--primary-rgb),0.14)`,
                      },
                    }}
                  >
                    <Box sx={{ px: 1, py: 0.78 }}>
                      <Stack direction="row" justifyContent="space-between" spacing={0.55} alignItems="flex-start">
                        <Checkbox
                          size="small"
                          checked={checked}
                          onClick={(event) => event.stopPropagation()}
                          onChange={() => handleToggleAssetSelection(asset.id)}
                          sx={{ p: 0.2, mr: 0.05, color: v('--text-muted') }}
                        />
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography sx={{ fontSize: '0.78rem', fontWeight: 900, color: v('--text-main'), lineHeight: 1.2 }} noWrap>
                            {asset.label}
                          </Typography>
                          <Typography sx={{ mt: 0.18, fontSize: '0.67rem', color: v('--text-secondary'), letterSpacing: '0.03em' }} noWrap>
                            H264 / {formatDuration(asset.durationSecs)} {asset.createdAt ? `/ ${formatDateDDMMYY(asset.createdAt)}` : ''}
                          </Typography>
                          <Stack direction="row" spacing={0.55} sx={{ mt: 0.48 }} flexWrap="wrap" useFlexGap>
                            {nowPlaying && (
                              <Chip label="ON AIR" size="small" sx={{ height: 17, borderRadius: 0, bgcolor: 'rgba(var(--primary-rgb),0.14)', color: v('--primary'), fontWeight: 900, fontSize: '0.56rem' }} />
                            )}
                            {upNext && (
                              <Chip label="NEXT" size="small" sx={{ height: 17, borderRadius: 0, bgcolor: 'rgba(var(--secondary-rgb),0.14)', color: v('--secondary'), fontWeight: 900, fontSize: '0.56rem' }} />
                            )}
                            <Typography sx={{ fontSize: '0.63rem', fontWeight: 700, color: v('--text-muted'), textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                              {formatAssetStatus(asset.normStatus)}
                            </Typography>
                            {asset.folderName && (
                              <Typography sx={{ fontSize: '0.63rem', fontWeight: 700, color: v('--accent'), textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                                {asset.folderName}
                              </Typography>
                            )}
                            <Typography sx={{ fontSize: '0.63rem', fontWeight: 700, color: v('--secondary'), textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                              {String(asset.assetClass || 'VIDEO')}
                            </Typography>
                          </Stack>
                          <Stack direction="row" spacing={0.45} sx={{ mt: 0.65 }} flexWrap="wrap" useFlexGap>
                            {renderCollapsedAssetActions(asset, 26)}
                          </Stack>
                        </Box>
                        <Chip
                          label={tone.label}
                          size="small"
                          sx={{
                            height: 17,
                            borderRadius: 0,
                            bgcolor: tone.bg,
                            color: tone.color,
                            fontWeight: 900,
                            fontSize: '0.56rem',
                            flexShrink: 0,
                            alignSelf: 'center',
                          }}
                        />
                      </Stack>
                    </Box>
                  </Box>
                );
              })}
                  </Box>
                </Box>
              ) : (
                <Box sx={{ ...cardSx, p: 2, bgcolor: 'background.default' }}>
                  <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary' }}>
                    No matching assets yet.
                  </Typography>
                </Box>
              )}
            </Box>

            <Box
              sx={{
                borderTop: '1px solid',
                borderColor: 'var(--border)',
                py: 0.75,
                display: 'flex',
                justifyContent: 'space-between',
                gap: 1,
                flexWrap: 'wrap',
              }}
            >
              <Typography sx={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>
                {assets.length} assets / {readyCount} ready / {processingLabel}
              </Typography>
            </Box>
          </Stack>
        </Paper>

        <Paper elevation={0} sx={{ ...operatorPanelSx, height: '100%', order: { xs: 1, lg: 2 }, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
          <Box sx={operatorZoneHeaderSx}>
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
              <Box sx={{ ...operatorZoneNumberSx, borderColor: v('--primary'), color: v('--primary') }}>02</Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={operatorZoneTitleSx}>
                  Rundown Editor <Box component="span" sx={{ color: v('--text-secondary'), fontWeight: 800 }}>({rundownDateLabel})</Box>
                </Typography>
                <Typography sx={{ fontSize: '0.66rem', color: v('--text-secondary') }} noWrap>
                  {playlistName} / {itemCount} items / {playlistModeLabel} / {scheduleEnabled ? 'AutoStart On' : 'AutoStart Off'} / Start: {currentPlaylistStartDisplay} / {selectedPlaylistIsPlaying ? 'Live Edit' : 'Offline Edit'}
                </Typography>
              </Box>
            </Stack>
            <Chip
              label={selectedPlaylistIsPlaying ? 'Live edit' : 'Offline edit'}
              size="small"
              sx={{
                ...studioPillSx,
                bgcolor: selectedPlaylistIsPlaying ? 'rgba(var(--success-rgb),0.12)' : 'var(--bg-card-hover)',
                color: selectedPlaylistIsPlaying ? v('--success') : v('--text-secondary'),
                border: '1px solid',
                borderColor: selectedPlaylistIsPlaying ? v('--success') : v('--border'),
              }}
            />
          </Box>
          <Box
            sx={{
              px: 1.2,
              py: 0.85,
              bgcolor: 'var(--bg-card-hover)',
              borderBottom: '1px solid',
              borderColor: v('--border'),
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 1fr) auto' },
              alignItems: 'center',
              gap: 0.8,
            }}
          >
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={0.7} alignItems="stretch" sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  minWidth: { xs: '100%', md: 260 },
                  flex: 1,
                  px: 0.9,
                  py: 0.6,
                  bgcolor: 'var(--bg-body)',
                  border: '1px solid',
                  borderColor: v('--border'),
                  borderRadius: '4px',
                }}
              >
                <Typography sx={railTitleSx}>Loaded Playlist</Typography>
                <Typography sx={{ fontSize: '0.76rem', fontWeight: 900, color: v('--text-main') }} noWrap>
                  {playlistName}
                </Typography>
              </Box>
              <Box
                sx={{
                  minWidth: { xs: '100%', md: 220 },
                  flex: 0.75,
                  px: 0.9,
                  py: 0.6,
                  bgcolor: 'var(--bg-body)',
                  border: '1px solid',
                  borderColor: v('--border'),
                  borderRadius: '4px',
                }}
              >
                <Typography sx={railTitleSx}>Next Playlist</Typography>
                <Typography sx={{ fontSize: '0.76rem', fontWeight: 900, color: v('--text-main') }} noWrap>
                  {nextChainedPlaylist ? nextChainedPlaylist.name : 'None'}
                </Typography>
              </Box>
            </Stack>

            <Stack direction="row" spacing={0.45} alignItems="center" sx={{ overflowX: 'auto', flexWrap: 'nowrap', justifyContent: { xs: 'flex-start', xl: 'flex-end' }, pb: 0.1 }}>
              <Tooltip title="Start playout">
                <span>
                  <IconButton
                    onClick={() => handleAction('start')}
                    disabled={!studio || !!busyAction || isRunning || !hasConfiguredOutputs}
                    sx={!studio || !!busyAction || isRunning || !hasConfiguredOutputs
                      ? { ...actionIconSx, ...disabledActionSx }
                      : { ...actionIconSx, color: v('--primary'), borderColor: v('--primary'), bgcolor: 'rgba(var(--primary-rgb),0.1)' }}
                  >
                    <PlaylistPlayIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Reload active session">
                <span>
                  <IconButton
                    onClick={() => handleAction('reload')}
                    disabled={!studio || !!busyAction || !isRunning}
                    sx={!studio || !!busyAction || !isRunning ? { ...actionIconSx, ...disabledActionSx } : { ...actionIconSx, color: v('--accent'), borderColor: v('--accent') }}
                  >
                    <SyncIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Skip to next item">
                <span>
                  <IconButton
                    onClick={() => handleAction('skip')}
                    disabled={!studio || !!busyAction || !isRunning}
                    sx={!studio || !!busyAction || !isRunning ? { ...actionIconSx, ...disabledActionSx } : { ...actionIconSx, color: v('--warning'), borderColor: v('--warning') }}
                  >
                    <SkipNextIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Stop playout">
                <span>
                  <IconButton
                    onClick={() => handleAction('stop')}
                    disabled={!studio || !!busyAction || !isRunning}
                    sx={!studio || !!busyAction || !isRunning ? { ...actionIconSx, ...disabledActionSx } : { ...actionIconSx, color: 'var(--danger)', borderColor: 'var(--danger)' }}
                  >
                    <StopCircleIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Apply pending rundown edits to the active playout at the next safe boundary">
                <span>
                  <IconButton
                    onClick={() => handleAction('save')}
                    disabled={!studio || !!busyAction || !isRunning || !pendingLiveChanges}
                    sx={!studio || !!busyAction || !isRunning || !pendingLiveChanges ? { ...actionIconSx, ...disabledActionSx } : { ...actionIconSx, color: v('--secondary'), borderColor: v('--secondary') }}
                  >
                    <SaveOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Generate the saved playout and EPG snapshot">
                <span>
                  <IconButton
                    onClick={() => handleAction('generate')}
                    disabled={!studio || !!busyAction || playlistItems.length === 0}
                    sx={!studio || !!busyAction || playlistItems.length === 0 ? { ...actionIconSx, ...disabledActionSx } : { ...actionIconSx, color: v('--success'), borderColor: v('--success') }}
                  >
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Divider flexItem orientation="vertical" sx={{ alignSelf: 'stretch', borderColor: v('--border') }} />
              <Tooltip title="Open Playlist Manager">
                <span>
                  <IconButton
                    onClick={() => handleOpenPlaylistManager('open')}
                    sx={{ ...actionIconSx, color: v('--text-secondary'), borderColor: v('--border') }}
                  >
                    <ViewListIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Create playlist blocks">
                <span>
                  <IconButton
                    onClick={handleCreatePlaylist}
                    sx={{ ...actionIconSx, color: v('--secondary'), borderColor: v('--secondary'), bgcolor: 'rgba(var(--secondary-rgb),0.1)' }}
                  >
                    <PlaylistAddOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Import live feed">
                <span>
                  <IconButton
                    onClick={() => setImportLiveDialogOpen(true)}
                    disabled={!studio?.playlist?.id}
                    sx={!studio?.playlist?.id ? { ...actionIconSx, ...disabledActionSx } : { ...actionIconSx, color: v('--accent'), borderColor: v('--accent') }}
                  >
                    <TvIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Box>

          <Box
            sx={{
              px: 0.85,
              py: 0.6,
              bgcolor: 'var(--bg-card-hover)',
              borderBottom: '1px solid',
              borderColor: v('--border'),
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', lg: 'auto minmax(190px, auto) minmax(110px, auto)' },
              gap: 0.45,
              alignItems: 'center',
            }}
          >
            <Stack
              direction="row"
              spacing={0.45}
              alignItems="center"
              sx={{ px: 0.2, py: 0.1 }}
            >
              <Tooltip title={`Mode: ${playlistModeLabel}. Click to cycle Sequential, Loop block, Loop all blocks.`}>
                <span>
                  <IconButton
                    size="small"
                    onClick={handleCyclePlaylistMode}
                    sx={playlistMode === 'LOOP' || playlistLoopAll
                      ? { ...actionIconSx, width: 30, height: 30, borderRadius: '999px', bgcolor: 'rgba(var(--primary-rgb),0.12)', color: v('--primary'), borderColor: v('--primary') }
                      : { ...actionIconSx, width: 30, height: 30, borderRadius: '999px', bgcolor: 'rgba(var(--text-main-rgb), 0.08)', color: v('--text-main'), borderColor: 'var(--border-strong)' }}
                  >
                    {playlistMode === 'LOOP' || playlistLoopAll ? <LoopIcon sx={{ fontSize: 15 }} /> : <EastIcon sx={{ fontSize: 15 }} />}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={`Auto-Start is ${scheduleEnabled ? 'on' : 'off'}. Click to turn it ${scheduleEnabled ? 'off' : 'on'}.`}>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => void handleToggleScheduleEnabled()}
                    sx={scheduleEnabled
                      ? { ...actionIconSx, width: 30, height: 30, borderRadius: '999px', bgcolor: 'rgba(var(--success-rgb),0.12)', color: v('--success'), borderColor: v('--success') }
                      : { ...actionIconSx, width: 30, height: 30, borderRadius: '999px', bgcolor: 'rgba(var(--text-main-rgb), 0.08)', color: v('--text-main'), borderColor: 'var(--border-strong)' }}
                  >
                    <ScheduleIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
            <Stack
              direction="row"
              spacing={0.65}
              alignItems="center"
              onClick={() => setScheduleModalOpen(true)}
              title="Open the playlist timing window. Use this to set or review when the playlist is allowed to start."
              sx={{
                px: 0.65,
                py: 0.38,
                border: '1px solid',
                borderColor: v('--border'),
                bgcolor: 'var(--bg-body)',
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: 'rgba(var(--secondary-rgb),0.06)',
                  borderColor: v('--secondary'),
                },
              }}
            >
              <ScheduleIcon sx={{ fontSize: 15, color: v('--secondary') }} />
              <Typography sx={railTitleSx}>Start Time</Typography>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 900, color: v('--text-main') }}>
                {scheduleEnabled ? currentPlaylistStartLabel : 'Off'}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.65} alignItems="center" sx={{ px: 0.65, py: 0.38, border: '1px solid', borderColor: v('--border'), bgcolor: 'var(--bg-body)' }}>
              <TvIcon sx={{ fontSize: 15, color: outputRouteReady ? v('--success') : v('--warning') }} />
              <Typography sx={railTitleSx}>Outputs</Typography>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 900, color: outputStatusTone.color }}>{destinationMonitorLabel}</Typography>
            </Stack>
          </Box>

          <Box sx={{ p: 1.2, bgcolor: 'background.default', borderBottom: '1px solid', borderColor: v('--border') }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <PlaylistPlayIcon sx={{ fontSize: 18, color: v('--primary') }} />
              <Typography sx={railTitleSx}>Rundown</Typography>
              <Tooltip title="Focus on the on-air row">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => scrollToActiveRundownRow('smooth')}
                    disabled={!currentRundownId}
                    sx={!currentRundownId ? { ...actionIconSx, ...disabledActionSx, width: 24, height: 24 } : { ...actionIconSx, width: 24, height: 24, color: v('--secondary'), borderColor: v('--secondary') }}
                  >
                    <PlayArrowIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </span>
              </Tooltip>
              <Box sx={{ flex: 1 }} />
              <Chip label={`${readyCount} ready`} size="small" sx={{ borderRadius: 0, fontWeight: 800 }} />
              <Chip label={`${processingCount} processing`} size="small" sx={{ borderRadius: 0, fontWeight: 800 }} />
              <Divider orientation="vertical" flexItem sx={{ mx: 0.2, borderColor: v('--border') }} />
              <Chip label={`Current ${formatDurationWithUnits(currentPlaylistDurationSecs)}`} size="small" sx={{ ...studioPillSx, bgcolor: 'var(--bg-body)', color: v('--text-secondary'), border: '1px solid', borderColor: v('--border') }} />
              <Chip label={`Filler ${formatDurationWithUnits(blockFillerDurationSecs)}`} size="small" sx={{ ...studioPillSx, bgcolor: 'var(--bg-body)', color: v('--text-secondary'), border: '1px solid', borderColor: v('--border') }} />
              <Chip label={`Total ${formatDurationWithUnits(totalPlaylistDurationSecs)}`} size="small" sx={{ ...studioPillSx, bgcolor: 'var(--bg-body)', color: v('--text-secondary'), border: '1px solid', borderColor: v('--border') }} />
              <Chip label={playlistWindowDeltaLabel} size="small" sx={{ ...studioPillSx, bgcolor: playlistWindowDeltaTone.bg, color: playlistWindowDeltaTone.color, border: '1px solid', borderColor: playlistWindowDeltaSecs === 0 ? v('--border') : v('--danger') }} />
            </Stack>
          </Box>

          <Box
            sx={{
              px: 1,
              py: 0.55,
              bgcolor: 'var(--bg-card-hover)',
              borderBottom: '1px solid',
              borderColor: v('--border'),
              display: 'grid',
              gridTemplateColumns: '70px minmax(0, 1fr) 82px 82px 82px 88px 124px',
              gap: 0,
            }}
          >
            <Stack direction="row" spacing={0.2} alignItems="center" justifyContent="center">
              <Checkbox
                size="small"
                checked={allRundownRowsSelected}
                indeterminate={!allRundownRowsSelected && someRundownRowsSelected}
                disabled={selectableRundownItemIds.length === 0}
                onChange={handleToggleSelectAllRundownItems}
                sx={{ p: 0.2 }}
              />
              <Typography sx={{ ...railTitleSx, textAlign: 'center' }}>Slot</Typography>
            </Stack>
            <Typography sx={{ ...railTitleSx, pl: 1.2 }}>Title / Class</Typography>
            <Typography sx={{ ...railTitleSx, pl: 1 }}>In</Typography>
            <Typography sx={{ ...railTitleSx, pl: 1 }}>Duration</Typography>
            <Typography sx={{ ...railTitleSx, pl: 1 }}>Out</Typography>
            <Typography sx={{ ...railTitleSx, pl: 1 }}>Status</Typography>
            <Typography sx={{ ...railTitleSx, textAlign: 'center' }}>Actions</Typography>
          </Box>

          {isCoarsePointer && draggedRundownItemId ? (
            <Box sx={{ px: 1.1, py: 0.55, borderBottom: '1px solid', borderColor: v('--border'), bgcolor: 'rgba(var(--secondary-rgb),0.08)' }}>
              <Typography sx={{ fontSize: '0.68rem', fontWeight: 900, color: v('--secondary'), letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                Reorder armed: tap another row to drop the selected asset there
              </Typography>
            </Box>
          ) : null}
          {nextHardTimedItem ? (
            <Box sx={{ px: 1.1, py: 0.55, borderBottom: '1px solid', borderColor: v('--border'), bgcolor: 'rgba(var(--warning-rgb),0.08)' }}>
              <Typography sx={{ fontSize: '0.68rem', fontWeight: 900, color: v('--warning'), letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                Protected boundary ahead: {nextHardTimedItem.asset_label || nextHardTimedItem.source_label || nextHardTimedItem.kind} at {getPlaylistItemInLabel(nextHardTimedItem, playlistTimeZone)}
              </Typography>
            </Box>
          ) : null}

          <Box
            ref={rundownScrollRef}
            onScroll={(event) => setRundownScrollTop(event.currentTarget.scrollTop)}
            sx={{ px: 1.2, pb: 1.2, flex: 1, minHeight: 0, overflowY: 'auto' }}
          >
            {playlistItems.length > 0 ? (
              <Box sx={{ height: rundownVirtualHeight, position: 'relative' }}>
                <Box sx={{ position: 'absolute', top: rundownVirtualTop, left: 0, right: 0 }}>
            {visibleRundownItems.map((item) => {
              const rowLocked = !!item.is_locked;
              return (
              <RundownRow
                key={item.id}
                domId={`playkastr-rundown-item-${item.id}`}
                item={item}
                selected={item.id === selectedRundownItem?.id}
                bulkSelected={selectedRundownItemIdSet.has(item.id)}
                onAir={isCurrentRundownItem(item)}
                next={isNextRundownItem(item)}
                inTime={getPlaylistItemInLabel(item, playlistTimeZone)}
                runtimeItemSecs={polledRuntimeItemSecs}
                runtimeProgressBaseMs={runtimeProgressBaseMs}
                runtimeCurrentItemId={snapshot?.runtime?.current_item_id}
                outTime={getPlaylistItemOutLabel(item, playlistTimeZone)}
                metaLine={getRundownMetaLine(item)}
                dragArmed={draggedRundownItemId === item.id}
                dropMode={draggedRundownItemId !== null && draggedRundownItemId !== item.id}
                draggable={!isCoarsePointer && !!item.can_drag}
                locked={rowLocked}
                lockReason={studio?.rundown_lock_reason || undefined}
                onSelect={() => {
                  if (isCoarsePointer && draggedRundownItemId && draggedRundownItemId !== item.id) {
                    void handleDropRundown(item.id);
                    return;
                  }
                  setSelectedRundownId(item.id);
                }}
                onStartFrom={() => void handleStartFromAsset(item)}
                onMoveUp={() => void handleMoveSelectedUp(item)}
                onMoveDown={() => void handleMoveSelectedDown(item)}
                onRemove={() => setRemoveDialogItemId(item.id)}
                onToggleBulkSelect={() => handleToggleRundownItemSelection(item)}
                onArmDrag={() => handleArmRundownMove(item.id)}
                canStartFrom={!!item.can_start_from}
                canMoveUp={!!item.can_move_up}
                canMoveDown={!!item.can_move_down}
                canRemove={!!item.can_remove}
                onDragStart={() => handleDragStartRundown(item.id)}
                onDragEnd={() => setDraggedRundownItemId(null)}
                onDropOn={() => void handleDropRundown(item.id)}
              />
              );
            })}
                </Box>
              </Box>
            ) : (
              <Box sx={{ ...cardSx, p: 2, bgcolor: 'background.default' }}>
                <Typography sx={{ fontSize: '0.82rem', color: 'text.secondary' }}>
                  No rundown items yet. Upload content and assign it to a playlist to begin.
                </Typography>
              </Box>
            )}
          </Box>
          <Box
            sx={{
              px: 1.2,
              py: 0.9,
              borderTop: '1px solid',
              borderColor: v('--border'),
              bgcolor: 'var(--bg-card-hover)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
            }}
            >
              <Typography sx={{ fontSize: '0.72rem', color: v('--text-secondary') }}>
                {itemCount} items / {readyCount} ready / {processingLabel}
              </Typography>
              <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap">
                <Button
                  size="small"
                  variant="outlined"
                  disabled={!studio?.playlist?.id}
                  onClick={() => void handlePopulateCurrentBlock()}
                  sx={!studio?.playlist?.id ? { ...microButtonSx, ...disabledActionSx } : microButtonSx}
                >
                  Auto Fill
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  disabled={selectedRundownDeleteIds.length === 0}
                  onClick={() => void handleDeleteSelectedRundownItems()}
                  sx={selectedRundownDeleteIds.length === 0 ? { ...microButtonSx, ...disabledActionSx } : microButtonSx}
                >
                  Delete Selected
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={playlistItems.length === 0}
                  onClick={handleClearAllItems}
                  sx={playlistItems.length === 0 ? { ...microButtonSx, ...disabledActionSx } : microButtonSx}
                >
                  Clear All
                </Button>
              </Stack>
            </Box>
        </Paper>

        <Paper elevation={0} sx={{ ...operatorPanelSx, p: 0.9, height: '100%', order: { xs: 3, lg: 3 }, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
          <Box sx={{ ...operatorZoneHeaderSx, mb: 0.9 }}>
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
              <Box sx={{ ...operatorZoneNumberSx, borderColor: v('--accent'), color: v('--accent') }}>03</Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={operatorZoneTitleSx}>Runtime + Preview</Typography>
                <Typography sx={{ fontSize: '0.66rem', color: v('--text-secondary') }} noWrap>
                  {isRunning ? playingItemLabel : 'Waiting for playout'}
                </Typography>
              </Box>
            </Stack>
            <Chip label={previewShouldRun ? 'Live' : 'Offline'} size="small" sx={{ ...studioPillSx, bgcolor: previewShouldRun ? 'rgba(var(--secondary-rgb),0.14)' : 'var(--bg-body)', color: previewShouldRun ? v('--secondary') : v('--text-secondary') }} />
          </Box>
          <Stack spacing={1.1} sx={{ minHeight: 0, flex: 1, overflow: 'hidden' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" spacing={1} alignItems="center">
                <TvIcon sx={{ color: v('--primary'), fontSize: 18 }} />
                <Typography sx={railTitleSx}>Program Preview</Typography>
              </Stack>
              <Stack direction="row" spacing={0.65} alignItems="center" flexWrap="wrap" useFlexGap>
                {renderPlaykastrPreviewTypeControl()}
                {previewShouldRun ? (
                  <Chip label="Live Feed" size="small" sx={{ ...studioPillSx, bgcolor: 'rgba(var(--secondary-rgb),0.14)', color: v('--secondary') }} />
                ) : null}
              </Stack>
            </Stack>

            <Box sx={{ ...previewPanelSx, minHeight: { xs: 220, md: 250, lg: 180 } }}>
              <MemoizedVideoPlayer
                key={`playkastr-rail-preview-${previewItemKey}`}
                channelId={activeChannel.id}
                channelHashId={activeChannel.channelHashId || activeChannel.hashId || null}
                streamName={activeChannel.name}
                streamKeyPin={activeChannel.streamKeyPin}
                isLive={rundownModeActive && previewShouldRun}
                previewType={playkastrPreviewType}
                autoPlay
              />
            </Box>

            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pr: 0.15 }}>
            <Stack spacing={1.1}>
            <SheetSection title="Now Playing">
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                  gap: 0.7,
                }}
              >
                <DetailField label="Playlist" value={playingPlaylistName} />
                <DetailField label="Item" value={playingItemLabel} />
                <DetailField label="Loaded For Edit" value={playlistName} />
                <DetailField label="Live Edits" value={liveSaveStateLabel} />
              </Box>
              <Typography sx={{ mt: 0.7, fontSize: '0.74rem', color: v('--text-secondary') }}>
                {selectedPlaylistNotice}
              </Typography>
            </SheetSection>

            <SheetSection title="Up Next">
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 0.7 }}>
                <DetailField label="Next Item" value={nextItem} />
                <DetailField label="Next Playlist" value={nextScheduleName} />
              </Box>
            </SheetSection>

            <SheetSection title="Output Health">
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                  gap: 0.7,
                }}
              >
                <DetailField label="Destinations" value={hasConfiguredOutputs ? `${playableDestinations.length} configured` : '--'} />
                <DetailField label="State" value={String(destinationMonitorState || 'missing').toUpperCase()} />
                <DetailField label="Traffic" value={hasConfiguredOutputs ? `${destinationTrafficKbps.toFixed(0)} kbps` : '--'} />
                <DetailField label="Remote" value={destinationIps.length > 0 ? destinationIps.join(', ') : '--'} />
              </Box>
              <Typography sx={{ mt: 0.7, fontSize: '0.74rem', color: v('--text-secondary') }}>
                {hasConfiguredOutputs ? `${destinationMonitorLabel} / shared channel live path` : 'No push destinations configured for this channel yet.'}
              </Typography>
              {hasConfiguredOutputs && (
                <Typography sx={{ mt: 0.35, fontSize: '0.72rem', color: v('--text-secondary') }}>
                  {destinationUpdatedAt ? `Updated ${formatDateTimeDDMMYY(destinationUpdatedAt)}` : 'Awaiting destination telemetry'}
                </Typography>
              )}
            </SheetSection>

            <SheetSection title="Schedule / EPG">
              <Stack spacing={0.6}>
                <DetailField label="Countdown" value={nextChainedPlaylist ? nextScheduleCountdown : '--'} />
                <DetailField label="Date" value={nextChainedPlaylist ? `${nextScheduleDate} ${nextScheduleLabel}` : '--'} />
                <DetailField label="Today" value={todayScheduledPlaylists.length > 0 ? `${todayScheduledPlaylists.length} clocked playlist${todayScheduledPlaylists.length === 1 ? '' : 's'}` : 'No clocked playlists'} />
                <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap>
                  <Chip
                    label={scheduleEnabled ? 'Scheduled' : 'Schedule Off'}
                    clickable
                    onClick={() => void handleToggleScheduleEnabled()}
                    size="small"
                    sx={{
                      ...studioPillSx,
                      bgcolor: scheduleEnabled ? 'rgba(var(--success-rgb),0.12)' : 'var(--bg-body)',
                      color: scheduleEnabled ? v('--success') : v('--text-secondary'),
                      border: '1px solid',
                      borderColor: scheduleEnabled ? v('--success') : v('--border'),
                    }}
                  />
                  <TextField
                    select
                    size="small"
                    disabled={!scheduleEnabled || clockSaving}
                    value={scheduleRecurrence}
                    onChange={(event) => void handleChangeScheduleRecurrence(event.target.value as 'DAILY' | 'WEEKLY')}
                    sx={{
                      minWidth: 120,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 0,
                        bgcolor: 'var(--bg-body)',
                        fontSize: '0.72rem',
                        fontWeight: 800,
                      },
                    }}
                  >
                      <MenuItem value="DAILY" sx={{ fontSize: '0.74rem', fontWeight: 700 }}>6-hour block</MenuItem>
                  </TextField>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setWorkspaceView('epg')}
                    sx={microButtonSx}
                  >
                    EPG
                  </Button>
                </Stack>
                <Box sx={{ display: 'flex', gap: 0.55, flexWrap: 'wrap' }}>
                  {todayScheduledPlaylists.length > 0 ? todayScheduledPlaylists.slice(0, 4).map((playlist) => (
                    <Chip
                      key={playlist.id}
                      label={`${playlist.clock_start} ${playlist.name}`}
                      size="small"
                      sx={{ ...studioPillSx, bgcolor: 'var(--bg-body)', color: v('--text-secondary'), border: '1px solid', borderColor: v('--border') }}
                    />
                  )) : (
                    <Typography sx={{ fontSize: '0.74rem', color: v('--text-secondary') }}>
                      No clocked playlists configured for today.
                    </Typography>
                  )}
                </Box>
              </Stack>
            </SheetSection>
            </Stack>
            </Box>
          </Stack>
        </Paper>
      </Box>
      <Dialog open={credentialsDialogOpen} onClose={() => setCredentialsDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ borderRadius: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <VpnKeyIcon sx={{ fontSize: '1.25rem', color: v('--secondary') }} />
            <Typography sx={{ fontSize: '1rem', fontWeight: 900, color: v('--text-main') }}>
              Stream Details
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          <Stack spacing={1.4}>
            {!credentialStreamKeyPin ? (
              <Alert severity="info" sx={{ borderRadius: 0 }}>
                Stream details will appear once this channel has a stream key.
              </Alert>
            ) : null}
            <FormControl component="fieldset">
              <RadioGroup row value={credentialsProtocol} onChange={(event) => setCredentialsProtocol(event.target.value as 'srt' | 'rtmp')}>
                <FormControlLabel value="srt" control={<Radio size="small" />} label="SRT" />
                <FormControlLabel value="rtmp" control={<Radio size="small" />} label="RTMP" />
              </RadioGroup>
            </FormControl>
            {credentialsProtocol === 'srt' ? (
              <Stack spacing={1.4}>
                <TextField
                  label="SRT URL"
                  value={credentialSrtUrl}
                  helperText="Complete URL for encoders with one server field."
                  size="small"
                  fullWidth
                  InputProps={{ readOnly: true, endAdornment: credentialCopyAdornment(credentialSrtUrl, 'SRT URL') }}
                  sx={credentialFieldSx}
                />
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 2fr) minmax(100px, 1fr)' }, gap: 1.4 }}>
                  <TextField
                    label="SRT Domain"
                    value={credentialPublicHost}
                    helperText="Server domain"
                    size="small"
                    fullWidth
                    InputProps={{ readOnly: true, endAdornment: credentialCopyAdornment(credentialPublicHost, 'domain') }}
                    sx={credentialFieldSx}
                  />
                  <TextField
                    label="SRT Port"
                    value="8890"
                    helperText="Port"
                    size="small"
                    fullWidth
                    InputProps={{ readOnly: true, endAdornment: credentialCopyAdornment('8890', 'port') }}
                    sx={credentialFieldSx}
                  />
                </Box>
                <TextField
                  label="SRT Stream ID"
                  value={credentialSrtStreamId}
                  helperText="For encoders with a separate Stream ID field."
                  size="small"
                  fullWidth
                  InputProps={{ readOnly: true, endAdornment: credentialCopyAdornment(credentialSrtStreamId, 'Stream ID') }}
                  sx={credentialFieldSx}
                />
              </Stack>
            ) : (
              <Stack spacing={1.4}>
                <TextField
                  label="RTMP URL"
                  value={credentialRtmpUrl}
                  helperText="Complete URL for encoders with one server field."
                  size="small"
                  fullWidth
                  InputProps={{ readOnly: true, endAdornment: credentialCopyAdornment(credentialRtmpUrl, 'RTMP URL') }}
                  sx={credentialFieldSx}
                />
                <TextField
                  label="RTMP Server URL"
                  value={credentialRtmpServerUrl}
                  helperText="Server URL without stream key."
                  size="small"
                  fullWidth
                  InputProps={{ readOnly: true, endAdornment: credentialCopyAdornment(credentialRtmpServerUrl, 'server URL') }}
                  sx={credentialFieldSx}
                />
                <TextField
                  label="Stream Key"
                  value={credentialFullStreamKey}
                  helperText="For encoders with a separate Stream Key field."
                  size="small"
                  fullWidth
                  InputProps={{ readOnly: true, endAdornment: credentialCopyAdornment(credentialFullStreamKey, 'Stream Key') }}
                  sx={credentialFieldSx}
                />
              </Stack>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCredentialsDialogOpen(false)} sx={microButtonSx}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={insertDialogOpen} onClose={() => { if (!insertSaving) setInsertDialogOpen(false); }} fullWidth maxWidth="xs">
        <DialogTitle sx={{ borderRadius: 0 }}>Insert Assets</DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          <Stack spacing={1.2}>
            <TextField
              select
              label="Assets"
              size="small"
              value={insertScopeDraft}
              onChange={(event) => setInsertScopeDraft(event.target.value as 'selected' | 'all')}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
            >
              <MenuItem value="selected" disabled={selectedReadyAssets.length === 0}>
                Selected ready assets ({selectedReadyAssets.length})
              </MenuItem>
              <MenuItem value="all" disabled={allReadyAssets.length === 0}>
                All ready assets ({allReadyAssets.length})
              </MenuItem>
            </TextField>
            <Stack direction="row" spacing={0.8} alignItems="center">
              <Checkbox
                checked={insertRandomDraft}
                onChange={(event) => setInsertRandomDraft(event.target.checked)}
                size="small"
                sx={{ p: 0.4 }}
              />
              <Typography sx={{ fontSize: '0.78rem', color: v('--text-secondary'), fontWeight: 800 }}>
                Random order
              </Typography>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInsertDialogOpen(false)} disabled={insertSaving} sx={microButtonSx}>Cancel</Button>
          <Button onClick={() => void handleSubmitInsertDialog()} disabled={insertSaving} variant="contained" sx={microButtonSx}>
            {insertSaving ? 'Inserting...' : 'Insert'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={metadataDialogOpen} onClose={() => setMetadataDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ borderRadius: 0 }}>Program Metadata</DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          <Stack spacing={1.2}>
            <TextField
              label="Title"
              value={assetMetadataTitleDraft}
              onChange={(event) => setAssetMetadataTitleDraft(event.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
            />
            <TextField
              select
              label="Type"
              value={assetMetadataTypeDraft}
              onChange={(event) => setAssetMetadataTypeDraft(event.target.value as (typeof programTypeOptions)[number])}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
            >
              {programTypeOptions.map((option) => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Age Rating"
              value={assetMetadataAgeRatingDraft}
              onChange={(event) => setAssetMetadataAgeRatingDraft(event.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
            >
              {currentAgeRatingOptions.map((option) => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Genre"
              value={assetMetadataGenreDraft}
              onChange={(event) => setAssetMetadataGenreDraft(event.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
            >
              {genreOptions.map((option) => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Synopsis"
              value={assetMetadataSynopsisDraft}
              multiline
              minRows={4}
              inputProps={{ maxLength: 300 }}
              onChange={(event) => setAssetMetadataSynopsisDraft(event.target.value.slice(0, 300))}
              helperText={`${assetMetadataSynopsisDraft.length}/300`}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMetadataDialogOpen(false)} sx={microButtonSx}>Cancel</Button>
          <Button onClick={() => void handleSaveAssetMetadata()} variant="contained" disabled={assetMetadataSaving} sx={microButtonSx}>Save Metadata</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={mediaInfoDialogOpen} onClose={() => setMediaInfoDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ borderRadius: 0 }}>Media Info</DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          {mediaInfoLoading ? (
            <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={26} />
            </Box>
          ) : (
            <Stack spacing={1}>
              <Typography sx={{ fontSize: '0.86rem', fontWeight: 900, color: v('--text-main') }}>
                {mediaInfoAsset?.label || mediaInfo?.label || 'Selected asset'}
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 0.8 }}>
                <DetailField label="Duration" value={formatDuration(mediaInfo?.durationSecs ?? mediaInfoAsset?.durationSecs ?? null)} />
                <DetailField label="Resolution" value={mediaInfo?.width && mediaInfo?.height ? `${mediaInfo.width}x${mediaInfo.height}` : '--'} />
                <DetailField label="FPS" value={mediaInfo?.fps || '--'} />
                <DetailField label="Bitrate" value={mediaInfo?.bitrate ? `${mediaInfo.bitrate} kbps` : '--'} />
                <DetailField label="Video Codec" value={mediaInfo?.videoCodec || '--'} />
                <DetailField label="Audio Codec" value={mediaInfo?.audioCodec || '--'} />
                <DetailField label="Sample Rate" value={mediaInfo?.sampleRate ? `${mediaInfo.sampleRate} Hz` : '--'} />
                <DetailField label="Channels" value={mediaInfo?.audioChannels ?? '--'} />
                <DetailField label="Norm Status" value={mediaInfo?.normStatus || mediaInfoAsset?.normStatus || '--'} />
                <DetailField label="Folder" value={mediaInfo?.folderName || mediaInfoAsset?.folderName || 'Unfiled'} />
                <DetailField label="Type" value={mediaInfo?.type || mediaInfoAsset?.epgTypeDefault || '--'} />
                <DetailField label="Genre" value={mediaInfo?.genre || mediaInfoAsset?.epgGenreDefault || '--'} />
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMediaInfoDialogOpen(false)} sx={microButtonSx}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={createPlaylistDialogOpen} onClose={() => setCreatePlaylistDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ borderRadius: 0 }}>Playlist Manager</DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          <Stack spacing={1.2}>
            <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap>
              <Button
                onClick={() => setPlaylistManagerMode('open')}
                variant={playlistManagerMode === 'open' ? 'contained' : 'outlined'}
                sx={mockupButtonSx}
              >
                Open
              </Button>
              <Button
                onClick={() => setPlaylistManagerMode('create')}
                variant={playlistManagerMode === 'create' ? 'contained' : 'outlined'}
                sx={mockupButtonSx}
              >
                Create
              </Button>
            </Stack>

            {playlistManagerMode === 'open' ? (
              <Stack spacing={1}>
                {playlistManagerGroups.length > 0 ? playlistManagerGroups.map((group) => (
                  <Box key={group.label} sx={{ ...cardSx, bgcolor: 'var(--bg-card-hover)', p: 1 }}>
                    <Typography sx={{ ...railTitleSx, mb: 0.75 }}>{group.label}</Typography>
                    <Stack spacing={0.65}>
                      {group.items.map((playlist) => {
                        const isLoaded = Number(playlist.id) === Number(loadedPlaylistId);
                        const isPlaying = isRunning && Number(playlist.id) === Number(playingPlaylistId);
                        const itemTotal = Array.isArray((playlist as any).items) ? (playlist as any).items.length : 0;
                        const statusLabel = isPlaying ? 'Now Playing' : isLoaded ? 'Loaded' : itemTotal === 0 ? 'Empty' : 'Scheduled';
                        const statusTone = isPlaying
                          ? { bg: 'rgba(var(--success-rgb),0.12)', color: v('--success'), border: v('--success') }
                          : isLoaded
                            ? { bg: 'rgba(var(--primary-rgb),0.12)', color: v('--primary'), border: v('--primary') }
                            : itemTotal === 0
                              ? { bg: 'var(--bg-body)', color: v('--text-secondary'), border: v('--border') }
                              : { bg: 'rgba(var(--secondary-rgb),0.12)', color: v('--secondary'), border: v('--secondary') };
                        return (
                          <Box
                            key={playlist.id}
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) auto' },
                              gap: 0.8,
                              alignItems: 'center',
                              p: 0.85,
                              bgcolor: isLoaded ? 'rgba(var(--primary-rgb),0.05)' : 'var(--bg-body)',
                              border: '1px solid',
                              borderColor: isLoaded ? v('--primary') : v('--border'),
                              borderRadius: '4px',
                            }}
                          >
                            <Stack spacing={0.35} sx={{ minWidth: 0 }}>
                              <Stack direction="row" spacing={0.55} alignItems="center" flexWrap="wrap" useFlexGap>
                                <Typography sx={{ fontSize: '0.82rem', fontWeight: 900, color: v('--text-main') }} noWrap>
                                  {playlist.name || `Playlist ${playlist.id}`}
                                </Typography>
                                <Chip
                                  label={statusLabel}
                                  size="small"
                                  sx={{
                                    ...studioPillSx,
                                    bgcolor: statusTone.bg,
                                    color: statusTone.color,
                                    border: '1px solid',
                                    borderColor: statusTone.border,
                                  }}
                                />
                              </Stack>
                              <Typography sx={{ fontSize: '0.72rem', color: v('--text-secondary') }} noWrap>
                                {formatPlaylistWindowLabel(playlist)} / {itemTotal} items / {String(playlist.mode || 'SEQUENTIAL').toLowerCase()}
                              </Typography>
                            </Stack>
                            <Stack direction="row" spacing={0.5} justifyContent={{ xs: 'flex-start', md: 'flex-end' }} flexWrap="wrap" useFlexGap>
                              <Button
                                size="small"
                                variant={isLoaded ? 'contained' : 'outlined'}
                                disabled={isLoaded}
                                onClick={() => {
                                  void handleSelectPlaylist(Number(playlist.id));
                                  setCreatePlaylistDialogOpen(false);
                                }}
                                sx={mockupButtonSx}
                              >
                                {isLoaded ? 'Loaded' : 'Open'}
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={!studio?.playlist?.id || Number(playlist.id) === Number(studio?.playlist?.id)}
                                onClick={() => void handleChangeNextPlaylist(resolvePlayKastrPlaylistRouteId(playlist))}
                                sx={mockupButtonSx}
                              >
                                Set Next
                              </Button>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setPlaylistActionTargetId(Number(playlist.id));
                                  setPlaylistRenameDraft(stripGeneratedPlaylistSuffix(playlist.name || '') || playlist.name || '');
                                  setPlaylistFrequencyDraft('DAILY');
                                  setPlaylistDateDraft(playlist.start_date || new Date().toISOString().slice(0, 10));
                                  setPlaylistTimeDraft(String(playlist.clock_start || formatClockNowLocal()).slice(0, 5));
                                  setPlaylistActionDialog('edit');
                                }}
                                sx={actionIconSx}
                              >
                                <DriveFileRenameOutlineIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setPlaylistActionTargetId(Number(playlist.id));
                                  setPlaylistActionDialog('delete');
                                }}
                                sx={{ ...actionIconSx, color: 'var(--danger)', borderColor: 'var(--danger)' }}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          </Box>
                        );
                      })}
                    </Stack>
                  </Box>
                )) : (
                  <Alert severity="info" sx={{ borderRadius: 0 }}>
                    No playlists exist yet. Create the first four-block playlist set.
                  </Alert>
                )}
              </Stack>
            ) : (
              <Stack spacing={1.2}>
                <TextField
                  label="Base name"
                  value={newPlaylistNameDraft}
                  onChange={(event) => setNewPlaylistNameDraft(event.target.value)}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
                />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2}>
              <TextField
                select
                label="Frequency"
                value={newPlaylistFrequencyUnit}
                onChange={(event) => setNewPlaylistFrequencyUnit(event.target.value as 'DAILY' | 'WEEKLY')}
                sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
              >
                <MenuItem value="DAILY">6-hour block</MenuItem>
              </TextField>
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2}>
              <TextField
                label="Date"
                type="date"
                value={newPlaylistDateDraft}
                onChange={(event) => setNewPlaylistDateDraft(event.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
              />
              <TextField
                label="To Start at"
                type="time"
                value={newPlaylistTimeDraft}
                onChange={(event) => setNewPlaylistTimeDraft(event.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 60 }}
                sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
              />
                </Stack>
                <Alert severity="info" sx={{ borderRadius: 0 }}>
                  Create produces four linked 6-hour blocks from the selected start window. Blocks start empty unless auto-populate is enabled.
                </Alert>
                <Stack spacing={0.4}>
              <Stack direction="row" spacing={0.8} alignItems="center">
                <Checkbox
                  checked={createAutopopulateDraft}
                  onChange={(event) => setCreateAutopopulateDraft(event.target.checked)}
                  size="small"
                  sx={{ p: 0.4 }}
                />
                <Typography sx={{ fontSize: '0.78rem', color: v('--text-secondary'), fontWeight: 800 }}>
                  Auto-populate block filler
                </Typography>
              </Stack>
              {createAutopopulateDraft ? (
                <Stack direction="row" spacing={0.8} alignItems="center" sx={{ pl: 3.1 }}>
                  <Checkbox
                    checked={createAutopopulateAllBlocksDraft}
                    onChange={(event) => setCreateAutopopulateAllBlocksDraft(event.target.checked)}
                    size="small"
                    sx={{ p: 0.4 }}
                  />
                  <Typography sx={{ fontSize: '0.76rem', color: v('--text-secondary'), fontWeight: 800 }}>
                    Auto-populate all four blocks
                  </Typography>
                </Stack>
              ) : null}
                </Stack>
              </Stack>
            )}
            </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreatePlaylistDialogOpen(false)} sx={microButtonSx}>Close</Button>
          {playlistManagerMode === 'create' ? (
            <Button onClick={() => void handleSubmitCreatePlaylist()} variant="contained" disabled={createPlaylistSaving} sx={microButtonSx}>Create Blocks</Button>
          ) : (
            <Button onClick={() => setPlaylistManagerMode('create')} variant="contained" sx={microButtonSx}>Create Blocks</Button>
          )}
        </DialogActions>
      </Dialog>
      <Dialog open={importLiveDialogOpen} onClose={() => setImportLiveDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ borderRadius: 0 }}>Import Live</DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          <Stack spacing={1.2}>
            <TextField
              label="Live Source URL"
              value={liveSourceUrlDraft}
              onChange={(event) => setLiveSourceUrlDraft(event.target.value)}
              placeholder="srt:// or rtmp:// or rtsp://"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
            />
            <TextField
              label="Source Label"
              value={liveSourceLabelDraft}
              onChange={(event) => setLiveSourceLabelDraft(event.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
            />
            <TextField
              label="Expected Duration (seconds)"
              type="number"
              value={liveExpectedDurationDraft}
              onChange={(event) => setLiveExpectedDurationDraft(event.target.value)}
              inputProps={{ min: 1 }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
            />
            <TextField
              label="Type"
              value="Live"
              disabled
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
            />
            <TextField
              select
              label="Genre"
              value={liveGenreDraft}
              onChange={(event) => setLiveGenreDraft(event.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
            >
              {genreOptions.map((genre) => (
                <MenuItem key={genre} value={genre}>{genre}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Age Rating"
              value={liveAgeRatingDraft}
              onChange={(event) => setLiveAgeRatingDraft(event.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
            >
              {tvRatingOptions.map((rating) => (
                <MenuItem key={rating} value={rating}>{rating}</MenuItem>
              ))}
            </TextField>
            <Typography sx={{ fontSize: '0.73rem', color: v('--text-secondary') }}>
              Live items need a source, duration, genre, and age rating before they can be added to the rundown.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportLiveDialogOpen(false)} sx={microButtonSx}>Cancel</Button>
          <Button onClick={() => void handleSubmitImportLive()} variant="contained" disabled={importLiveSaving} sx={microButtonSx}>Add Live Item</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={scheduleModalOpen} onClose={() => setScheduleModalOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ borderRadius: 0 }}>Schedule Playlist</DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          <Stack spacing={1.2}>
            <Chip
              label={scheduleEnabledDraft ? 'Schedule On' : 'Schedule Off'}
              clickable
              onClick={() => setScheduleEnabledDraft((current) => !current)}
              size="small"
              sx={{
                ...studioPillSx,
                alignSelf: 'flex-start',
                bgcolor: scheduleEnabledDraft ? 'rgba(var(--success-rgb),0.12)' : 'var(--bg-body)',
                color: scheduleEnabledDraft ? v('--success') : v('--text-secondary'),
                border: '1px solid',
                borderColor: scheduleEnabledDraft ? v('--success') : v('--border'),
              }}
            />
            <TextField
              label="Start date"
              type="date"
              value={scheduleDateDraft}
              disabled={!scheduleEnabledDraft}
              onChange={(event) => setScheduleDateDraft(event.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
            />
            <TextField
              label="Start time"
              type="time"
              value={clockDraft}
              disabled={!scheduleEnabledDraft}
              onChange={(event) => setClockDraft(event.target.value)}
              inputProps={{ step: 1 }}
              InputLabelProps={{ shrink: true }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
            />
            <TextField
              select
              label="Recurrence"
              value={scheduleRecurrenceDraft}
              disabled={!scheduleEnabledDraft}
              onChange={(event) => setScheduleRecurrenceDraft(event.target.value as 'DAILY' | 'WEEKLY')}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
            >
              <MenuItem value="DAILY">6-hour block</MenuItem>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScheduleModalOpen(false)} sx={microButtonSx}>Cancel</Button>
          <Button onClick={() => void handleSaveScheduleModal()} variant="contained" disabled={clockSaving} sx={microButtonSx}>Save Schedule</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={!!playlistActionDialog} onClose={() => setPlaylistActionDialog(null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ borderRadius: 0 }}>{playlistActionDialog === 'delete' ? 'Delete Playlist' : 'Edit Playlist'}</DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          <Stack spacing={1.2}>
            <TextField
              select
              label="Playlist"
              value={playlistActionTargetId || ''}
              onChange={(event) => {
                const nextId = Number(event.target.value);
                setPlaylistActionTargetId(nextId);
                const selected = orderedPlaylists.find((playlist) => playlist.id === nextId);
                setPlaylistRenameDraft(stripGeneratedPlaylistSuffix(selected?.name || '') || selected?.name || '');
                setPlaylistFrequencyDraft(String(selected?.recurrence || '').toUpperCase() === 'WEEKLY' ? 'WEEKLY' : 'DAILY');
                setPlaylistDateDraft(selected?.start_date || new Date().toISOString().slice(0, 10));
                setPlaylistTimeDraft(String(selected?.clock_start || formatClockNowLocal()).slice(0, 5));
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
            >
                {orderedPlaylists.map((playlist) => (
                  <MenuItem key={playlist.id} value={playlist.id}>
                    {playlist.name}{isRunning && Number(playlist.id) === Number(studio?.playlist?.id) ? ' (now playing)' : ''}
                  </MenuItem>
                ))}
              </TextField>
            {playlistActionDialog === 'edit' ? (
              <>
                <TextField
                  label="Name"
                  value={playlistRenameDraft}
                  onChange={(event) => setPlaylistRenameDraft(event.target.value)}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
                />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2}>
                  <TextField
                    select
                    label="Frequency"
                    value={playlistFrequencyDraft}
                    onChange={(event) => setPlaylistFrequencyDraft(event.target.value as 'DAILY' | 'WEEKLY')}
                    sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
                  >
                    <MenuItem value="DAILY">6-hour block</MenuItem>
                  </TextField>
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2}>
                  <TextField
                    label="Date"
                    type="date"
                    value={playlistDateDraft}
                    onChange={(event) => setPlaylistDateDraft(event.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
                  />
                  <TextField
                    label="Time"
                    type="time"
                    value={playlistTimeDraft}
                    onChange={(event) => setPlaylistTimeDraft(event.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ step: 60 }}
                    sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
                  />
                </Stack>
              </>
            ) : (
              <Typography sx={{ fontSize: '0.8rem', color: v('--text-secondary') }}>
                This removes the selected playlist. If it is currently on air, Playkaster will stop that session first.
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlaylistActionDialog(null)} sx={microButtonSx}>Cancel</Button>
          {playlistActionDialog === 'edit' ? (
            <Button onClick={() => void handleRenamePlaylistDialog()} variant="contained" sx={microButtonSx}>Save</Button>
          ) : (
            <Button onClick={() => void handleDeletePlaylist()} color="error" variant="contained" sx={microButtonSx}>Delete</Button>
          )}
        </DialogActions>
      </Dialog>
      <Dialog open={!!removeDialogItem} onClose={() => setRemoveDialogItemId(null)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ borderRadius: 0 }}>
          Remove Rundown Item
        </DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          <Stack spacing={1.2}>
            <Typography sx={{ fontSize: '0.78rem', color: v('--text-secondary') }}>
              Remove this item from the rundown. Block filler is recalculated automatically inside the same playlist.
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, color: v('--text-main') }}>
              {removeDialogItem?.asset_label || removeDialogItem?.source_label || removeDialogItem?.kind || '--'}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveDialogItemId(null)} sx={microButtonSx}>Cancel</Button>
          <Button onClick={() => { if (removeDialogItem) void handleRemoveSelectedItem(removeDialogItem); }} variant="contained" color="error" sx={microButtonSx}>Remove</Button>
        </DialogActions>
      </Dialog>
      <Menu
        anchorEl={assetOptionsAnchorEl}
        open={!!assetOptionsAnchorEl}
        onClose={closeAssetOptionsMenu}
        PaperProps={{
          sx: {
            borderRadius: 0,
            border: '1px solid',
            borderColor: v('--border'),
            bgcolor: 'var(--bg-card)',
          },
        }}
      >
        <MenuItem onClick={() => { void handleAssetOptionsAction('metadata'); }} sx={{ fontSize: '0.76rem', fontWeight: 700 }}>
          Program metadata
        </MenuItem>
        <MenuItem onClick={() => { void handleAssetOptionsAction('info'); }} sx={{ fontSize: '0.76rem', fontWeight: 700 }}>
          Media info
        </MenuItem>
        <MenuItem onClick={(event) => { void handleAssetOptionsAction('folder', event); }} sx={{ fontSize: '0.76rem', fontWeight: 700 }}>
          Move to folder
        </MenuItem>
        <MenuItem onClick={() => { void handleAssetOptionsAction('rename'); }} sx={{ fontSize: '0.76rem', fontWeight: 700 }}>
          Rename asset
        </MenuItem>
        <MenuItem onClick={() => { void handleAssetOptionsAction('delete'); }} sx={{ fontSize: '0.76rem', fontWeight: 700, color: 'var(--danger)' }}>
          Delete asset
        </MenuItem>
      </Menu>
      <Menu
        anchorEl={folderPickerAnchorEl}
        open={!!folderPickerAnchorEl}
        onClose={closeFolderPicker}
        PaperProps={{
          sx: {
            borderRadius: 0,
            border: '1px solid',
            borderColor: v('--border'),
            bgcolor: 'var(--bg-card)',
          },
        }}
      >
        <MenuItem
          onClick={() => {
            void handleMoveAssetToFolder(null);
          }}
          sx={{ fontSize: '0.76rem', fontWeight: 700 }}
        >
          Unfiled
        </MenuItem>
        {folders.map((folder) => (
          <MenuItem
            key={folder.id}
            onClick={() => {
              void handleMoveAssetToFolder(folder.id);
            }}
            sx={{ fontSize: '0.76rem', fontWeight: 700 }}
          >
            {folder.name}
          </MenuItem>
        ))}
      </Menu>
      <Menu
        anchorEl={assetTypePickerAnchorEl}
        open={!!assetTypePickerAnchorEl}
        onClose={closeAssetTypePicker}
        PaperProps={{
          sx: {
            borderRadius: 0,
            border: '1px solid',
            borderColor: v('--border'),
            bgcolor: 'var(--bg-card)',
          },
        }}
      >
        {(['VIDEO', 'FILLER', 'AD'] as const).map((assetClass) => (
          <MenuItem
            key={assetClass}
            onClick={() => {
              void handleSetAssetClass(assetClass);
            }}
            sx={{ fontSize: '0.76rem', fontWeight: 700 }}
          >
            {assetClass}
          </MenuItem>
        ))}
      </Menu>
    </Stack>
    </Box>
  );
};

const playKastrChannelSignature = (channel?: StreamChannel | null) => channel ? [
  channel.id,
  channel.channelHashId,
  channel.hashId,
  channel.name,
  channel.streamKeyPin,
  channel.playkastrActive,
].join(':') : 'none';

const playKastrStatsSignature = (stats?: StreamStats | null, analytics?: StreamAnalytics | null) => [
  stats?.type,
  (stats as any)?.isPlayKastrSource,
  (stats as any)?.isMediaBoxSource,
  (stats as any)?.sourceLabel,
  stats?.streamName,
  stats?.streamKeyPin,
  Number(analytics?.viewers || 0) > 0,
].join(':');

const playKastrUserSignature = (user?: PlayKastrTabProps['user']) => user ? [
  user.alwaysRecord,
  user.streamName,
  user.firstName,
  user.lastName,
  user.country,
].join(':') : 'none';

export const PlayKastrTab = React.memo(PlayKastrTabComponent, (previous, next) => {
  if (!previous.activeChannel?.playkastrActive || !next.activeChannel?.playkastrActive) {
    return previous.activeChannel === next.activeChannel
      && previous.stats === next.stats
      && previous.analytics === next.analytics
      && previous.isStreamLive === next.isStreamLive
      && previous.user === next.user;
  }
  return playKastrChannelSignature(previous.activeChannel) === playKastrChannelSignature(next.activeChannel)
    && playKastrStatsSignature(previous.stats, previous.analytics) === playKastrStatsSignature(next.stats, next.analytics)
    && previous.isStreamLive === next.isStreamLive
    && playKastrUserSignature(previous.user) === playKastrUserSignature(next.user);
});

PlayKastrTab.displayName = 'PlayKastrTab';
